import { createPortal } from 'react-dom'
import { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type CSSProperties } from 'react'
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom'
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceDot,
  ReferenceArea,
  ReferenceLine,
  ComposedChart,
} from 'recharts'

// Pullman menu-card "Reports A La Carte" feature (lazy bundle ok in future).
import { ReportsALaCarteModal } from './components/menu-cards'
import './components/menu-cards/reports-alacarte.css'
import { EventsMeracDashboard } from './components/EventsMeracDashboard'
import { EmailDigestControls } from './components/EmailDigest'
import { FlipPageBarButton, PageSelectorControls, useNavPrefs, type NavPrefsState } from './components/PageSelector'
import { NAV_PAGES, pageByPath } from './navPages'
import { CustomerServiceScreen } from './components/CustomerService'
import { AdminSessionCodes } from './components/AdminSessionCodes'
import { AdminViewing } from './components/AdminViewing'
import { reportPageView } from './pageViews'
import { ResearchDashboard } from './components/ResearchDashboard'
import { TimetableStudioScreen } from './components/TimetableStudioScreen'
import { FoundationScreen } from './components/FoundationScreen'
import { MembershipScreen } from './components/membership/MembershipScreen'
import { BookingsDashboard } from './components/BookingsDashboard'
import { PullmanDemandDashboard } from './components/PullmanDemandDashboard'
import { CompareToggle, LyCell, lyWindow, deltaLabel, shortSeasonLabel, windowLabel, type CompareAlign } from './components/compare'
import { PullmanPeople } from './components/PullmanPeople'
import { PullmanBookingsTaken } from './components/PullmanBookingsTaken'
import { PullmanCapacity } from './components/PullmanCapacity'
import { PullmanLiveSales } from './components/PullmanLiveSales'
import { SalesScreen } from './components/SalesScreen'
import { BookingOfficesScreen } from './components/BookingOfficesScreen'
import ExecutiveScreen from './components/ExecutiveScreen'
import { ClaudeHelp } from './components/ClaudeHelp'

// Pullman Live — full-screen, immersive on-board dining tab (iPad,
// offline-first). Lazy-loaded so its bundle (and, later, the offline/IndexedDB
// engine) never weighs down the main analytics console. Rendered outside the
// Header + TopNav chrome — see the immersive branch in App().
const PullmanOffline = lazy(() => import('./components/PullmanOffline/PullmanOffline'))

/**
 * Auth model:
 *   - /.auth/me tells us only whether the user has a SWA session at all.
 *   - /api/me is the single source of truth for identity + role. It calls
 *     into our withSession() pipeline, which resolves group membership
 *     per-request (no rolesSource/GetRoles in this project — see
 *     api/src/auth.ts for rationale).
 */

interface MeResponse {
  upn: string
  role: 'admin' | 'power' | 'standard' | null
  groups: string[]
  isAdmin: boolean
  isPower: boolean
  isBudget: boolean
  isEngagement: boolean
  isEvents: boolean
  /** SG-Exec-Reports — the Outlets by Time tab. Not implied by isAdmin. */
  isExec: boolean
  /** SG-Reports-CustomerServices — the Customer Service tab. Not implied by isAdmin. */
  isCustomerService: boolean
  hasAccess: boolean
  /** Disabled by a Reports admin on Admin -> Access (db/114): role is null and every other API call is refused. */
  disabled?: boolean
}

interface AuthMeResponse {
  clientPrincipal: { userId: string; userDetails: string } | null
}

// Mode pill on each chart — Daily/Pullman/Event/All. Computed from
// cache.AllSales.BookingType server-side; see db/07_aggregates_add_mode.sql.
type Mode = 'all' | 'daily' | 'pullman' | 'event'
const MODES: { key: Mode; label: string; tooltip: string }[] = [
  { key: 'all',     label: 'All',     tooltip: 'Every sale line, regardless of service type.' },
  { key: 'daily',   label: 'Daily',   tooltip: 'Daily Service & Standard Service bookings, plus retail (Tea Rooms, gift shops) and walk-up sales.' },
  { key: 'pullman', label: 'Pullman', tooltip: 'Pullman dining services and Christmas Pullman.' },
  { key: 'event',   label: 'Event',   tooltip: 'Special services — Santa, Flying Scotsman, Steam Gala, galas, ghost trains, charters, group bookings, education, etc.' },
]

interface SeasonRow {
  id: number
  year_label: string   // e.g. "2026/2027"
  start_date: string
  end_date: string
  is_current: boolean
  display_order: number
}

interface DashboardOneKpis {
  season: string
  gross_revenue: number
  net_revenue: number
  ga_revenue: number
  ga_agreed_donations: number
  footfall: number
  sale_lines: number
  ticket_headcount: number
  ticket_headcount_people: number
  /** When the live MERAC layer (today + yesterday) was read; null = overnight figures only. */
  live_as_of?: string | null
  live_days?: string[]
}

interface DepartmentRow {
  department: string
  gross_revenue: number
  net_revenue: number
  sale_lines: number
}

interface DailyRow {
  date: string
  gross_revenue: number
  net_revenue: number
}

interface SourceRow {
  source: string
  gross_revenue: number
  net_revenue: number
  sale_lines: number
}

interface MonthlyFootfallRow {
  year_month: string
  month_label: string
  footfall: number
}

interface YoyRow {
  fiscal_month_no: number
  month_label: string
  current: number
  prior: number
}

interface LeadRow {
  bucket_label: string
  bucket_order: number
  booking_count: number
  gross_revenue: number
  net_revenue: number
}

interface DowRow {
  day_no: number
  day_label: string
  gross_revenue: number
  net_revenue: number
  avg_per_day: number
  sale_lines: number
  visit_days: number
}

interface AdminUserRow {
  id: string
  upn: string
  name: string
  job_title: string | null
  role: 'admin' | 'power' | 'standard'
  groups: string[]
  account_enabled: boolean
  /** Disabled on the Access tab (app.DisabledUsers, db/114): still in Entra, refused by the API. */
  disabled: { at: string; by: string; reason: string | null } | null
}

interface DisabledUserRow { upn: string; name: string | null; groups: string[]; reason: string | null; disabled_at: string | null; disabled_by: string }
interface DisabledLogRow { id: number; upn: string; action: string; groups: string[]; reason: string | null; by: string; at: string | null }

interface AdminGroupRow {
  id: string
  name: string
  role: 'admin' | 'power' | 'standard' | 'budget' | 'engagement' | 'events' | 'exec'
  member_count: number
}

interface AdminUsersResponse {
  users: AdminUserRow[]
  groups: AdminGroupRow[]
}

// ---------- Events (admin CRUD) -----------------------------------------
//
// app.Events is the table that drives the (E4) event-band overlay on
// time-series charts. The admin Events tab manages it. Backend lives at
// /api/events (collection: GET any-auth, POST admin) and /api/events/:id
// (PUT/DELETE admin only).

interface EventRow {
  id: number
  name: string
  start_date: string
  end_date: string
  forecast_revenue: number | null
  forecast_cost: number | null
  actual_revenue: number | null
  color: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

// What the modal collects. Same shape on both create + edit; mode flag
// tells the parent which API verb to use.
interface EventInput {
  name: string
  start_date: string
  end_date: string
  forecast_revenue: number | null
  forecast_cost: number | null
  actual_revenue: number | null
  color: string | null
  notes: string | null
}

interface EventsResponse {
  from: string
  to: string
  rows: EventRow[]
}

// ---------- My Dashboard ------------------------------------------------
//
// Per-user pinned components live in app.MyDashboardComponents. The catalog
// of available components is served from /api/components (static for now;
// see api/src/components-catalog.ts).

type ComponentKind = 'kpi' | 'chart' | 'table'
type DashboardSlug = 'dashboard-one' | 'pullman'

interface CatalogComponent {
  key: string
  name: string
  description: string
  kind: ComponentKind
  dashboard: DashboardSlug
  thumbnail?: string
}

interface PinRow {
  component_key: string
  position: number
  added_at: string
  /** Locked filter values — null means "no lock; component uses its default". */
  locked_mode: Mode | null
  locked_trend: boolean | null
  locked_events: boolean | null
  component: CatalogComponent | null
}

/** Per-pin spec sent to PUT /api/my-dashboard when locks are involved. */
interface PinSpec {
  component_key: string
  locked_mode: Mode | null
  locked_trend: boolean | null
  locked_events: boolean | null
}

interface MyDashboardResponse {
  upn: string
  rows: PinRow[]
}

interface ComponentsCatalogResponse {
  rows: CatalogComponent[]
}

// ---------- Chart requests --------------------------------------------
//
// app.ChartRequests holds user-submitted requests for new chart components.
// Lifecycle: pending -> in_progress -> fulfilled (or declined).
// Optional skill-file upload stored in Azure Blob; admins download via
// /api/chart-requests/{id}/skill-file.

type ChartRequestStatus = 'pending' | 'in_progress' | 'fulfilled' | 'declined'

interface ChartRequest {
  id: number
  upn: string
  title: string
  description: string | null
  skill_file_blob_uri: string | null
  status: ChartRequestStatus
  fulfilled_component_key: string | null
  admin_notes: string | null
  created_at: string
  updated_at: string
  fulfilled_at: string | null
}

interface ChartRequestsResponse {
  rows: ChartRequest[]
}

// ---------- Pullman Reports --------------------------------------------
//
// Per-service occupancy from MERAC.AZEPOS, mirrored into
// cache.PullmanSessions and pre-rolled into agg.Pullman_Services. The
// dashboard reads everything for a date range from a single endpoint --
// the agg table is small enough that one round-trip is the right design.
//
// CRITICAL: each PB10 record is a coupe = 4 people. Capacity per service
// is 103 (83 individual seats + 5 coupes × 4). See SKILL.md.

interface PullmanService {
  shd_rec_no: number
  service_date: string         // 'YYYY-MM-DD'
  service_datetime: string     // ISO
  departure_time: string       // 'HH:MM'
  service_description: string
  day_of_week: string
  day_of_week_num: number      // 1=Mon..7=Sun
  service_type: string
  season: string
  individual_seats: number
  coupes_sold: number
  people_total: number
  capacity: number
  pct_full: number             // 0..1
  revenue_gross: number | null
  revenue_net: number | null
  robin_people: number | null
  jos_people: number | null
  opal_people: number | null
  garnet_people: number | null
  exclusivity_seats: number      // seats sold as "Exclusivity Seat" — taken, no diner (db/88)
}

// The Services panel's "Exclude Exclusivity Seat Bookings" view: an exclusivity
// seat is a seat bought to keep the party's table private — a taken seat with
// no diner — so with the toggle on it comes off individual seats and people and
// % capacity is recomputed. coupes are untouched (a coupe is sold whole).
function applyExclusivityExclusion(services: PullmanService[], on: boolean): PullmanService[] {
  if (!on) return services
  return services.map((s) => {
    const excl = Math.max(0, Math.min(s.exclusivity_seats ?? 0, s.individual_seats))
    if (excl === 0) return s
    const individual = s.individual_seats - excl
    const people = Math.max(0, s.people_total - excl)
    return { ...s, individual_seats: individual, people_total: people, pct_full: s.capacity > 0 ? people / s.capacity : 0 }
  })
}

// Panel totals for the Services table / CSV / print, from the rows as shown
// (with or without exclusivity seats) — so the totals row always adds up to
// the rows above it. Other KPI fields are carried through unchanged.
function panelKpis(rows: PullmanService[], base: PullmanKpis): PullmanKpis {
  let individual = 0, coupes = 0, people = 0, capacity = 0
  for (const s of rows) { individual += s.individual_seats; coupes += s.coupes_sold; people += s.people_total; capacity += s.capacity }
  return { ...base, service_count: rows.length, individual_seats_total: individual, coupes_sold_total: coupes, people_total: people, capacity_total: capacity, avg_pct_full: capacity > 0 ? people / capacity : 0 }
}

interface PullmanKpiExtremum {
  service_date: string
  service_description: string
  people_total: number
  pct_full: number
}

interface PullmanKpis {
  service_count: number
  individual_seats_total: number
  coupes_sold_total: number
  people_total: number
  capacity_total: number
  avg_pct_full: number
  highest: PullmanKpiExtremum | null
  lowest:  PullmanKpiExtremum | null
  coupes_available_total: number
  coupe_utilisation_pct: number
  individual_seats_available_total: number
  individual_seat_utilisation_pct: number
  revenue_gross_total: number
  revenue_net_total: number
  revenue_net_per_service: number
  revenue_net_per_person: number
  services_with_revenue: number
}

interface PullmanByDow {
  day_of_week_num: number
  day_of_week: string
  service_count: number
  people_total: number
  avg_people: number
  avg_pct_full: number
  revenue_gross: number
  revenue_net: number
  services_with_revenue: number
  avg_revenue_net: number
}

interface PullmanByServiceType {
  service_type: string
  service_count: number
  people_total: number
  avg_people: number
  avg_pct_full: number
}

interface PullmanByMonth {
  year_month: string
  month_label: string
  service_count: number
  people_total: number
  avg_people: number
  avg_pct_full: number
}

interface PullmanResponse {
  from: string | null
  to: string | null
  data_range: { earliest: string | null; latest: string | null }
  kpis: PullmanKpis
  services: PullmanService[]
  by_dow: PullmanByDow[]
  by_service_type: PullmanByServiceType[]
  by_month: PullmanByMonth[]
}

// ---------- Hooks --------------------------------------------------------

function useCurrentUser() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const authRes = await fetch('/.auth/me')
        const auth = (await authRes.json()) as AuthMeResponse
        if (!auth.clientPrincipal) {
          const returnTo = window.location.pathname + window.location.search
          window.location.replace(
            `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`
          )
          return
        }
        const meRes = await fetch('/api/me')
        if (meRes.status === 401) { window.location.replace('/.auth/login/aad'); return }
        if (!meRes.ok) throw new Error(`/api/me returned ${meRes.status}`)
        const data = (await meRes.json()) as MeResponse
        if (cancelled) return
        setMe(data)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { me, loading, error }
}

function useSeasons(): { seasons: SeasonRow[]; loading: boolean; error: string | null } {
  const [seasons, setSeasons] = useState<SeasonRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/seasons')
        if (!res.ok) throw new Error(`/api/seasons returned ${res.status}`)
        const data = (await res.json()) as { seasons: SeasonRow[] }
        if (cancelled) return
        // Newest first — display_order is set so highest = newest
        const sorted = [...data.seasons].sort((a, b) => b.display_order - a.display_order)
        setSeasons(sorted)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { seasons, loading, error }
}

function useDashboardOneKpis(season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) {
  const [data, setData] = useState<DashboardOneKpis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!season) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const params = new URLSearchParams()
        params.set('season', season)
        params.set('mode', mode)
        if (from) params.set('from', from)
        if (to)   params.set('to',   to)
        const url = `/api/dashboards/dashboard-one/kpis?${params.toString()}`
        const res = await fetch(url)
        if (!res.ok) throw new Error(`/api/dashboards/dashboard-one/kpis returned ${res.status}`)
        const json = (await res.json()) as DashboardOneKpis
        if (cancelled) return
        setData(json)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season, mode, from, to])

  return { data, loading, error }
}

/**
 * Generic hook for the season-scoped list endpoints (revenue-by-department,
 * daily-revenue, booking-source, monthly-footfall). They all return
 * { season, rows: T[] } and re-fetch when the season changes.
 *
 * Optional `from`/`to` (YYYY-MM-DD) narrow the result to a date range; the
 * server falls back to season+mode aggregates when omitted.
 */
function useSeasonedRows<T>(
  endpoint: string,
  season: string | null,
  mode: Mode = 'all',
  from?: string | null,
  to?: string | null,
) {
  const [rows, setRows] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!season) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const params = new URLSearchParams()
        params.set('season', season)
        params.set('mode', mode)
        if (from) params.set('from', from)
        if (to)   params.set('to',   to)
        const url = `${endpoint}?${params.toString()}`
        const res = await fetch(url)
        if (!res.ok) throw new Error(`${endpoint} returned ${res.status}`)
        const json = (await res.json()) as { season: string; rows: T[] }
        if (cancelled) return
        setRows(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [endpoint, season, mode, from, to])

  return { rows, loading, error }
}

const useRevenueByDepartment = (season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) =>
  useSeasonedRows<DepartmentRow>('/api/dashboards/dashboard-one/revenue-by-department', season, mode, from, to)

const useDailyRevenue = (season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) =>
  useSeasonedRows<DailyRow>('/api/dashboards/dashboard-one/daily-revenue', season, mode, from, to)

const useBookingSource = (season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) =>
  useSeasonedRows<SourceRow>('/api/dashboards/dashboard-one/booking-source', season, mode, from, to)

const useMonthlyFootfall = (season: string | null, from?: string | null, to?: string | null) =>
  useSeasonedRows<MonthlyFootfallRow>('/api/dashboards/dashboard-one/monthly-footfall', season, 'all', from, to)

const useRevenueYoy = (season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) =>
  useSeasonedRows<YoyRow>('/api/dashboards/dashboard-one/revenue-yoy', season, mode, from, to)

const useBookingLeadTime = (season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) =>
  useSeasonedRows<LeadRow>('/api/dashboards/dashboard-one/booking-lead-time', season, mode, from, to)

const useRevenueByDow = (season: string | null, mode: Mode = 'all', from?: string | null, to?: string | null) =>
  useSeasonedRows<DowRow>('/api/dashboards/dashboard-one/revenue-by-dow', season, mode, from, to)

// Monthly "Traffic" (daily-services ticket income) budget for the Daily revenue
// trend overlay (#1). Any-auth endpoint (Chris authorised exposing the Traffic
// line to all report users). months[] index 0 = fiscal month 1 (Mar). Overlay
// is non-critical, so failures are swallowed and the chart just omits it.
interface TrafficBudget { months: number[]; pullman_months?: number[]; has_budget: boolean; has_pullman?: boolean }
function useTrafficBudget(season: string | null) {
  const [budget, setBudget] = useState<TrafficBudget | null>(null)
  useEffect(() => {
    if (!season) { setBudget(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/dashboards/dashboard-one/traffic-budget?season=${encodeURIComponent(season)}`)
        if (!res.ok) return
        const j = (await res.json()) as TrafficBudget
        if (!cancelled) setBudget(j)
      } catch {
        /* overlay is optional — ignore */
      }
    })()
    return () => { cancelled = true }
  }, [season])
  return budget
}

// ---------- Bank holidays ------------------------------------------------
//
// Bank holidays drive the small blue pins drawn on time-series charts so the
// reader can spot Easter / May Day / Christmas weekends without doing date
// arithmetic. Data comes from /api/bank-holidays which reads ref.BankHolidays
// (32 rows, 2024–2027 England-and-Wales, grouped by cluster_id so a single
// pin can describe a whole long weekend).

interface BankHoliday {
  date: string
  name: string
  cluster_id: string
  cluster_label: string
  is_substitute: boolean
}

interface BankHolidaysResponse {
  from: string
  to: string
  rows: BankHoliday[]
}

function useBankHolidays(from: string | null, to: string | null) {
  const [rows, setRows] = useState<BankHoliday[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!from || !to) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const url = `/api/bank-holidays?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        const res = await fetch(url)
        if (!res.ok) throw new Error(`/api/bank-holidays returned ${res.status}`)
        const json = (await res.json()) as BankHolidaysResponse
        if (cancelled) return
        setRows(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [from, to])

  return { rows, loading, error }
}

// ---------- Events admin hook -------------------------------------------
//
// Returns ALL events (wide window — admin needs full visibility). Re-fetches
// when `refreshKey` changes; bumping the key from the parent (after a save
// or delete) reloads the list without remounting the component.

function useEvents(refreshKey: number) {
  const [rows, setRows] = useState<EventRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        // Wide window — give admin everything in the table.
        const res = await fetch('/api/events?from=2020-01-01&to=2099-12-31')
        if (!res.ok) throw new Error(`/api/events returned ${res.status}`)
        const json = (await res.json()) as EventsResponse
        if (cancelled) return
        setRows(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [refreshKey])

  return { rows, loading, error }
}

// ---------- My Dashboard hooks ------------------------------------------
//
// useComponentsCatalog fetches the static catalog once. useMyDashboard
// owns the user's pin list and exposes a save() helper that PUT's the
// full replacement list. Both hooks keep their data in sync via a small
// refresh counter — bumping it forces a re-fetch.

function useComponentsCatalog(enabled: boolean) {
  const [catalog, setCatalog] = useState<CatalogComponent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await fetch('/api/components')
        if (!res.ok) throw new Error(`/api/components returned ${res.status}`)
        const json = (await res.json()) as ComponentsCatalogResponse
        if (cancelled) return
        setCatalog(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [enabled])

  return { catalog, loading, error }
}

function useMyDashboard(enabled: boolean) {
  const [pins, setPins] = useState<PinRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!enabled) { setLoading(false); return }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await fetch('/api/my-dashboard')
        if (!res.ok) throw new Error(`/api/my-dashboard returned ${res.status}`)
        const json = (await res.json()) as MyDashboardResponse
        if (cancelled) return
        setPins(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [enabled, refreshKey])

  // Full-replace save. Accepts either a plain string[] (legacy: no locks)
  // or a PinSpec[] (rich: per-pin filter locks). Either way we send the new
  // `pins` body shape so the API can round-trip locked_mode / locked_trend /
  // locked_events. Returns true on success so the caller can close its
  // modal cleanly. Rejection is captured into `error`.
  const save = async (input: string[] | PinSpec[]): Promise<boolean> => {
    setSaving(true)
    setError(null)
    try {
      const pinSpecs: PinSpec[] = input.map((entry) =>
        typeof entry === 'string'
          ? { component_key: entry, locked_mode: null, locked_trend: null, locked_events: null }
          : entry
      )
      const res = await fetch('/api/my-dashboard', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pins: pinSpecs }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `PUT /api/my-dashboard returned ${res.status}`)
      }
      const json = (await res.json()) as MyDashboardResponse
      setPins(json.rows ?? [])
      return true
    } catch (e) {
      setError(String((e as Error)?.message ?? e))
      return false
    } finally {
      setSaving(false)
    }
  }

  const refresh = () => setRefreshKey((k) => k + 1)

  return { pins, loading, error, saving, save, refresh }
}

// useChartRequests owns the user's (or all, if admin) chart requests and
// exposes create + patch helpers. Multipart upload is handled here so the
// modal stays a thin presentational component.
function useChartRequests(enabled: boolean) {
  const [requests, setRequests] = useState<ChartRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!enabled) { setLoading(false); return }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await fetch('/api/chart-requests')
        if (!res.ok) throw new Error(`/api/chart-requests returned ${res.status}`)
        const json = (await res.json()) as ChartRequestsResponse
        if (cancelled) return
        setRequests(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [enabled, refreshKey])

  const refresh = () => setRefreshKey((k) => k + 1)

  // Create a request, optionally with a file. If `file` is provided, we use
  // multipart/form-data; otherwise plain JSON.
  const create = async (
    title: string,
    description: string | null,
    file: File | null
  ): Promise<{ ok: true; row: ChartRequest } | { ok: false; error: string }> => {
    setBusy(true)
    try {
      let res: Response
      if (file) {
        const fd = new FormData()
        fd.append('title', title)
        if (description) fd.append('description', description)
        fd.append('skill_file', file, file.name)
        res = await fetch('/api/chart-requests', { method: 'POST', body: fd })
      } else {
        res = await fetch('/api/chart-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, description }),
        })
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        return { ok: false, error: body.error || `POST returned ${res.status}` }
      }
      const row = (await res.json()) as ChartRequest
      refresh()
      return { ok: true, row }
    } catch (e) {
      return { ok: false, error: String((e as Error)?.message ?? e) }
    } finally {
      setBusy(false)
    }
  }

  // Admin-only patch. Body fields are all optional; server enforces the
  // fulfilled/component_key/fulfilled_at consistency rule.
  const patch = async (
    id: number,
    body: { status?: ChartRequestStatus; fulfilled_component_key?: string | null; admin_notes?: string | null }
  ): Promise<{ ok: true; row: ChartRequest } | { ok: false; error: string }> => {
    setBusy(true)
    try {
      const res = await fetch(`/api/chart-requests/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        return { ok: false, error: body.error || `PATCH returned ${res.status}` }
      }
      const row = (await res.json()) as ChartRequest
      refresh()
      return { ok: true, row }
    } catch (e) {
      return { ok: false, error: String((e as Error)?.message ?? e) }
    } finally {
      setBusy(false)
    }
  }

  return { requests, loading, error, busy, create, patch, refresh }
}

// ---------- Pullman hook ------------------------------------------------
//
// One round-trip fetch of /api/dashboards/pullman/services for a given
// date range. Returns the full envelope (services, kpis, by_dow,
// by_service_type, by_month, plus data_range so the date picker can
// constrain its bounds). Re-fetches when from/to change. Passing both as
// null kicks off the initial fetch with no params -- the API defaults to
// the full data range and the parent uses that to populate the picker.

function usePullmanServices(from: string | null, to: string | null, enabled = true) {
  const [data, setData] = useState<PullmanResponse | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // Compare-to-last-season (2026-10-23) uses a second instance of this hook that only
    // fetches while the comparison is switched on.
    if (!enabled) { setData(null); setLoading(false); setError(null); return }
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const params = new URLSearchParams()
        if (from) params.set('from', from)
        if (to)   params.set('to', to)
        const qs = params.toString()
        const url = qs
          ? `/api/dashboards/pullman/services?${qs}`
          : `/api/dashboards/pullman/services`
        const res = await fetch(url)
        if (!res.ok) throw new Error(`/api/dashboards/pullman/services returned ${res.status}`)
        const json = (await res.json()) as PullmanResponse
        if (cancelled) return
        setData(json)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [from, to, enabled])

  return { data, loading, error }
}

// ISO date strings to a human range. Examples:
//   '2026-05-23','2026-05-31' -> '23 — 31 May 2026'
//   '2026-12-27','2027-01-02' -> '27 Dec 2026 — 2 Jan 2027'
//   '2026-09-24','2026-09-24' -> '24 Sep 2026'
function formatEventDateRange(startIso: string, endIso: string): string {
  const s = new Date(startIso + 'T00:00:00')
  const e = new Date(endIso + 'T00:00:00')
  const sameYear = s.getFullYear() === e.getFullYear()
  const sameMonth = sameYear && s.getMonth() === e.getMonth()
  const fmtFull = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  if (startIso === endIso) return fmtFull(s)
  if (sameMonth) {
    return `${s.getDate()} \u2014 ${e.getDate()} ${e.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`
  }
  if (sameYear) {
    return `${s.getDate()} ${s.toLocaleDateString('en-GB', { month: 'short' })} \u2014 ${fmtFull(e)}`
  }
  return `${fmtFull(s)} \u2014 ${fmtFull(e)}`
}

// NYMR fiscal year runs March 1 through end of February.
// "2026/2027" -> { from: '2026-03-01', to: '2027-02-28' }
function seasonDateRange(yearLabel: string | null): { from: string; to: string } | null {
  if (!yearLabel) return null
  const m = yearLabel.match(/^(\d{4})\/(\d{4})$/)
  if (!m) return null
  return { from: `${m[1]}-03-01`, to: `${m[2]}-02-28` }
}

// ISO date "2026-04-03" -> "Apr 2026" (matches MonthlyFootfall.month_label)
function holidayMonthYearLabel(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}
// ISO date "2026-04-03" -> "Apr" (matches RevenueYoy.month_label)
function holidayMonthShortLabel(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { month: 'short' })
}

interface BankHolidayPin {
  x: string
  label: string
  key: string
}

// Dedupe holidays by their projected X-axis value so that, for example,
// Good Friday + Easter Monday collapse to a single pin on a monthly chart
// but stay as two separate pins on the daily chart.
function dedupHolidaysByX(
  holidays: BankHoliday[],
  toX: (h: BankHoliday) => string | null
): BankHolidayPin[] {
  const seen = new Map<string, BankHolidayPin>()
  for (const h of holidays) {
    const x = toX(h)
    if (!x || seen.has(x)) continue
    seen.set(x, { x, label: h.cluster_label, key: `${h.cluster_id}-${x}` })
  }
  return Array.from(seen.values())
}

// Build an array of <ReferenceDot> JSX elements ready to drop inside a
// Recharts LineChart/BarChart. Renders a small blue pin (vertical stem +
// circle head) with a native browser tooltip showing the cluster label.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderBankHolidayPins(pins: BankHolidayPin[]): any[] {
  return pins.map((p) => (
    <ReferenceDot
      key={p.key}
      x={p.x}
      y={0}
      isFront
      ifOverflow="visible"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      shape={(props: any) => {
        const cx = Number(props.cx)
        const cy = Number(props.cy)
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return <g />
        return (
          <g>
            <line x1={cx} y1={cy} x2={cx} y2={cy - 22} stroke="#3b6cb8" strokeWidth={1.5} />
            <circle cx={cx} cy={cy - 22} r={4.5} fill="#3b6cb8" stroke="#fff" strokeWidth={1.5}>
              <title>{p.label}</title>
            </circle>
          </g>
        )
      }}
    />
  ))
}

// ---------- Trend lines --------------------------------------------------
//
// Linear-regression overlay for time-series charts. The per-chart pill in
// the header (TrendToggle) flips an opt-in dashed red line drawn on top
// of the actuals. We use index position as X (0..N-1) rather than calendar
// distance because the points are uniformly spaced anyway, and that lets
// the same helper power both daily and monthly charts.

function linearRegression(values: number[]): { slope: number; intercept: number } {
  const n = values.length
  if (n < 2) return { slope: 0, intercept: values[0] ?? 0 }
  const meanX = (n - 1) / 2
  const meanY = values.reduce((s, v) => s + v, 0) / n
  let num = 0
  let den = 0
  for (let i = 0; i < n; i++) {
    num += (i - meanX) * (values[i] - meanY)
    den += (i - meanX) ** 2
  }
  const slope = den === 0 ? 0 : num / den
  return { slope, intercept: meanY - slope * meanX }
}

// Returns a copy of `rows` with `trendKey` populated by linear regression on
// `valueKey`. Non-finite source values are treated as 0 so a single bad row
// can't NaN out the whole line.
function withTrendValues<T extends object>(
  rows: T[],
  valueKey: keyof T,
  trendKey: string
): (T & Record<string, number>)[] {
  if (rows.length === 0) return [] as (T & Record<string, number>)[]
  const values = rows.map((r) => {
    const v = Number(r[valueKey])
    return Number.isFinite(v) ? v : 0
  })
  const { slope, intercept } = linearRegression(values)
  return rows.map((r, i) => ({
    ...r,
    [trendKey]: slope * i + intercept,
  })) as (T & Record<string, number>)[]
}

const TREND_RED = '#c0392b'
const BUDGET_GOLD = '#c89b3c'

// Single-pill toggle that lives alongside the mode pills in chart-card
// headers. Default off — the dashed line is visually loud, so charts opt-in.
function TrendToggle({ on, onChange }: { on: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      className={`trend-toggle ${on ? 'active' : ''}`}
      onClick={() => onChange(!on)}
      title={on ? 'Hide the trend line' : 'Show a linear-regression trend line over the actuals'}
      aria-pressed={on}
    >
      Trend
    </button>
  )
}

// ---------- Event bands --------------------------------------------------
//
// Renders horizontal date-range bands on time-series charts so the reader
// can see at a glance which periods on the chart correspond to scheduled
// events (Santa Express, Steam Gala, Day out with Thomas, ...). Each band
// is split by today's date: the part already in the past renders in a
// muted green ("actual"), the part still in the future renders in a gold
// dashed-border ("forecast"). Hover tooltip shows name + dates +
// forecast/actual revenue.
//
// Events come from /api/events filtered to the active season's date range;
// see DashboardOne for the fetch.

function useEventsForRange(from: string | null, to: string | null) {
  const [rows, setRows] = useState<EventRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!from || !to) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const url = `/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        const res = await fetch(url)
        if (!res.ok) throw new Error(`/api/events returned ${res.status}`)
        const json = (await res.json()) as EventsResponse
        if (cancelled) return
        setRows(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [from, to])

  return { rows, loading, error }
}

interface EventBand {
  ev: EventRow
  x1: string
  x2: string
  isPast: boolean
  key: string
}

// Today + N days as YYYY-MM-DD
function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function eventTooltipText(ev: EventRow): string {
  const lines: string[] = [ev.name, formatEventDateRange(ev.start_date, ev.end_date)]
  if (ev.forecast_revenue !== null) {
    lines.push(`Forecast revenue: ${gbp0.format(ev.forecast_revenue)}`)
  }
  if (ev.actual_revenue !== null) {
    lines.push(`Actual revenue: ${gbp0.format(ev.actual_revenue)}`)
  }
  return lines.join('\n')
}

// Bands for the daily chart (continuous-looking but actually CATEGORY axis,
// since dataKey is the ISO date string). Recharts ReferenceArea on a
// category axis only renders if x1/x2 match values that exist as data
// rows. The daily series omits days with zero sales — so far-future event
// boundaries (e.g. Sep 24 when forward bookings haven't filled that day
// yet) miss the axis and the band silently disappears.
//
// Fix: snap each boundary to the nearest existing data date. Events that
// fall entirely outside the data range are skipped. Events spanning today
// are split at today's boundary (also snapped).
function eventDailyBands(
  events: EventRow[],
  dataDates: string[],
  todayIso: string
): EventBand[] {
  if (dataDates.length === 0) return []
  const sorted = [...dataDates].sort()
  const minData = sorted[0]
  const maxData = sorted[sorted.length - 1]

  // Snap to the nearest existing data date by absolute day difference.
  function snap(target: string): string {
    if (target <= minData) return minData
    if (target >= maxData) return maxData
    const tMs = new Date(target + 'T00:00:00').getTime()
    let best = sorted[0]
    let bestDiff = Math.abs(new Date(best + 'T00:00:00').getTime() - tMs)
    for (let i = 1; i < sorted.length; i++) {
      const d = sorted[i]
      const diff = Math.abs(new Date(d + 'T00:00:00').getTime() - tMs)
      if (diff < bestDiff) {
        best = d
        bestDiff = diff
      }
    }
    return best
  }

  const out: EventBand[] = []
  for (const ev of events) {
    // Skip events entirely outside the data range.
    if (ev.end_date < minData || ev.start_date > maxData) continue

    if (ev.end_date < todayIso) {
      out.push({ ev, x1: snap(ev.start_date), x2: snap(ev.end_date), isPast: true, key: `e${ev.id}-p` })
    } else if (ev.start_date > todayIso) {
      out.push({ ev, x1: snap(ev.start_date), x2: snap(ev.end_date), isPast: false, key: `e${ev.id}-f` })
    } else {
      // Spans today — split. Both endpoints (today and today+1) snap too.
      out.push({ ev, x1: snap(ev.start_date), x2: snap(todayIso), isPast: true, key: `e${ev.id}-p` })
      const next = addDaysIso(todayIso, 1)
      if (next <= ev.end_date) {
        out.push({ ev, x1: snap(next), x2: snap(ev.end_date), isPast: false, key: `e${ev.id}-f` })
      }
    }
  }
  return out
}

// Bands for monthly charts (category axis, label like "Apr 2026").
// Whole-month granularity — events get the full month band even if they
// only run on a couple of days within it.
function eventMonthlyBands(
  events: EventRow[],
  data: { year_month: string; month_label: string }[],
  todayIso: string
): EventBand[] {
  const labelByYm: Record<string, string> = {}
  for (const m of data) labelByYm[m.year_month] = m.month_label
  const todayMonth = todayIso.slice(0, 7)
  const out: EventBand[] = []
  for (const ev of events) {
    const startYm = ev.start_date.slice(0, 7)
    const endYm = ev.end_date.slice(0, 7)
    const startLabel = labelByYm[startYm]
    const endLabel = labelByYm[endYm]
    if (!startLabel || !endLabel) continue
    const isPast = endYm < todayMonth
    out.push({ ev, x1: startLabel, x2: endLabel, isPast, key: `e${ev.id}-${startYm}` })
  }
  return out
}

// Bands for the YoY chart (category axis with short month labels like
// "Apr"). Events here only make sense for the current fiscal year, which
// is already enforced by the parent fetching events scoped to the season's
// date range.
function eventYoyBands(events: EventRow[], todayIso: string): EventBand[] {
  const todayDate = new Date(todayIso + 'T00:00:00')
  const out: EventBand[] = []
  for (const ev of events) {
    const start = new Date(ev.start_date + 'T00:00:00')
    const end = new Date(ev.end_date + 'T00:00:00')
    const startLabel = start.toLocaleDateString('en-GB', { month: 'short' })
    const endLabel = end.toLocaleDateString('en-GB', { month: 'short' })
    const isPast = end < todayDate
    out.push({ ev, x1: startLabel, x2: endLabel, isPast, key: `e${ev.id}-${startLabel}` })
  }
  return out
}

// Past = solid muted green (matches NYMR-green-light). Future = semi-
// transparent gold with a dashed border to distinguish forecast.
const EVENT_PAST_FILL    = 'rgba(31, 110, 84, 0.16)'
const EVENT_PAST_STROKE  = '#1f6e54'
const EVENT_FUTURE_FILL  = 'rgba(200, 155, 60, 0.18)'
const EVENT_FUTURE_STROKE = '#c89b3c'

// Build an array of <ReferenceArea> JSX elements ready to drop inside a
// Recharts chart. Custom shape draws a filled rect with a per-event stroke
// (using the event's own colour if set, else the past/future default) and
// a native <title> tooltip. Past portion = solid border; future = dashed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderEventBands(bands: EventBand[]): any[] {
  return bands.map((b) => {
    const fill = b.isPast ? EVENT_PAST_FILL : EVENT_FUTURE_FILL
    const defaultStroke = b.isPast ? EVENT_PAST_STROKE : EVENT_FUTURE_STROKE
    const stroke = b.ev.color ?? defaultStroke
    const dash = b.isPast ? undefined : '4 3'
    return (
      <ReferenceArea
        key={b.key}
        x1={b.x1}
        x2={b.x2}
        ifOverflow="hidden"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        shape={(props: any) => {
          const x = Number(props.x)
          const y = Number(props.y)
          const w = Number(props.width)
          const h = Number(props.height)
          if (!Number.isFinite(x) || !Number.isFinite(w) || w <= 0) return <g />
          return (
            <g>
              <rect
                x={x} y={y} width={w} height={h}
                fill={fill} stroke={stroke} strokeWidth={1.5}
                strokeDasharray={dash}
                pointerEvents="all"
              >
                <title>{eventTooltipText(b.ev)}</title>
              </rect>
            </g>
          )
        }}
      />
    )
  })
}

// Per-chart pill in the header next to TrendToggle. Default off.
function EventsToggle({ on, onChange }: { on: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      className={`events-toggle ${on ? 'active' : ''}`}
      onClick={() => onChange(!on)}
      title={on ? 'Hide the event bands' : 'Show coloured bands marking scheduled events'}
      aria-pressed={on}
    >
      Events
    </button>
  )
}

// ---------- Formatters ---------------------------------------------------

const gbp0 = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })
const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

// Compact display for big numbers in cards: 7,416,909 -> "£7.42M"
function gbpCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `£${(value / 1_000_000).toFixed(2)}M`
  if (Math.abs(value) >= 1_000)     return `£${(value / 1_000).toFixed(1)}k`
  return gbp0.format(value)
}

// '2026/2027' -> '2025/2026'
function priorSeasonLabel(s: string): string {
  const m = s.match(/^(\d{4})\/(\d{4})$/)
  if (!m) return s
  return `${parseInt(m[1]) - 1}/${parseInt(m[2]) - 1}`
}

// '2026/2027' -> 'FY 26/27'
function shortFy(s: string): string {
  const m = s.match(/^(\d{4})\/(\d{4})$/)
  if (!m) return s
  return `FY ${m[1].slice(2)}/${m[2].slice(2)}`
}

// ---------- Components ---------------------------------------------------

// "Reload Site" (Chris, 2026-09-25): a discreet header button, enabled only when
// the site has been redeployed since this tab loaded — so nobody has to know
// about F5. The deployed version is the hashed bundle name in /index.html;
// the loaded version is the module script this tab is running. Checked every
// minute and whenever the tab becomes visible again. No pop-up.
function useSiteVersion(): { stale: boolean; reload: () => void } {
  const [stale, setStale] = useState(false)
  useEffect(() => {
    const loaded = Array.from(document.scripts).map((s) => s.src).find((src) => /\/assets\/index-[\w-]+\.js/.test(src))
    const loadedHash = loaded?.match(/\/assets\/(index-[\w-]+\.js)/)?.[1] ?? null
    if (!loadedHash) return   // dev server — nothing to compare
    let cancelled = false
    const check = async () => {
      try {
        const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store', credentials: 'include' })
        if (!res.ok) return
        const html = await res.text()
        const deployed = html.match(/\/assets\/(index-[\w-]+\.js)/)?.[1] ?? null
        if (!cancelled && deployed && deployed !== loadedHash) setStale(true)
      } catch { /* offline or transient — try again next tick */ }
    }
    void check()
    const t = globalThis.setInterval(() => void check(), 60_000)
    const onVis = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onVis)
    return () => { cancelled = true; globalThis.clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])
  return { stale, reload: () => globalThis.location.reload() }
}

function Header({ me, nav }: { me: MeResponse; nav?: NavPrefsState }) {
  const site = useSiteVersion()
  return (
    <header className="app-header">
      <div className="app-header__left">
        <h1>NYMR Reports</h1>
        {nav?.prefs.flip_page_bar && <PageSelectorControls me={me} nav={nav} />}
      </div>
      <div className="user-chip">
        {nav && <FlipPageBarButton nav={nav} />}
        <EmailDigestControls hasAccess={me.hasAccess} />
        <button
          type="button"
          className={'reload-site' + (site.stale ? ' reload-site--stale' : '')}
          disabled={!site.stale}
          onClick={site.reload}
          title={site.stale ? 'A newer version of the site has been released — click to load it' : 'You are on the latest version of the site'}
        >
          ↻ Reload Site
        </button>
        <span>{me.upn}</span>
        {me.role ? (
          <span className="role-pill" data-role={me.role}>{me.role}</span>
        ) : (
          <span className="role-pill" style={{ background: '#a33', color: '#fff' }}>
            no access
          </span>
        )}
        <a href="/logout" style={{ color: 'var(--nymr-cream)', marginLeft: '0.5rem' }}>
          Sign out
        </a>
      </div>
    </header>
  )
}

function SeasonBar({
  seasons,
  activeYear,
  onChange,
  lead,
  trail,
}: {
  seasons: SeasonRow[]
  activeYear: string | null
  onChange: (year_label: string) => void
  /** Before the year pills / after them — a page that wants its header on
   *  this ONE bar (Dashboard One, Chris 2026-10-28: "we waste a lot of space
   *  at the top"). */
  lead?: ReactNode
  trail?: ReactNode
}) {
  if (seasons.length === 0) return null
  return (
    <nav className={'season-bar' + (lead || trail ? ' season-bar--with-head' : '')} aria-label="Running seasons">
      {lead}
      {seasons.map((s) => {
        // year_label like "2026/2027" -> display "FY 26/27"
        const [a, b] = s.year_label.split('/')
        const display = a && b ? `FY ${a.slice(2)}/${b.slice(2)}` : s.year_label
        return (
          <button
            key={s.id}
            className={`season-pill ${activeYear === s.year_label ? 'active' : ''}`}
            onClick={() => onChange(s.year_label)}
            title={s.year_label}
          >
            {display}
          </button>
        )
      })}
      {trail}
    </nav>
  )
}

function KpiCard({
  label,
  value,
  loading,
  sub,
  info,
  days,
}: {
  label: string
  value: string
  loading: boolean
  sub?: {
    label: string
    value: string
    onClick?: () => void
    muted?: boolean
    toggle?: { on: boolean; onToggle: () => void; title: string; info?: ReactNode }
  }
  info?: ReactNode
  /** Today / yesterday, small, under the headline (2026-09-25). '—' = not refreshed yet.
   *  onClickToday / onClickYesterday make the figures open a popup (the Footfall card). */
  days?: { today: string; yesterday: string; title?: string; onClickToday?: () => void; onClickYesterday?: () => void }
}) {
  const dayValue = (v: string, onClick?: () => void) => {
    const cls = 'kpi-card__day-value' + (v === '—' ? ' kpi-card__day-value--empty' : '')
    return onClick && v !== '—'
      ? <button type="button" className={cls + ' kpi-card__sub-link kpi-card__day-link'} onClick={onClick}>{v} <span aria-hidden="true">▸</span></button>
      : <span className={cls}>{v}</span>
  }
  return (
    <div className="kpi-card">
      <div className="kpi-card__label">
        {label}
        {info && <InfoPopover>{info}</InfoPopover>}
      </div>
      <div className="kpi-card__value">{loading ? '…' : value}</div>
      {days && (
        <div className="kpi-card__days" title={days.title}>
          <span className="kpi-card__day">
            <span className="kpi-card__day-label">Today</span>
            {dayValue(days.today, days.onClickToday)}
          </span>
          <span className="kpi-card__day">
            <span className="kpi-card__day-label">Yesterday</span>
            {dayValue(days.yesterday, days.onClickYesterday)}
          </span>
        </div>
      )}
      {sub && (
        <div className="kpi-card__sub" title={sub.onClick ? 'Click for the breakdown by ticket type' : `${sub.label}: ${sub.value}`}>
          <span className="kpi-card__sub-label">
            {sub.toggle && (
              <button
                type="button"
                className={'kpi-card__fam-toggle' + (sub.toggle.on ? ' active' : '')}
                aria-pressed={sub.toggle.on}
                onClick={sub.toggle.onToggle}
                title={sub.toggle.title}
                aria-label={sub.toggle.title}
              >
                <span aria-hidden="true">👪</span>
              </button>
            )}
            {sub.label}
            {sub.toggle?.info && <InfoPopover>{sub.toggle.info}</InfoPopover>}
          </span>
          {sub.onClick ? (
            <button type="button" className="kpi-card__sub-value kpi-card__sub-link" onClick={sub.onClick} disabled={loading}>
              {loading ? '…' : sub.value} <span aria-hidden="true">▸</span>
            </button>
          ) : (
            <span className={"kpi-card__sub-value" + (sub.muted ? " kpi-card__sub-value--muted" : "")}>{loading ? '…' : sub.value}</span>
          )}
        </div>
      )}
    </div>
  )
}

function ModePillBar({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div className="mode-pills" role="tablist" aria-label="Service mode">
      {MODES.map((m) => (
        <button
          key={m.key}
          type="button"
          role="tab"
          aria-selected={mode === m.key}
          className={`mode-pill ${mode === m.key ? 'active' : ''}`}
          onClick={() => onChange(m.key)}
          title={m.tooltip}
          data-mode={m.key}
        >
          {m.label}
        </button>
      ))}
    </div>
  )
}

// ---- Today / yesterday under the season cards + the Refresh button (2026-09-25) ----
// The day figures come from agg.DashboardOne_DayKpis, written by Sync Hub job
// Refresh-DashboardDay straight from the LIVE MERAC tables (reports db/102) —
// every morning after the fact refresh, and on demand from the button below.
interface DayKpi {
  day_date: string
  gross_revenue: number
  net_revenue: number
  ga_revenue: number
  ga_agreed_donations: number
  footfall: number
  sale_lines: number
  ticket_headcount: number
  ticket_headcount_people: number
}
interface DayKpisResponse {
  mode: Mode
  today_date: string
  yesterday_date: string
  today: DayKpi | null
  yesterday: DayKpi | null
  refreshed_at: string | null
}
interface DayRefreshStatus {
  in_flight: boolean
  latest: {
    id: number
    status: string
    requested_by: string
    requested_at: string | null
    started_at: string | null
    completed_at: string | null
    message: string | null
    duration_ms: number | null
  } | null
  last_refresh: string | null
  typical_duration_ms: number | null
  lockout_minutes: number
  next_available_at: string | null
  locked: boolean
  can_request: boolean
}

// Chris, 2026-09-25 — the hover note on the button, verbatim.
const DAY_REFRESH_HOVER =
  "This button will only update today's and yesterday's sales, as the overnight job is a 20-minute job and cannot be permitted to run during the day due to performance hits on the MERAC server. Any historic sales changes made will be updated in the overnight job."

function useDayKpis(mode: Mode, nonce: number) {
  const [data, setData] = useState<DayKpisResponse | null>(null)
  useEffect(() => {
    let cancelled = false
    fetch(`/api/dashboards/dashboard-one/day-kpis?mode=${mode}`)
      .then(async (r) => { if (!r.ok) throw new Error(`(${r.status})`); return r.json() as Promise<DayKpisResponse> })
      .then((d) => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setData(null) })
    return () => { cancelled = true }
  }, [mode, nonce])
  return data
}

/** "25 Sept, 10:57" for the refresh status line. */
function prettyWhen(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
/** "gayl.ripley@nymr.co.uk" -> "Gayl Ripley". */
function prettyUpn(upn: string | null | undefined): string {
  if (!upn) return 'someone'
  const local = upn.split('@')[0]
  return local.split(/[._-]+/).filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') || upn
}

/** Where Dashboard One's KPI header parts go when they ride the season bar:
 *  the label + as-of before the year pills, the refresh after the month
 *  selector. null = render them in the KPI header as before. */
interface KpiHeadSlots { lead: HTMLElement | null; trail: HTMLElement | null }

function DashboardOneKpiBar({ season, from, to, initialMode, me, headSlots }: { season: string | null; from?: string | null; to?: string | null; initialMode?: Mode | null; me?: MeResponse | null; headSlots?: KpiHeadSlots }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const [hcOpen, setHcOpen] = useState(false)
  // The per-day ticket-type popup behind the Footfall card's Today / Yesterday figures.
  const [hcDay, setHcDay] = useState<{ day: string; label: 'today' | 'yesterday' } | null>(null)
  // Family/multi-person expansion on the ticket-sales headcount — ON by default.
  // When on, multi-person products (Family, Couple, Adult & Child, "N People")
  // are counted as the people on them rather than one ticket line each.
  const [famExpand, setFamExpand] = useState(true)
  const { data, loading, error } = useDashboardOneKpis(season, mode, from, to)

  // Today / yesterday (live MERAC basis) + the shared refresh status. Every
  // viewer polls the status (15 s; 5 s while a run is in flight) so a refresh
  // anyone requested updates the small figures for all open pages.
  const [dayNonce, setDayNonce] = useState(0)
  const days = useDayKpis(mode, dayNonce)
  const [rs, setRs] = useState<DayRefreshStatus | null>(null)
  const [posting, setPosting] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)
  const lastRefreshRef = useRef<string | null>(null)
  const pollStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/dashboards/dashboard-one/refresh', { credentials: 'include' })
      if (!res.ok) return
      const s = (await res.json()) as DayRefreshStatus
      setRs(s)
      if (s.last_refresh && lastRefreshRef.current && s.last_refresh > lastRefreshRef.current) setDayNonce((n) => n + 1)
      if (s.last_refresh) lastRefreshRef.current = s.last_refresh
    } catch { /* transient — next tick */ }
  }, [])
  const inFlight = rs?.in_flight ?? false
  useEffect(() => {
    void pollStatus()
    const t = globalThis.setInterval(() => void pollStatus(), inFlight ? 5000 : 15000)
    return () => globalThis.clearInterval(t)
  }, [pollStatus, inFlight])
  const refreshBusy = inFlight || posting
  const refreshLocked = rs?.locked ?? false
  const onRefresh = useCallback(async () => {
    if (refreshBusy || refreshLocked) return
    setPosting(true)
    setRefreshMsg(null)
    try {
      const res = await fetch('/api/dashboards/dashboard-one/refresh', { method: 'POST', credentials: 'include', headers: { Accept: 'application/json' } })
      if (!res.ok && res.status !== 202) {
        const b = await res.json().catch(() => ({} as { error?: string }))
        throw new Error(b.error || `Request failed (${res.status})`)
      }
      await pollStatus()
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : String(err))
    } finally {
      setPosting(false)
    }
  }, [refreshBusy, refreshLocked, pollStatus])

  // Was the last read of MERAC the button's run (then we can say who) or the
  // morning schedule? Same minute as the latest completed request = the button.
  const lastByButton = !!(rs?.latest && rs.latest.status === 'completed' && rs.latest.completed_at && rs.last_refresh
    && Math.abs(new Date(rs.latest.completed_at).getTime() - new Date(rs.last_refresh).getTime()) < 5 * 60_000)

  const asOf = days?.refreshed_at ? `as of ${prettyWhen(days.refreshed_at)}` : 'not refreshed yet'
  const dayPair = (pick: (d: DayKpi) => string) => ({
    today: days?.today ? pick(days.today) : '—',
    yesterday: days?.yesterday ? pick(days.yesterday) : '—',
    title: `Today = ${days?.today_date ?? 'today'}, yesterday = ${days?.yesterday_date ?? 'yesterday'} — live from MERAC, ${asOf}`,
  })

  const info = (
    <>
      <h4>About these figures</h4>
      <p>Headline numbers for the selected season: the overnight copy of the ticketing system (07:30) with <strong>today and yesterday replaced by the live MERAC figures</strong> as of the last refresh (every morning at 07:55, and the Refresh button). The small Today / Yesterday lines are those live figures on their own.</p>
      <h4>What each one means</h4>
      <p>
        <strong>Gross revenue</strong> — total of all sales (including VAT).<br />
        <strong>Net revenue</strong> — sales after VAT is removed.<br />
        <strong>Footfall</strong> — number of confirmed visitors, from the dedicated warehouse footfall feed (children counted, multi-person tickets like Couple/Family expanded, dogs excluded). Split by the Daily / Pullman / Event pills by tying each footfall entry to its ticket sale (a small unmatched remainder counts as Daily). For a single day or a narrowed range, a <strong>multi-day ticket counts once per day of its validity</strong> (a 4-day Gala ticket = 1 on each of its four days), so Today / Yesterday and their yield aren't inflated by the later days.<br />
        <strong>Total ticket sales headcount</strong> (the smaller line under Footfall) — an <em>independent</em> count from the sales ledger: admission ticket quantities, excluding package sub-items, Gift-Aid uplift lines, booking fees and dog tickets (children kept). The 👪 toggle (on by default) counts multi-person products (Family, Couple, Adult&nbsp;&amp;&nbsp;Child, &ldquo;N&nbsp;People&rdquo;) as the people on them; off, each ticket counts as one line. Either way it's the till-side ledger count and deliberately will <em>not</em> match the footfall feed.<br />
        <strong>Gift Aid</strong> — reclaimable Gift Aid (25% of the donations where the donor <em>agreed</em> Gift Aid), with the agreed donations shown in (brackets). With All selected this is the same season figure as the gated <em>Gift Aid</em> tab; the pills split it by the booking the donation was made with (till and standard-service sales = Daily). Click the ⓘ on the card for how it's derived.<br />
        <strong>Yield / head</strong> — net revenue divided by footfall.
      </p>
    </>
  )

  if (error) {
    return (
      <div className="placeholder" style={{ borderColor: '#a33' }}>
        <strong>Couldn't load KPIs</strong>
        <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>
      </div>
    )
  }

  const yieldPerHead =
    data && data.footfall > 0 ? data.net_revenue / data.footfall : null
  // Into the season bar when Dashboard One hands us its slots; inline otherwise.
  const slot = (el: HTMLElement | null | undefined, node: ReactNode) => (el ? createPortal(node, el) : node)

  return (
    <section className="kpi-section">
      <header className="kpi-section__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {slot(headSlots?.lead, <>
          <span className="kpi-section__label">Season at a glance</span>
          {data?.live_as_of && (
            <span className="kpi-section__asof" title="The headline figures include today and yesterday live from MERAC (Sync Hub job Refresh-DashboardDay); everything earlier is the overnight copy.">
              season to date as of {prettyWhen(data.live_as_of)}
            </span>
          )}
          </>)}
          {me?.isPower && slot(headSlots?.trail,
            <div className="kpi-refresh">
              <button
                type="button"
                className="kpi-refresh__btn"
                onClick={() => void onRefresh()}
                disabled={refreshBusy || refreshLocked}
                title={DAY_REFRESH_HOVER}
              >
                {refreshBusy ? '↻ Refreshing…' : '↻ Refresh today & yesterday'}
              </button>
              <span className="kpi-refresh__note">
                {rs ? (
                  <>
                    Last refresh <strong>{prettyWhen(rs.last_refresh)}</strong>
                    {rs.last_refresh && (lastByButton ? ` by ${prettyUpn(rs.latest?.requested_by)}` : ' by the morning schedule')}
                    {lastByButton && rs.latest?.duration_ms != null ? ` (took ${Math.max(1, Math.round(rs.latest.duration_ms / 1000))} s)` : ''}
                    {'. '}
                    {inFlight
                      ? <>Refreshing now — started by {prettyUpn(rs.latest?.requested_by)} at {prettyWhen(rs.latest?.requested_at)}; the figures update here when it finishes.</>
                      : refreshLocked && rs.next_available_at
                      ? <>Next refresh permitted <strong>{prettyWhen(rs.next_available_at)}</strong>.</>
                      : <><strong>Available now.</strong></>}
                    {rs.latest?.status === 'error' && !inFlight ? <span className="kpi-refresh__err"> Last run failed{rs.latest.message ? ` (${rs.latest.message})` : ''}.</span> : ''}
                    {' '}Takes about a minute before the figures change; once an hour.
                  </>
                ) : ''}
                {refreshMsg && <span className="kpi-refresh__err"> {refreshMsg}</span>}
              </span>
            </div>
          )}
        </div>
        <ModePillBar mode={mode} onChange={setMode} />
        <InfoPopover>{info}</InfoPopover>
      </header>
      <div className="kpi-bar">
        <KpiCard label="Gross revenue" value={data ? gbpCompact(data.gross_revenue) : '—'} loading={loading} days={dayPair((d) => gbpCompact(d.gross_revenue))} />
        <KpiCard label="Net revenue"   value={data ? gbpCompact(data.net_revenue)   : '—'} loading={loading} days={dayPair((d) => gbpCompact(d.net_revenue))} />
        <KpiCard
          label="Footfall"
          value={data ? num0.format(data.footfall) : '—'}
          loading={loading}
          days={{
            ...dayPair((d) => num0.format(d.footfall)),
            onClickToday: days?.today ? () => setHcDay({ day: days.today_date, label: 'today' }) : undefined,
            onClickYesterday: days?.yesterday ? () => setHcDay({ day: days.yesterday_date, label: 'yesterday' }) : undefined,
          }}
          sub={{
            label: 'Total ticket sales headcount',
            value: data ? num0.format(famExpand ? data.ticket_headcount_people : data.ticket_headcount) : '—',
            onClick: data ? () => setHcOpen(true) : undefined,
            toggle: {
              on: famExpand,
              onToggle: () => setFamExpand((v) => !v),
              title: famExpand
                ? 'Counting multi-person tickets as people (on). Click to count them as one ticket line each.'
                : 'Counting each ticket as one line. Click to count multi-person tickets as the people on them.',
              info: (
                <>
                  <h4>Family / multi-person expansion</h4>
                  <p>The base headcount counts one <em>ticket line</em> each, so a multi-person product (a Family, a Couple, an Adult&nbsp;&amp;&nbsp;Child, or a &ldquo;2/3/4&nbsp;People&rdquo; bundle) counts as <strong>1</strong>, not the people on it.</p>
                  <p>With this <strong>on</strong> (the default), those products are counted as the <strong>people</strong> they represent — Family&nbsp;=&nbsp;4, Couple&nbsp;=&nbsp;2, Adult&nbsp;&amp;&nbsp;Child&nbsp;=&nbsp;2, &ldquo;N&nbsp;People&rdquo;&nbsp;=&nbsp;N. Group bookings already carry their people count, so they are unaffected.</p>
                  <p>It only lifts the total by a few thousand — most sales are single Adult/Child/Group tickets. This is still the sales-ledger count, <em>not</em> the warehouse footfall feed above.</p>
                </>
              ),
            },
          }}
        />
        <KpiCard
          label="Gift Aid"
          value={data ? `${gbpCompact(data.ga_agreed_donations * 0.25)} (${gbpCompact(data.ga_agreed_donations)})` : '—'}
          loading={loading}
          days={dayPair((d) => `${gbpCompact(d.ga_agreed_donations * 0.25)} (${gbpCompact(d.ga_agreed_donations)})`)}
          info={
            <>
              <h4>How this Gift Aid figure is calculated</h4>
              <p>The headline is the <strong>reclaimable Gift Aid</strong> — <strong>25%</strong> of the donations where the donor <strong>agreed</strong> Gift Aid (signed the declaration): the <em>eligible</em> base. The figure in <strong>(brackets)</strong> is those agreed donations themselves.</p>
              <p>So <em>reclaim = agreed donations × 25%</em>. With All selected this is the <strong>same figure as the gated Gift Aid tab</strong> (from the MERAC Gift-Aid flags), <strong>not</strong> the gross value of every donation fare — the eligible base is what we can actually claim from HMRC. The Daily / Pullman / Event pills split it by the booking the donation was made with; a till sale counts as Daily. Today and yesterday are live from MERAC as of the last refresh.</p>
            </>
          }
        />
        <KpiCard
          label="Yield / head"
          value={yieldPerHead !== null ? gbp0.format(yieldPerHead) : '—'}
          loading={loading}
          days={dayPair((d) => (d.footfall > 0 ? gbp0.format(d.net_revenue / d.footfall) : '—'))}
        />
      </div>
      {hcOpen && <TicketHeadcountModal season={season} from={from} to={to} mode={mode} onClose={() => setHcOpen(false)} />}
      {hcDay && <TicketHeadcountModal season={season} day={hcDay.day} dayLabel={hcDay.label} mode={mode} onClose={() => setHcDay(null)} />}
    </section>
  )
}

// Popup listing the ticket types that make up the "total ticket sales headcount"
// figure. Also states the family/couple caveat (each multi-person ticket counts
// as one, not the people on it).
interface HeadcountRow { item: string; qty: number; multi_person: boolean }
function TicketHeadcountModal({ season, from, to, day, dayLabel, mode = 'all', onClose }: { season: string | null; from?: string | null; to?: string | null; day?: string | null; dayLabel?: 'today' | 'yesterday'; mode?: Mode; onClose: () => void }) {
  const [rows, setRows] = useState<HeadcountRow[]>([])
  const [total, setTotal] = useState<number>(0)
  const [liveAsOf, setLiveAsOf] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    const p = new URLSearchParams()
    if (season) p.set('season', season)
    p.set('mode', mode)   // follows the Daily / Pullman / Event pill (db/108)
    // A single day = the live MERAC breakdown (Footfall card's Today / Yesterday);
    // otherwise the overnight cache for the range.
    if (day) p.set('day', day)
    else {
      if (from) p.set('from', from)
      if (to) p.set('to', to)
    }
    fetch(`/api/dashboards/dashboard-one/ticket-headcount-breakdown?${p.toString()}`)
      .then(async (r) => { if (!r.ok) throw new Error(`(${r.status})`); return r.json() as Promise<{ total: number; rows: HeadcountRow[]; live_as_of?: string | null }> })
      .then((d) => { if (!cancelled) { setRows(d.rows ?? []); setTotal(d.total ?? 0); setLiveAsOf(d.live_as_of ?? null) } })
      .catch((e) => { if (!cancelled) setError(String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [season, from, to, day, mode])
  const multiCount = rows.filter((r) => r.multi_person).reduce((a, r) => a + r.qty, 0)
  const modeTitle = mode === 'all' ? '' : ` — ${mode === 'daily' ? 'Daily services' : mode === 'pullman' ? 'Pullman' : 'Events'} only`
  const dayTitle = day
    ? `${dayLabel === 'today' ? 'Today' : dayLabel === 'yesterday' ? 'Yesterday' : ''}${dayLabel ? ', ' : ''}${formatPrettyDate(day)}`
    : null
  return (
    <div className="hc-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="hc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hc-modal__head">
          <div>
            <div className="hc-modal__title">Ticket sales headcount — by ticket type{dayTitle ? ` — ${dayTitle}` : ''}{modeTitle}</div>
            <div className="hc-modal__sub">
              Admission tickets counted (excludes package extras, Gift-Aid donation lines, booking fees and dogs) · total {num0.format(total)}
              {day ? ` · live from MERAC${liveAsOf ? `, as of ${prettyWhen(liveAsOf)}` : ''}` : ''}
            </div>
          </div>
          <button type="button" className="hc-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="hc-modal__note">
          Each line counts <strong>ticket quantity</strong>, not people — so a <strong>Family</strong> or <strong>Couple</strong> ticket counts as <strong>1</strong> here. The 👪 toggle on the Footfall card expands these to the people on them (Family&nbsp;=&nbsp;4, Couple&nbsp;=&nbsp;2, etc.). {multiCount > 0 && <>Multi-person tickets in this period: <strong>{num0.format(multiCount)}</strong> (marked ⚑).</>}
        </div>
        <div className="hc-modal__body">
          {loading ? <div className="sales-empty">Loading…</div>
            : error ? <div className="sales-empty sales-empty--error">Couldn’t load: {error}</div>
            : rows.length === 0 ? <div className="sales-empty">No ticket sales in this period.</div>
            : (
              <table className="hc-table">
                <thead><tr><th>Ticket type</th><th className="ta-right">Tickets</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.item}>
                      <td>{r.item}{r.multi_person && <span title="Multi-person ticket — counted as 1" style={{ color: '#c89b3c', marginLeft: 6 }}>⚑</span>}</td>
                      <td className="ta-right">{num0.format(r.qty)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td>Total</td><td className="ta-right">{num0.format(total)}</td></tr></tfoot>
              </table>
            )}
        </div>
      </div>
    </div>
  )
}

// Donut palette — NYMR greens + gold + supporting tones, ordered for contrast.
const CHART_PALETTE = [
  '#0b3d2e', // nymr-green
  '#c89b3c', // nymr-accent (gold)
  '#1f6e54', // nymr-green-light
  '#2d8a6b', // teal-green
  '#8c7039', // dark gold
  '#6b6b6b', // nymr-grey
  '#4a9b7d', // lighter teal
  '#a89c80', // warm grey
  '#d4b06a', // light gold
]

// Small (i) icon → click → plain-English popover. Closes on outside-click or Esc.
function InfoPopover({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="info-popover">
      <button
        type="button"
        className="info-popover__trigger"
        aria-label="What is this?"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <text x="8" y="11.5" textAnchor="middle" fontSize="9" fontWeight="600" fill="currentColor" fontFamily="serif">i</text>
        </svg>
      </button>
      {open && (
        <div className="info-popover__panel" role="dialog" aria-modal="false">
          {children}
        </div>
      )}
    </div>
  )
}

function ChartCard({
  title,
  loading,
  error,
  empty,
  className,
  info,
  modeBar,
  trendToggle,
  eventsToggle,
  actions,
  children,
}: {
  title: string
  loading: boolean
  error: string | null
  empty: boolean
  className?: string
  info?: ReactNode
  modeBar?: ReactNode
  trendToggle?: ReactNode
  eventsToggle?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className={`chart-card ${className ?? ''}`}>
      <header className="chart-card__head">
        <h3 className="chart-card__title">{title}</h3>
        {modeBar}
        {trendToggle}
        {eventsToggle}
        {actions}
        {info && <InfoPopover>{info}</InfoPopover>}
      </header>
      <div className="chart-card__body">
        {error ? (
          <p className="chart-card__error">Couldn't load: {error}</p>
        ) : loading ? (
          <p className="chart-card__placeholder">Loading…</p>
        ) : empty ? (
          <p className="chart-card__placeholder">No data for this season yet.</p>
        ) : (
          children
        )}
      </div>
    </section>
  )
}

interface DeptDetailPayload {
  department: string; from: string; to: string
  gross: number; net: number; lines: number
  booking_fee: { value: number; count: number; items: { item: string; qty: number; net: number }[] }
  vouchers: { value: number; count: number; items: { item: string; qty: number; net: number }[] }
  net_excl_fee: number
}

function DeptDetailModal({ department, season, mode, from, to, onClose }: { department: string; season: string | null; mode: Mode; from?: string | null; to?: string | null; onClose: () => void }) {
  const [data, setData] = useState<DeptDetailPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    const q = new URLSearchParams({ department, mode })
    if (from && to) { q.set('from', from); q.set('to', to) }
    else if (season) q.set('season', season)
    fetch(`/api/dashboards/dashboard-one/revenue-by-department/detail?${q.toString()}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error((b as { error?: string }).error || `${r.status}`) } return r.json() as Promise<DeptDetailPayload> })
      .then((d) => { if (live) setData(d) })
      .catch((e) => { if (live) setError(String((e as Error)?.message ?? e)) })
    return () => { live = false }
  }, [department, season, mode, from, to])
  const itemTable = (items: { item: string; qty: number; net: number }[]) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', marginTop: 4 }}>
      <tbody>
        {items.map((i) => (
          <tr key={i.item}>
            <td style={{ padding: '2px 8px', borderBottom: '1px solid #f0ece1' }}>{i.item}</td>
            <td style={{ padding: '2px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right', width: 80 }}>{Math.round(i.qty).toLocaleString('en-GB')}</td>
            <td style={{ padding: '2px 8px', borderBottom: '1px solid #f0ece1', textAlign: 'right', width: 100 }}>{gbp0.format(Math.round(i.net))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', width: 560, maxWidth: '94vw', maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <strong style={{ fontSize: '0.95rem' }}>{department} — revenue breakdown</strong>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b6b6b' }}>×</button>
        </div>
        {error && <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>}
        {!data && !error && <p style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>Loading…</p>}
        {data && (
          <>
            <p style={{ fontSize: '0.72rem', color: '#8a8a8a', margin: '0 0 8px' }}>{data.from} → {data.to} · net figures after VAT.</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              {[
                { label: 'Net revenue', value: gbp0.format(Math.round(data.net)), color: '#0b3d2e' },
                { label: 'Booking fees', value: `${gbp0.format(Math.round(data.booking_fee.value))} (${Math.round(data.booking_fee.count).toLocaleString('en-GB')})`, color: '#b45309' },
                { label: 'Net excl. booking fee', value: gbp0.format(Math.round(data.net_excl_fee)), color: '#0b3d2e' },
                { label: 'Vouchers in these sales', value: `${gbp0.format(Math.round(data.vouchers.value))} (${Math.round(data.vouchers.count).toLocaleString('en-GB')})`, color: '#8a6d1a' },
              ].map((c) => (
                <div key={c.label} style={{ flex: '1 1 120px', border: '1px solid #e5e1d6', borderRadius: 8, padding: '8px 10px', background: '#faf9f5' }}>
                  <div style={{ fontSize: '0.68rem', color: '#6b6b6b' }}>{c.label}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
            {data.booking_fee.items.length > 0 && (
              <>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#0b3d2e' }}>Booking fee items</div>
                {itemTable(data.booking_fee.items)}
              </>
            )}
            {data.vouchers.items.length > 0 && (
              <>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#0b3d2e', marginTop: 8 }}>Voucher items counted</div>
                {itemTable(data.vouchers.items)}
              </>
            )}
            {data.booking_fee.items.length === 0 && data.vouchers.items.length === 0 && (
              <p style={{ fontSize: '0.78rem', color: '#6b6b6b' }}>No booking-fee or voucher lines in this department for the period.</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function RevenueByDepartmentChart({ season, className, from, to, initialMode }: { season: string | null; className?: string; from?: string | null; to?: string | null; initialMode?: Mode | null }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const [detailDept, setDetailDept] = useState<string | null>(null)
  const { rows, loading, error } = useRevenueByDepartment(season, mode, from, to)
  // Height grows with row count so labels never collide.
  const height = Math.max(220, rows.length * 38)
  const info = (
    <>
      <h4>What this shows</h4>
      <p>Net revenue (after VAT) for the selected season, broken down by the department that made the sale, ordered highest to lowest.</p>
      <h4>How it's calculated</h4>
      <p>Every transaction carries a department tag (e.g. Tea Rooms, Internet, Booking Offices). All transactions for the season are grouped by that tag and totaled.</p>
      <p>Click a bar for the department's breakdown — for Internet that splits out the £1.50 booking fees (count and value, plus net revenue excluding them) and any voucher lines within the sales.</p>
    </>
  )
  return (
    <ChartCard title="Revenue by department" loading={loading} error={error} empty={rows.length === 0} className={className} info={info} modeBar={<ModePillBar mode={mode} onChange={setMode} />}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v) => gbpCompact(v as number)}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            type="category"
            dataKey="department"
            width={170}
            tick={{ fontSize: 11, fill: '#1a1a1a' }}
            stroke="#e5e1d6"
          />
          <Tooltip
            formatter={(v) => gbp0.format(v as number)}
            labelFormatter={(l) => `${l} — click bar for booking fees & vouchers`}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          <Bar
            dataKey="net_revenue"
            name="Net revenue"
            fill="#0b3d2e"
            radius={[0, 3, 3, 0]}
            cursor="pointer"
            onClick={(d: { department?: string }) => { if (d?.department) setDetailDept(d.department) }}
          />
        </BarChart>
      </ResponsiveContainer>
      {detailDept && (
        <DeptDetailModal department={detailDept} season={season} mode={mode} from={from} to={to} onClose={() => setDetailDept(null)} />
      )}
    </ChartCard>
  )
}

// ---- Events lane (2026-09-25) ------------------------------------------------
// Chris: the event shading on the sales chart "is very hard to see ... stack
// overlay them with different colours and heights — anything spreading across a
// longer period is a lower height". This is a strip of labelled, colour-coded
// bars drawn directly ABOVE the daily chart's plot area on the same date axis:
// one bar per event from the Events tab's own feed (app.MeracEventSnapshot),
// packed into lanes so overlapping events never hide each other, short events
// on the top lanes and long-running ones underneath. The x geometry mirrors the
// LineChart below (margin left 8 + Y-axis width 60; margin right 24) and the
// category axis is interpolated by day, so a bar lands on the right dates even
// where the series skips days with no sales.
function useElementWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setWidth(el.getBoundingClientRect().width)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width]
}

const EVENT_LANE_COLOURS = ['#0b3d2e', '#c89b3c', '#1f6e54', '#8c3b2e', '#2d5f8a', '#8c7039', '#6a3a8c', '#2d8a6b', '#a83232', '#4a6b8a', '#7a5c2e', '#3d6b3d']

function EventsLane({ events, dates, todayIso, leftPx, rightPx }: { events: EventRow[]; dates: string[]; todayIso: string; leftPx: number; rightPx: number }) {
  const [ref, width] = useElementWidth()
  const sorted = useMemo(() => [...dates].sort(), [dates])
  const dayMs = 86_400_000
  const toMs = (iso: string) => new Date(iso + 'T00:00:00Z').getTime()
  // x for any date: interpolate between the two nearest series dates (the
  // category axis places the N series points evenly across the plot width).
  const xOf = (iso: string, plotW: number): number => {
    const n = sorted.length
    if (n === 0) return leftPx
    if (n === 1) return leftPx + plotW / 2
    const step = plotW / (n - 1)
    if (iso <= sorted[0]) return leftPx
    if (iso >= sorted[n - 1]) return leftPx + plotW
    let lo = 0, hi = n - 1
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (sorted[mid] <= iso) lo = mid; else hi = mid }
    const span = toMs(sorted[hi]) - toMs(sorted[lo])
    const frac = span > 0 ? (toMs(iso) - toMs(sorted[lo])) / span : 0
    return leftPx + (lo + frac) * step
  }
  const layout = useMemo(() => {
    if (!width || sorted.length === 0) return { bars: [] as { ev: EventRow; lane: number; x1: number; x2: number; colour: string; isPast: boolean; spansToday: boolean }[], lanes: 0 }
    const plotW = Math.max(0, width - leftPx - rightPx)
    const minD = sorted[0], maxD = sorted[sorted.length - 1]
    const inRange = events.filter((e) => e.end_date >= minD && e.start_date <= maxD)
    // Short events first so they take the top lanes; long ones fall to the bottom.
    const byLength = [...inRange].sort((a, b) => (toMs(a.end_date) - toMs(a.start_date)) - (toMs(b.end_date) - toMs(b.start_date)) || a.start_date.localeCompare(b.start_date))
    const laneEnds: number[] = []
    const colourIndex = new Map<number, number>()
    ;[...inRange].sort((a, b) => a.start_date.localeCompare(b.start_date) || a.id - b.id).forEach((e, i) => colourIndex.set(e.id, i))
    const bars = byLength.map((ev) => {
      const s = toMs(ev.start_date), e = toMs(ev.end_date) + dayMs
      let lane = laneEnds.findIndex((end) => end <= s)
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(e) } else laneEnds[lane] = e
      const x1 = xOf(ev.start_date, plotW)
      const x2 = Math.max(x1 + 3, xOf(addDaysIso(ev.end_date, 1), plotW))
      return { ev, lane, x1, x2, colour: EVENT_LANE_COLOURS[(colourIndex.get(ev.id) ?? 0) % EVENT_LANE_COLOURS.length], isPast: ev.end_date < todayIso, spansToday: ev.start_date <= todayIso && ev.end_date >= todayIso }
    })
    return { bars, lanes: laneEnds.length }
  }, [events, sorted, width, leftPx, rightPx, todayIso]) // eslint-disable-line react-hooks/exhaustive-deps
  const laneH = 15, gap = 3
  const height = layout.lanes > 0 ? layout.lanes * (laneH + gap) + 4 : 18
  return (
    <div ref={ref} className="events-lane" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} aria-label="Events in this period">
          {layout.bars.length === 0 && <text x={leftPx} y={12} fontSize={11} fill="#6b6b6b">No events in this period.</text>}
          {layout.bars.map((b) => {
            const y = 2 + b.lane * (laneH + gap)
            const w = b.x2 - b.x1
            const maxChars = Math.floor((w - 8) / 5.6)
            const label = maxChars >= 4 ? (b.ev.name.length > maxChars ? b.ev.name.slice(0, Math.max(3, maxChars - 1)) + '…' : b.ev.name) : ''
            return (
              <g key={b.ev.id}>
                <rect x={b.x1} y={y} width={w} height={laneH} rx={3} fill={b.colour} fillOpacity={b.isPast ? 0.85 : 0.35} stroke={b.colour} strokeWidth={1} strokeDasharray={b.isPast ? undefined : '3 2'}>
                  <title>{eventTooltipText(b.ev)}{b.isPast ? '' : b.spansToday ? '\n(running now)' : '\n(future)'}</title>
                </rect>
                {label && (
                  <text x={b.x1 + 4} y={y + laneH - 4} fontSize={10} fontWeight={600} fill={b.isPast ? '#fff' : '#2a2a2a'} style={{ pointerEvents: 'none' }}>{label}</text>
                )}
              </g>
            )
          })}
        </svg>
      )}
    </div>
  )
}

function DailyRevenueChart({ season, className, bankHolidays = [], events = [], from, to, initialMode, initialTrend, initialEvents }: { season: string | null; className?: string; bankHolidays?: BankHoliday[]; events?: EventRow[]; from?: string | null; to?: string | null; initialMode?: Mode | null; initialTrend?: boolean | null; initialEvents?: boolean | null }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const [trendOn, setTrendOn] = useState(initialTrend ?? false)
  const [eventsOn, setEventsOn] = useState(initialEvents ?? false)
  const { rows, loading, error } = useDailyRevenue(season, mode, from, to)
  const trafficBudget = useTrafficBudget(season)
  const dailyPins = useMemo(
    () => dedupHolidaysByX(bankHolidays, (h) => h.date),
    [bankHolidays]
  )
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const eventBands = useMemo(
    () => eventDailyBands(events, rows.map((r) => r.date), todayIso),
    [events, rows, todayIso]
  )

  // Budget overlay: the selected mode's income-target run-rate. Traffic
  // (daily-services ticket income) for All/Daily; the Pullman budget for Pullman
  // mode. Not shown for Event (no matching budget line).
  const budgetMonths =
    mode === 'pullman' ? (trafficBudget?.pullman_months ?? null) : (trafficBudget?.months ?? null)
  const showBudget =
    !!budgetMonths && (
      ((mode === 'all' || mode === 'daily') && !!trafficBudget?.has_budget) ||
      (mode === 'pullman' && !!trafficBudget?.has_pullman)
    )
  const budgetLabel = mode === 'pullman' ? 'Pullman budget' : 'Traffic budget'

  // Always compute the trend columns so the data shape is stable; each Line is
  // just hidden when not applicable. This avoids conditional <Line> children
  // which Recharts 2.13 doesn't always handle gracefully. When budget data is
  // present we phase each month's budget evenly across its days (budget =
  // run-rate) and fit a second regression through that run-rate — so the chart
  // shows actual-vs-budget trend lines (#1).
  const dataWithTrend = useMemo(() => {
    const withActualTrend = withTrendValues(rows, 'net_revenue', 'trend')
    if (!showBudget || !budgetMonths) {
      return withActualTrend.map((r) => ({ ...r, budget: null as number | null, budget_trend: null as number | null }))
    }
    const fmIndexOf = (iso: string): number => {
      const m = Number(iso.slice(5, 7))          // calendar month 1-12
      return m >= 3 ? m - 3 : m + 9              // fiscal month index 0-11 (0 = Mar)
    }
    const daysInMonthOf = (iso: string): number => {
      const [y, m] = iso.slice(0, 7).split('-').map(Number)
      return new Date(Date.UTC(y, m, 0)).getUTCDate()
    }
    const budgetSeries = rows.map((r) => {
      const monthTotal = budgetMonths[fmIndexOf(r.date)] ?? 0
      return monthTotal / daysInMonthOf(r.date)
    })
    const { slope, intercept } = linearRegression(budgetSeries)
    return withActualTrend.map((r, i) => ({
      ...r,
      budget: budgetSeries[i],
      budget_trend: slope * i + intercept,
    }))
  }, [rows, showBudget, budgetMonths])

  // Show one tick per month boundary so the X axis stays readable
  // even with ~365 days of daily data.
  const monthTicks = useMemo(() => {
    const ticks: string[] = []
    let lastMonth = ''
    for (const r of rows) {
      const ym = r.date.slice(0, 7) // 'YYYY-MM'
      if (ym !== lastMonth) {
        ticks.push(r.date)
        lastMonth = ym
      }
    }
    return ticks
  }, [rows])

  const formatTick = (d: string) => new Date(d).toLocaleDateString('en-GB', { month: 'short' })
  const formatTooltipLabel = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Total daily net revenue (after VAT) across every department for the selected season.</p>
      <h4>How it's calculated</h4>
      <p>Every sale, ticket, and transaction recorded against a calendar day is summed. Hover any point on the line to see the full date and amount.</p>
      <h4>Blue pins</h4>
      <p>England-and-Wales bank holidays. Hover a pin to see which long-weekend cluster or single-day holiday it marks.</p>
      <h4>Budget line (gold)</h4>
      <p>The gold line is the income budget from the Budget tab, phased evenly across each month's days (a daily run-rate). In <strong>All</strong> and <strong>Daily</strong> modes it's the daily-services <strong>Traffic</strong> budget; in <strong>Pullman</strong> mode it's the <strong>Pullman</strong> budget. It's not shown for Events. Note actuals are booked by <em>travel date</em> while the budget is an even monthly run-rate, so treat it as an indicative pace, not a to-the-day target.</p>
      <h4>Trend lines</h4>
      <p>Click <strong>Trend</strong> to overlay dashed lines of best fit — <span style={{ color: TREND_RED }}>red</span> through the daily actuals and <span style={{ color: BUDGET_GOLD }}>gold</span> through the daily budget run-rate. Comparing the two answers “are we pulling ahead of, or behind, plan?” for this season. Both are linear regressions on the values shown.</p>
      <h4>Events</h4>
      <p>Click <strong>Events</strong> to show the events lane above the chart: one labelled, colour-coded bar per event on the same date axis, stacked so overlapping events never hide each other — short events on the top rows, long-running ones underneath. Solid = already run; dashed and pale = still to come. Hover a bar for the dates and revenue. The events are the Events tab's own list (MERAC-linked events from the Sync Hub). The faint bands on the chart itself mark the same dates.</p>
    </>
  )

  return (
    <ChartCard
      title="Daily revenue trend"
      loading={loading}
      error={error}
      empty={rows.length === 0}
      className={className}
      info={info}
      modeBar={<ModePillBar mode={mode} onChange={setMode} />}
      trendToggle={<TrendToggle on={trendOn} onChange={setTrendOn} />}
      eventsToggle={<EventsToggle on={eventsOn} onChange={setEventsOn} />}
    >
      {eventsOn && rows.length > 0 && (
        <EventsLane events={events} dates={rows.map((r) => r.date)} todayIso={todayIso} leftPx={8 + 60} rightPx={24} />
      )}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={dataWithTrend} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
          <XAxis
            dataKey="date"
            ticks={monthTicks}
            tickFormatter={formatTick}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            tickFormatter={(v) => gbpCompact(v as number)}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={60}
          />
          <Tooltip
            labelFormatter={formatTooltipLabel}
            formatter={(v) => gbp0.format(v as number)}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
          />
          <Legend iconType="plainline" wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="net_revenue"
            name="Net revenue"
            stroke="#0b3d2e"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          {/* Budget run-rate overlay — Traffic (All/Daily) or Pullman (Pullman mode) */}
          <Line
            type="monotone"
            dataKey="budget"
            name={budgetLabel}
            stroke={BUDGET_GOLD}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls
            hide={!showBudget}
          />
          <Line
            type="linear"
            dataKey="trend"
            name="Trend (actual)"
            stroke={TREND_RED}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            isAnimationActive={false}
            legendType={trendOn ? 'plainline' : 'none'}
            hide={!trendOn}
          />
          <Line
            type="linear"
            dataKey="budget_trend"
            name="Trend (budget)"
            stroke={BUDGET_GOLD}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            isAnimationActive={false}
            legendType={showBudget && trendOn ? 'plainline' : 'none'}
            hide={!(showBudget && trendOn)}
          />
          {renderBankHolidayPins(dailyPins)}
          {eventsOn && renderEventBands(eventBands)}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function BookingSourceChart({ season, className, from, to, initialMode }: { season: string | null; className?: string; from?: string | null; to?: string | null; initialMode?: Mode | null }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const { rows, loading, error } = useBookingSource(season, mode, from, to)
  const total = useMemo(() => rows.reduce((s, r) => s + r.net_revenue, 0), [rows])

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Where bookings came from, as a share of total booking revenue. Only includes transactions flagged as bookings (not walk-up retail or Tea Room sales).</p>
      <h4>How it's calculated</h4>
      <p>Bookings are grouped by their captured source — Email, Phone, Walk-in, NYMR Member, and so on. <strong>'Unknown'</strong> means no source was recorded at the time of booking; most internet bookings currently fall into this bucket, which is a known data quality gap.</p>
    </>
  )

  return (
    <ChartCard title="Booking source breakdown" loading={loading} error={error} empty={rows.length === 0} className={className} info={info} modeBar={<ModePillBar mode={mode} onChange={setMode} />}>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={rows}
            dataKey="net_revenue"
            nameKey="source"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={1}
            stroke="#fff"
            strokeWidth={2}
          >
            {rows.map((_, i) => (
              <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(v, name) => [
              `${gbp0.format(v as number)} (${total > 0 ? Math.round(((v as number) / total) * 100) : 0}%)`,
              name as string,
            ]}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
          />
          <Legend
            iconType="circle"
            wrapperStyle={{ fontSize: 11 }}
            layout="vertical"
            align="right"
            verticalAlign="middle"
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function MonthlyFootfallChart({ season, className, bankHolidays = [], events = [], from, to, initialTrend, initialEvents }: { season: string | null; className?: string; bankHolidays?: BankHoliday[]; events?: EventRow[]; from?: string | null; to?: string | null; initialTrend?: boolean | null; initialEvents?: boolean | null }) {
  const [trendOn, setTrendOn] = useState(initialTrend ?? false)
  const [eventsOn, setEventsOn] = useState(initialEvents ?? false)
  const { rows, loading, error } = useMonthlyFootfall(season, from, to)
  const monthPins = useMemo(
    () => dedupHolidaysByX(bankHolidays, (h) => holidayMonthYearLabel(h.date)),
    [bankHolidays]
  )
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const eventBands = useMemo(
    () => eventMonthlyBands(events, rows, todayIso),
    [events, rows, todayIso]
  )
  const dataWithTrend = useMemo(
    () => withTrendValues(rows, 'footfall', 'trend'),
    [rows]
  )
  const info = (
    <>
      <h4>What this shows</h4>
      <p>Number of visitors per calendar month for the selected season.</p>
      <h4>How it's calculated</h4>
      <p>Counted from the dedicated footfall feed (independent of sales). Only confirmed visits are included — tickets booked for future dates that haven't been used yet are excluded.</p>
      <h4>Blue pins</h4>
      <p>Months that contain an England-and-Wales bank holiday. Hover for the holiday name (or long-weekend cluster).</p>
      <h4>Trend line</h4>
      <p>Click <strong>Trend</strong> to overlay a dashed red line of best fit through the monthly totals. Helpful for spotting whether the season is broadly climbing, flat, or sliding compared to its own pace.</p>
      <h4>Event bands</h4>
      <p>Click <strong>Events</strong> to overlay coloured bands across the months that contain a scheduled event. Past events render solid muted green; future events render gold with a dashed border. Hover for the event's name, dates and revenue.</p>
    </>
  )
  return (
    <ChartCard
      title="Monthly footfall"
      loading={loading}
      error={error}
      empty={rows.length === 0}
      className={className}
      info={info}
      trendToggle={<TrendToggle on={trendOn} onChange={setTrendOn} />}
      eventsToggle={<EventsToggle on={eventsOn} onChange={setEventsOn} />}
    >
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={dataWithTrend} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis
            dataKey="month_label"
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            tickFormatter={(v) => num0.format(v as number)}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={60}
          />
          <Tooltip
            formatter={(v) => num0.format(v as number)}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(200, 155, 60, 0.1)' }}
          />
          <Bar dataKey="footfall" name="Footfall" fill="#c89b3c" radius={[3, 3, 0, 0]} />
          <Line
            type="linear"
            dataKey="trend"
            name="Trend"
            stroke={TREND_RED}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            isAnimationActive={false}
            hide={!trendOn}
          />
          {renderBankHolidayPins(monthPins)}
          {eventsOn && renderEventBands(eventBands)}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function RevenueYoyChart({ season, className, bankHolidays = [], events = [], from, to, initialMode, initialTrend, initialEvents }: { season: string | null; className?: string; bankHolidays?: BankHoliday[]; events?: EventRow[]; from?: string | null; to?: string | null; initialMode?: Mode | null; initialTrend?: boolean | null; initialEvents?: boolean | null }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const [trendOn, setTrendOn] = useState(initialTrend ?? false)
  const [eventsOn, setEventsOn] = useState(initialEvents ?? false)
  const { rows, loading, error } = useRevenueYoy(season, mode, from, to)

  const currentLabel  = season ? shortFy(season)                    : 'Current'
  const priorLabelTxt = season ? shortFy(priorSeasonLabel(season))  : 'Prior'

  // YoY's x-axis is just "Apr", "May", … — holidays from this fiscal year are
  // pinned at the matching short month label.
  const yoyPins = useMemo(
    () => dedupHolidaysByX(bankHolidays, (h) => holidayMonthShortLabel(h.date)),
    [bankHolidays]
  )
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const eventBands = useMemo(
    () => eventYoyBands(events, todayIso),
    [events, todayIso]
  )

  // Trend is computed against the current-season series only — prior is
  // settled history and trending it adds visual noise without insight.
  // Always compute it so the data shape is stable; the Line just hides when
  // the toggle is off.
  const dataWithTrend = useMemo(
    () => withTrendValues(rows, 'current', 'trend'),
    [rows]
  )

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Monthly net revenue (after VAT) for the selected season alongside the previous season, so you can see at a glance whether each month is ahead of or behind last year. Months run March (the start of NYMR's fiscal year) through February.</p>
      <h4>How it's calculated</h4>
      <p>Sales are grouped by the fiscal month they belong to and totaled. The current season includes confirmed bookings for future months — so months you haven't reached yet may already show some revenue, and will continue to grow as more bookings are taken. The previous season is settled history.</p>
      <h4>Blue pins</h4>
      <p>Months containing an England-and-Wales bank holiday in the current fiscal year. Hover for the holiday name (or long-weekend cluster).</p>
      <h4>Trend line</h4>
      <p>Click <strong>Trend</strong> to overlay a dashed red line of best fit through the <em>current</em> season. Bear in mind that future months are still light (forward bookings only) so the trend will tilt downward early in the year and flatten as bookings fill in.</p>
      <h4>Event bands</h4>
      <p>Click <strong>Events</strong> to overlay coloured bands marking the months covered by scheduled events in the current fiscal year. Past events render solid muted green; future events render gold with a dashed border. Hover for the event's name, dates and revenue.</p>
    </>
  )

  return (
    <ChartCard
      title="Revenue: this year vs last year"
      loading={loading}
      error={error}
      empty={rows.length === 0}
      className={className}
      info={info}
      modeBar={<ModePillBar mode={mode} onChange={setMode} />}
      trendToggle={<TrendToggle on={trendOn} onChange={setTrendOn} />}
      eventsToggle={<EventsToggle on={eventsOn} onChange={setEventsOn} />}
    >
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={dataWithTrend} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis
            dataKey="month_label"
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            tickFormatter={(v) => gbpCompact(v as number)}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={60}
          />
          <Tooltip
            formatter={(v) => gbp0.format(v as number)}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="prior"   name={priorLabelTxt} fill="#c89b3c" radius={[3, 3, 0, 0]} />
          <Bar dataKey="current" name={currentLabel}  fill="#0b3d2e" radius={[3, 3, 0, 0]} />
          <Line
            type="linear"
            dataKey="trend"
            name="Trend"
            stroke={TREND_RED}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            isAnimationActive={false}
            hide={!trendOn}
          />
          {renderBankHolidayPins(yoyPins)}
          {eventsOn && renderEventBands(eventBands)}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function BookingLeadTimeChart({ season, className, from, to, initialMode }: { season: string | null; className?: string; from?: string | null; to?: string | null; initialMode?: Mode | null }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const { rows, loading, error } = useBookingLeadTime(season, mode, from, to)
  const info = (
    <>
      <h4>What this shows</h4>
      <p>How far in advance customers book — the height of each bar is the number of bookings that fell into that lead-time band.</p>
      <h4>How it's calculated</h4>
      <p>For every booking, we look at the gap in days between when the booking was made and the date of the actual visit, then group them into bands. Walk-ups (lead time of 0–7 days) sit on the left; long-range planners (180+ days) on the right. Hover any bar to see the matching net revenue.</p>
    </>
  )
  return (
    <ChartCard title="Booking lead time" loading={loading} error={error} empty={rows.length === 0} className={className} info={info} modeBar={<ModePillBar mode={mode} onChange={setMode} />}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rows} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis
            dataKey="bucket_label"
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            interval={0}
          />
          <YAxis
            tickFormatter={(v) => num0.format(v as number)}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={50}
          />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(v: any, _name: any, item: any) => {
              const revenue = item?.payload?.net_revenue ?? 0
              return [`${num0.format(v as number)} bookings (${gbpCompact(revenue)})`, 'Bookings']
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          <Bar dataKey="booking_count" name="Bookings" fill="#0b3d2e" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function RevenueByDowChart({ season, className, from, to, initialMode }: { season: string | null; className?: string; from?: string | null; to?: string | null; initialMode?: Mode | null }) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'all')
  const { rows, loading, error } = useRevenueByDow(season, mode, from, to)
  const info = (
    <>
      <h4>What this shows</h4>
      <p>Average net revenue (after VAT) earned on each weekday across the selected season — a like-for-like comparison of how each day of the week performs.</p>
      <h4>How it's calculated</h4>
      <p>Net revenue is summed by day-of-week, then divided by how many of that weekday actually occurred in the season. So a Saturday total of £1.25M over 53 Saturdays gives an average of about £24,000 per Saturday — directly comparable to a Tuesday's average even though they have slightly different counts of dates.</p>
    </>
  )
  return (
    <ChartCard title="Average revenue by weekday" loading={loading} error={error} empty={rows.length === 0} className={className} info={info} modeBar={<ModePillBar mode={mode} onChange={setMode} />}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rows} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis
            dataKey="day_label"
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            tickFormatter={(v) => gbpCompact(v as number)}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={60}
          />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(v: any, _name: any, item: any) => {
              const visits = item?.payload?.visit_days ?? 0
              return [`${gbp0.format(v as number)} avg (${visits} ${visits === 1 ? 'day' : 'days'})`, 'Avg / day']
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(200, 155, 60, 0.1)' }}
          />
          <Bar dataKey="avg_per_day" name="Avg / day" fill="#c89b3c" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// Top postcode districts by bookings. Self-fetching so it serves both the
// season-scoped Dashboard One (#4, mode-aware, default daily) and the Pullman
// page (P2, mode=pullman) which has a from/to range but no season label.
interface PostcodeRow {
  postcode: string
  bookings: number
  gross_revenue: number
  net_revenue: number
}

function TopPostcodesChart({
  season, from, to, initialMode, showModeBar, title, className,
}: {
  season?: string | null
  from?: string | null
  to?: string | null
  initialMode?: Mode
  showModeBar?: boolean
  title?: string
  className?: string
}) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'daily')
  const [rows, setRows] = useState<PostcodeRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const haveBasis = !!season || !!(from && to)
    if (!haveBasis) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const p = new URLSearchParams()
        p.set('mode', mode)
        p.set('limit', '10')
        if (season) p.set('season', season)
        if (from) p.set('from', from)
        if (to) p.set('to', to)
        const res = await fetch(`/api/dashboards/dashboard-one/top-postcodes?${p.toString()}`)
        if (!res.ok) throw new Error(`top-postcodes returned ${res.status}`)
        const json = (await res.json()) as { rows: PostcodeRow[] }
        if (!cancelled) setRows(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season, from, to, mode])

  const data = useMemo(() => [...rows].sort((a, b) => b.bookings - a.bookings), [rows])
  const height = Math.max(240, data.length * 30)
  const info = (
    <>
      <h4>What this shows</h4>
      <p>The ten postcode districts (e.g. <strong>YO18</strong>) that made the most bookings in the selected period, ranked — where our customers travel from.</p>
      <h4>How it&apos;s calculated</h4>
      <p>Each online / account booking carries a customer postcode. We count distinct bookings per outward postcode district and take the top ten. Walk-up till sales (no postcode) are excluded. Hover a bar for the net revenue from that district.</p>
    </>
  )
  return (
    <ChartCard
      title={title ?? 'Top postcodes by bookings'}
      loading={loading}
      error={error}
      empty={data.length === 0}
      className={className}
      info={info}
      modeBar={showModeBar ? <ModePillBar mode={mode} onChange={setMode} /> : undefined}
    >
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 56, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" horizontal={false} />
          <XAxis type="number" tickFormatter={(v) => num0.format(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis type="category" dataKey="postcode" width={64} tick={{ fontSize: 11, fill: '#1a1a1a' }} stroke="#e5e1d6" />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(v: any, _n: any, item: any) => {
              const r = item?.payload as PostcodeRow | undefined
              const net = r?.net_revenue ?? 0
              return [`${num0.format(v as number)} ${(v as number) === 1 ? 'booking' : 'bookings'} · ${gbp0.format(Math.round(net))} net`, r?.postcode ?? '']
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          <Bar dataKey="bookings" name="Bookings" fill="#0b3d2e" radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// Breakdown by service & days with start→end point, numbers and revenue (#3).
// Origin/destination is parsed from the Session description ("departs X, ends
// Y") since the warehouse FromStation/ToStation columns are empty placeholders.
interface ServiceBreakdownRow {
  service: string
  session_time: string | null
  direction: string | null
  service_days: number
  passengers: number
  gross_revenue: number
  net_revenue: number
}

function parseRoute(desc: string): { from: string; to: string } {
  const m = /departs\s+(.+?),\s*ends\s+(.+?)\.?$/i.exec(desc || '')
  if (m) return { from: m[1].trim(), to: m[2].trim() }
  return { from: desc || '—', to: '' }
}

function ServicesBreakdownTable({
  season, from, to, initialMode, className,
}: {
  season?: string | null
  from?: string | null
  to?: string | null
  initialMode?: Mode
  className?: string
}) {
  const [mode, setMode] = useState<Mode>(initialMode ?? 'daily')
  const [rows, setRows] = useState<ServiceBreakdownRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const haveBasis = !!season || !!(from && to)
    if (!haveBasis) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const p = new URLSearchParams()
        p.set('mode', mode)
        if (season) p.set('season', season)
        if (from) p.set('from', from)
        if (to) p.set('to', to)
        const res = await fetch(`/api/dashboards/dashboard-one/services-breakdown?${p.toString()}`)
        if (!res.ok) throw new Error(`services-breakdown returned ${res.status}`)
        const json = (await res.json()) as { rows: ServiceBreakdownRow[] }
        if (!cancelled) setRows(json.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season, from, to, mode])

  const totals = useMemo(
    () => rows.reduce((a, r) => ({ pax: a.pax + r.passengers, net: a.net + r.net_revenue }), { pax: 0, net: 0 }),
    [rows]
  )

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Each timetabled service in the selected period — its route (start → end point), departure time and direction — with the number of days it ran, passengers carried and net revenue. Ranked by revenue.</p>
      <h4>How it&apos;s calculated</h4>
      <p>Sales are grouped by the service&apos;s route/time/direction. <strong>Passengers</strong> = admission tickets sold; <strong>Net</strong> = revenue after VAT across all lines on that service. The route comes from the service description — origin/destination isn&apos;t separately recorded, so a service that has no route description is shown as-is.</p>
    </>
  )

  return (
    <ChartCard
      title="Services breakdown — route, days, passengers & revenue"
      loading={loading}
      error={error}
      empty={rows.length === 0}
      className={className}
      info={info}
      modeBar={<ModePillBar mode={mode} onChange={setMode} />}
    >
      <div className="svc-table-wrap">
        <table className="svc-table">
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th className="ta-right">Time</th>
              <th>Direction</th>
              <th className="ta-right">Days</th>
              <th className="ta-right">Passengers</th>
              <th className="ta-right">Net revenue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const { from: f, to: t } = parseRoute(r.service)
              return (
                <tr key={`${r.service}|${r.session_time}|${r.direction}|${i}`}>
                  <td>{f}</td>
                  <td>{t || '—'}</td>
                  <td className="ta-right">{r.session_time ?? '—'}</td>
                  <td>{r.direction ?? '—'}</td>
                  <td className="ta-right">{num0.format(r.service_days)}</td>
                  <td className="ta-right">{num0.format(r.passengers)}</td>
                  <td className="ta-right">{gbp0.format(Math.round(r.net_revenue))}</td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>Total ({rows.length} {rows.length === 1 ? 'service' : 'services'})</td>
              <td className="ta-right">{num0.format(totals.pax)}</td>
              <td className="ta-right">{gbp0.format(Math.round(totals.net))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </ChartCard>
  )
}

// Capacity & load (#2) — how full the scheduled trains ran: passengers carried
// vs an INDICATIVE seat capacity (Standard 7×64=448, Autocar 48, Pullman 103),
// per service type. Placeholders until ERP gives the real per-service carriage
// count — spelled out in the info popover.
interface CapacityType { key: string; label: string; seats_each: number; departures: number; seats: number; passengers: number; load_pct: number | null }
interface CapacityData {
  season: string; from: string; to: string
  constants: { seats_per_carriage: number; standard_carriages: number; standard_seats: number; autocar_seats: number; pullman_seats: number }
  types: CapacityType[]
  totals: { departures: number; seats: number; passengers: number; load_pct: number | null }
}

function LoadBar({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="cap-load cap-load--na">—</span>
  const w = Math.max(0, Math.min(100, pct))
  const tone = pct >= 75 ? 'high' : pct >= 40 ? 'mid' : 'low'
  return (
    <span className="cap-load" title={`${pct}% of indicative seats`}>
      <span className="cap-load__track"><span className={`cap-load__fill cap-load__fill--${tone}`} style={{ width: `${w}%` }} /></span>
      <span className="cap-load__pct">{pct}%</span>
    </span>
  )
}

function CapacityCard({ season, from, to, className }: { season?: string | null; from?: string | null; to?: string | null; className?: string }) {
  const [data, setData] = useState<CapacityData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const haveBasis = !!season || !!(from && to)
    if (!haveBasis) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const p = new URLSearchParams()
        if (season) p.set('season', season)
        if (from) p.set('from', from)
        if (to) p.set('to', to)
        const res = await fetch(`/api/dashboards/dashboard-one/capacity?${p.toString()}`)
        if (!res.ok) throw new Error(`capacity returned ${res.status}`)
        const json = (await res.json()) as CapacityData
        if (!cancelled) setData(json)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season, from, to])

  const c = data?.constants
  const info = (
    <>
      <h4>What this shows</h4>
      <p>How full our scheduled trains ran — passengers carried against the seats offered — for Standard, Autocar and Pullman services in the selected period.</p>
      <h4>How it&apos;s calculated</h4>
      <p><strong>Seat capacity is indicative for now.</strong> A Standard service is assumed to be {c ? c.standard_carriages : 7} carriages × {c ? c.seats_per_carriage : 64} seats = <strong>{c ? c.standard_seats : 448}</strong>; an <strong>Autocar</strong> seats <strong>{c ? c.autocar_seats : 48}</strong>; a <strong>Pullman</strong> seats <strong>{c ? c.pullman_seats : 103}</strong> across all carriages. When ERP goes live we&apos;ll have the accurate, contextual per-service carriage count for each departure and these figures will firm up.</p>
      <p><strong>Passengers</strong> are counted so one person = one passenger. For Standard and Autocar that&apos;s the fare admission line; for <strong>Pullman</strong> it&apos;s the per-person <em>Travel</em> line only — a Pullman diner also books Food, Supplement, Coupe and Drinks lines, and counting all of those overstated the load (it read over 100%). Events, galas, charters and non-scheduled dining are excluded.</p>
      <p><strong>To date only.</strong> The window is capped at today, so only services that have actually run are counted — advance bookings for future departures are not.</p>
    </>
  )

  const empty = !data || data.totals.departures === 0
  return (
    <ChartCard title="Capacity &amp; load (indicative)" loading={loading} error={error} empty={empty} className={className} info={info}>
      {data && (
        <div className="svc-table-wrap">
          <table className="svc-table cap-table">
            <thead>
              <tr>
                <th>Service</th>
                <th className="ta-right">Departures</th>
                <th className="ta-right">Seats/service</th>
                <th className="ta-right">Seats offered</th>
                <th className="ta-right">Passengers</th>
                <th className="cap-load-col">Load</th>
              </tr>
            </thead>
            <tbody>
              {data.types.map((t) => (
                <tr key={t.key}>
                  <td>{t.label}</td>
                  <td className="ta-right">{num0.format(t.departures)}</td>
                  <td className="ta-right">{num0.format(t.seats_each)}</td>
                  <td className="ta-right">{num0.format(t.seats)}</td>
                  <td className="ta-right">{num0.format(t.passengers)}</td>
                  <td className="cap-load-col"><LoadBar pct={t.load_pct} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>All scheduled</td>
                <td className="ta-right">{num0.format(data.totals.departures)}</td>
                <td className="ta-right">—</td>
                <td className="ta-right">{num0.format(data.totals.seats)}</td>
                <td className="ta-right">{num0.format(data.totals.passengers)}</td>
                <td className="cap-load-col"><LoadBar pct={data.totals.load_pct} /></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </ChartCard>
  )
}

// Website cart abandonment (P3) — site-wide GA4 funnel (cart→purchase). Rolling
// window, independent of the season selector. GA4 has no begin_checkout event,
// so this is cart abandonment only and cannot be split by Pullman/daily/events.
interface Ga4Window {
  start: string
  end: string
  sessions: number
  add_to_carts: number
  purchases: number
  transactions: number
  revenue: number
  abandonment_rate_pct: number | null
  by_day?: { date: string; add_to_carts: number; purchases: number; abandonment_rate_pct: number | null }[]
}
interface Ga4Data {
  days: number
  has_data: boolean
  last_refresh: string | null
  current: Ga4Window | null
  previous: Ga4Window | null
}

interface YtMonth { month: string; estimated_revenue: number; ad_revenue: number; premium_revenue: number; memberships_other: number; views: number; watch_minutes: number }
interface YtData {
  season: string; has_data: boolean
  current_month: YtMonth | null
  season_to_date: { estimated_revenue: number; ad_revenue: number; premium_revenue: number; memberships_other: number; views: number }
  months: YtMonth[]
  subscriber_count: number | null
  subscriber_date: string | null
}

function YouTubeRevenueCard({ className }: { className?: string }) {
  const [data, setData] = useState<YtData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/dashboards/dashboard-one/youtube-revenue')
        if (!res.ok) throw new Error(`youtube returned ${res.status}`)
        const j = (await res.json()) as YtData
        if (!cancelled) setData(j)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const info = (
    <>
      <h4>What this shows</h4>
      <p>The NYMR YouTube channel&apos;s <strong>estimated monetisation revenue</strong> from the YouTube Analytics API: the current month (provisional — YouTube finalises figures around the 10th of the following month) and the season to date, split into <strong>ad revenue</strong> and <strong>memberships &amp; other</strong> (channel memberships, Supers etc. — derived as total − ads − Premium, because YouTube exposes no direct memberships metric).</p>
      <h4>Members</h4>
      <p>Paid channel-member <em>counts</em> are a restricted YouTube API (visible only in YouTube Studio); the card shows the public <strong>subscriber</strong> count instead.</p>
      <h4>Refresh</h4>
      <p>Loaded by the youtube_revenue.py pull (ga4-tools) — re-run it with --post to update.</p>
    </>
  )
  const stat = (label: string, value: string, sub?: string, color?: string) => (
    <div key={label} style={{ flex: '1 1 130px', border: '1px solid #e5e1d6', borderRadius: 8, padding: '10px 12px', background: '#faf9f5' }}>
      <div style={{ fontSize: '0.7rem', color: '#6b6b6b' }}>{label}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: 700, color: color ?? '#0b3d2e' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.68rem', color: '#8a8a8a' }}>{sub}</div>}
    </div>
  )
  const cur = data?.current_month
  const tot = data?.season_to_date
  return (
    <ChartCard
      title="YouTube channel revenue"
      loading={loading}
      error={error}
      empty={!!data && !data.has_data}
      className={className}
      info={info}
    >
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {stat('This month (provisional)', cur ? gbp0.format(Math.round(cur.estimated_revenue)) : '—', cur ? `ads ${gbp0.format(Math.round(cur.ad_revenue))} · memberships & other ${gbp0.format(Math.round(cur.memberships_other))}` : 'no row for the current month yet')}
        {stat(`Season ${data?.season ?? ''} to date`, tot ? gbp0.format(Math.round(tot.estimated_revenue)) : '—', tot ? `ads ${gbp0.format(Math.round(tot.ad_revenue))} · memberships & other ${gbp0.format(Math.round(tot.memberships_other))} · premium ${gbp0.format(Math.round(tot.premium_revenue))}` : undefined)}
        {stat('Subscribers', data?.subscriber_count != null ? data.subscriber_count.toLocaleString('en-GB') : '—', data?.subscriber_date ? `as at ${data.subscriber_date} · paid-member counts are Studio-only` : 'paid-member counts are Studio-only', '#8a6d1a')}
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data?.months ?? []} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis dataKey="month" tickFormatter={(m) => new Date(m).toLocaleDateString('en-GB', { month: 'short' })} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={52} />
          <Tooltip
            labelFormatter={(m) => new Date(m as string).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            formatter={(v, name) => [gbp0.format(Math.round(v as number)), name]}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
          />
          <Bar dataKey="ad_revenue" name="Ads" stackId="r" fill="#0b3d2e" />
          <Bar dataKey="memberships_other" name="Memberships & other" stackId="r" fill="#c89b3c" />
          <Bar dataKey="premium_revenue" name="Premium" stackId="r" fill="#8a8a8a" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function Ga4CartAbandonmentCard({ className }: { className?: string }) {
  const [data, setData] = useState<Ga4Data | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/dashboards/ga4/cart-abandonment?days=30')
        if (!res.ok) throw new Error(`ga4 returned ${res.status}`)
        const j = (await res.json()) as Ga4Data
        if (!cancelled) setData(j)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const cur = data?.current
  const prev = data?.previous
  const curRate = cur?.abandonment_rate_pct ?? null
  const prevRate = prev?.abandonment_rate_pct ?? null
  const deltaPp = curRate != null && prevRate != null ? Math.round((curRate - prevRate) * 10) / 10 : null

  const info = (
    <>
      <h4>What this shows</h4>
      <p>The share of website shopping baskets that <strong>don&apos;t</strong> convert to a purchase, over the last 30 days, from Google Analytics 4 for www.nymr.co.uk. The dashed gold line is the previous 30 days for comparison.</p>
      <h4>How it&apos;s calculated</h4>
      <p>1 − (purchases ÷ add-to-cart events). This is a <strong>site-wide</strong> figure across all online sales — GA4 doesn&apos;t record a checkout step on this site (the basket hands off to an external booking engine), so it can&apos;t be split by Pullman / daily / events, and true checkout-funnel abandonment isn&apos;t available.</p>
      <h4>Caveat</h4>
      <p>Basket and purchase counts are reliable throughout; on-site purchase revenue was only tagged correctly from ~14 May 2026.</p>
    </>
  )

  const badge = curRate != null ? (
    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
      {curRate.toFixed(1)}%
      {deltaPp != null && (
        <span style={{ marginLeft: 6, fontSize: 11, color: deltaPp > 0 ? TREND_RED : deltaPp < 0 ? '#177245' : '#6b6b6b' }}>
          {deltaPp > 0 ? '▲' : deltaPp < 0 ? '▼' : '■'} {Math.abs(deltaPp).toFixed(1)}pp vs prev 30d
        </span>
      )}
    </span>
  ) : undefined

  return (
    <ChartCard
      title="Website cart abandonment (all online bookings)"
      loading={loading}
      error={error}
      empty={!!data && !data.has_data}
      className={className}
      info={info}
      actions={badge}
    >
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={cur?.by_day ?? []} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
          <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" minTickGap={24} />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={44} />
          <Tooltip
            labelFormatter={(d) => new Date(d as string).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(v: any, _n: any, item: any) => {
              const r = item?.payload as NonNullable<Ga4Window['by_day']>[number] | undefined
              const rate = v == null ? '—' : `${(v as number).toFixed(1)}%`
              return [`${rate} abandoned — ${num0.format(r?.add_to_carts ?? 0)} baskets, ${num0.format(r?.purchases ?? 0)} bought`, 'Cart abandonment']
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
          />
          {prevRate != null && (
            <ReferenceLine y={prevRate} stroke={BUDGET_GOLD} strokeDasharray="4 4" label={{ value: `Prev ${prevRate.toFixed(1)}%`, position: 'right', fill: BUDGET_GOLD, fontSize: 10 }} />
          )}
          <Line type="monotone" dataKey="abandonment_rate_pct" name="Cart abandonment" stroke={TREND_RED} strokeWidth={2} dot={false} connectNulls activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// ---------- Dashboard Two — Group Bookings -------------------------------
// Mirrors Dashboard One's core elements but scoped to Group Bookings, from a
// single envelope endpoint (all cache.AllSales, no aggregate table). Group
// bookings otherwise live inside the 'event' mode, so this is their own view.
function monthKeyToLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${names[(m || 1) - 1]} ${y}`
}

interface DashboardTwoData {
  season: string
  from: string
  to: string
  kpis: { gross_revenue: number; net_revenue: number; bookings: number; passengers: number; sale_lines: number }
  prev_season: string | null
  kpis_prev: { gross_revenue: number; net_revenue: number; bookings: number; passengers: number; sale_lines: number } | null
  by_dow: { day_no: number; day_label: string; gross_revenue: number; net_revenue: number; bookings: number; passengers: number }[]
  daily: { date: string; gross_revenue: number; net_revenue: number }[]
  monthly: { year_month: string; gross_revenue: number; net_revenue: number }[]
  top_postcodes: { postcode: string; bookings: number; net_revenue: number }[]
  journeys: { service: string; direction: string | null; service_days: number; passengers: number; net_revenue: number; bookings: number }[]
}

function DashboardTwo() {
  const { seasons } = useSeasons()
  const [season, setSeason] = useState<string | null>(null)
  useEffect(() => {
    if (season || seasons.length === 0) return
    const cur = seasons.find((s) => s.is_current) ?? seasons[0]
    setSeason(cur.year_label)
  }, [seasons, season])

  const [data, setData] = useState<DashboardTwoData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!season) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await fetch(`/api/dashboards/dashboard-two/groups?season=${encodeURIComponent(season)}`)
        if (!res.ok) throw new Error(`dashboard-two returned ${res.status}`)
        const j = (await res.json()) as DashboardTwoData
        if (!cancelled) setData(j)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season])

  const dowData = useMemo(() => {
    const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    const byNo = new Map((data?.by_dow ?? []).map((r) => [r.day_no, r]))
    return Array.from({ length: 7 }, (_, i) => {
      const r = byNo.get(i + 1)
      return { day_label: names[i], net_revenue: r?.net_revenue ?? 0, bookings: r?.bookings ?? 0, passengers: r?.passengers ?? 0 }
    })
  }, [data])

  const monthlyData = useMemo(
    () => (data?.monthly ?? []).map((m) => ({ ...m, label: monthKeyToLabel(m.year_month) })),
    [data]
  )
  const postcodeData = useMemo(() => [...(data?.top_postcodes ?? [])], [data])
  const journeyData = useMemo(
    () => (data?.journeys ?? []).slice(0, 12).map((j) => {
      const { from, to } = parseRoute(j.service)
      return { ...j, route: to ? `${from} → ${to}` : from }
    }),
    [data]
  )
  const lyLabel = data?.prev_season ? `last year (${data.prev_season})` : 'last year'

  return (
    <main className="app-main">
      <header className="pullman-head">
        <div>
          <p className="pullman-head__sub">Group bookings only — the same headline view as Dashboard One, scoped to groups. Cards show last year underneath.</p>
        </div>
        <label className="sales-field">
          <span>Season</span>
          <select value={season ?? ''} onChange={(e) => setSeason(e.target.value)}>
            {seasons.map((s) => (
              <option key={s.year_label} value={s.year_label}>{s.year_label}</option>
            ))}
          </select>
        </label>
      </header>

      {loading && !data && <p>Loading…</p>}
      {error && <p style={{ color: '#a33' }}>Couldn&apos;t load: {error}</p>}

      {data && (
        <>
          <section className="kpi-section">
            <header className="kpi-section__head">
              <span className="kpi-section__label">Group bookings at a glance</span>
            </header>
            <div className="kpi-bar">
              <KpiCard label="Gross revenue" value={gbpCompact(data.kpis.gross_revenue)} loading={loading}
                sub={data.kpis_prev ? { label: lyLabel, value: gbpCompact(data.kpis_prev.gross_revenue), muted: true } : undefined} />
              <KpiCard label="Net revenue"   value={gbpCompact(data.kpis.net_revenue)}   loading={loading}
                sub={data.kpis_prev ? { label: lyLabel, value: gbpCompact(data.kpis_prev.net_revenue), muted: true } : undefined} />
              <KpiCard label="Bookings"      value={num0.format(data.kpis.bookings)}      loading={loading}
                sub={data.kpis_prev ? { label: lyLabel, value: num0.format(data.kpis_prev.bookings), muted: true } : undefined} />
              <KpiCard label="Passengers"    value={num0.format(data.kpis.passengers)}    loading={loading}
                sub={data.kpis_prev ? { label: lyLabel, value: num0.format(data.kpis_prev.passengers), muted: true } : undefined} />
            </div>
          </section>

          <div className="dashboard-grid">
            {/* Daily revenue trend */}
            <ChartCard title="Group revenue — daily" loading={false} error={null} empty={data.daily.length === 0} className="grid-span-12">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.daily} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
                  <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-GB', { month: 'short' })} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" minTickGap={28} />
                  <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={60} />
                  <Tooltip labelFormatter={(d) => new Date(d as string).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} formatter={(v) => gbp0.format(v as number)} contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }} />
                  <Line type="monotone" dataKey="net_revenue" name="Net revenue" stroke="#0b3d2e" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Revenue by weekday */}
            <ChartCard title="Group revenue by weekday" loading={false} error={null} empty={dowData.every((d) => d.net_revenue === 0)} className="grid-span-6">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={dowData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
                  <XAxis dataKey="day_label" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
                  <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={56} />
                  <Tooltip formatter={(v) => gbp0.format(v as number)} contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }} cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }} />
                  <Bar dataKey="net_revenue" name="Net revenue" fill="#0b3d2e" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Monthly revenue */}
            <ChartCard title="Group revenue by month" loading={false} error={null} empty={monthlyData.length === 0} className="grid-span-6">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
                  <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={56} />
                  <Tooltip formatter={(v) => gbp0.format(v as number)} contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }} cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }} />
                  <Bar dataKey="net_revenue" name="Net revenue" fill="#c89b3c" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Ranked journeys */}
            <ChartCard title="Top journeys (start → end)" loading={false} error={null} empty={journeyData.length === 0} className="grid-span-6"
              info={<><h4>What this shows</h4><p>The group-booking routes carrying the most passengers, ranked. Route comes from each service&apos;s description.</p></>}>
              <ResponsiveContainer width="100%" height={Math.max(240, journeyData.length * 30)}>
                <BarChart data={journeyData} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => num0.format(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
                  <YAxis type="category" dataKey="route" width={150} tick={{ fontSize: 10, fill: '#1a1a1a' }} stroke="#e5e1d6" />
                  <Tooltip
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formatter={(v: any, _n: any, item: any) => {
                      const r = item?.payload as (typeof journeyData)[number] | undefined
                      return [`${num0.format(v as number)} passengers · ${gbp0.format(Math.round(r?.net_revenue ?? 0))} net`, r?.route ?? '']
                    }}
                    contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
                    cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
                  />
                  <Bar dataKey="passengers" name="Passengers" fill="#0b3d2e" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Top postcodes */}
            <ChartCard title="Top postcodes by bookings" loading={false} error={null} empty={postcodeData.length === 0} className="grid-span-6"
              info={<><h4>What this shows</h4><p>The postcode districts (e.g. YO18) making the most group bookings. District only — no full postcode.</p></>}>
              <ResponsiveContainer width="100%" height={Math.max(240, postcodeData.length * 30)}>
                <BarChart data={postcodeData} layout="vertical" margin={{ top: 8, right: 48, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => num0.format(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
                  <YAxis type="category" dataKey="postcode" width={64} tick={{ fontSize: 11, fill: '#1a1a1a' }} stroke="#e5e1d6" />
                  <Tooltip
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formatter={(v: any, _n: any, item: any) => {
                      const r = item?.payload as (typeof postcodeData)[number] | undefined
                      return [`${num0.format(v as number)} bookings · ${gbp0.format(Math.round(r?.net_revenue ?? 0))} net`, r?.postcode ?? '']
                    }}
                    contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
                    cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
                  />
                  <Bar dataKey="bookings" name="Bookings" fill="#c89b3c" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <p className="dashboard-footer">
            Group bookings only (MERAC booking type &ldquo;Group Booking&rdquo;) for season <code>{data.season}</code>. Refreshes daily from <code>OntrackDW.RPT.AllSales</code>.
          </p>
        </>
      )}
    </main>
  )
}

function DashboardOne({ me }: { me?: MeResponse | null }) {
  const { seasons, loading: seasonsLoading, error: seasonsError } = useSeasons()
  const [activeYear, setActiveYear] = useState<string | null>(null)
  // The season bar's slots for the KPI header (label + as-of / refresh).
  const [headLead, setHeadLead] = useState<HTMLSpanElement | null>(null)
  const [headTrail, setHeadTrail] = useState<HTMLSpanElement | null>(null)

  // Date-range picker state. Defaults to the active season's full range so
  // the initial render is identical to the pre-picker behavior; users can
  // narrow within the season.
  const [from, setFrom] = useState<string | null>(null)
  const [to,   setTo]   = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Default to the current season once seasons load
  useEffect(() => {
    if (activeYear || seasons.length === 0) return
    const cur = seasons.find((s) => s.is_current) ?? seasons[0]
    setActiveYear(cur.year_label)
  }, [seasons, activeYear])

  // When activeYear changes, snap the picker back to the full season range
  // (switching seasons should reset any narrow filter the user had).
  useEffect(() => {
    const range = seasonDateRange(activeYear)
    if (range) {
      setFrom(range.from)
      setTo(range.to)
    }
  }, [activeYear])

  const yearForKpi = useMemo(() => activeYear, [activeYear])

  // Bank holidays for the active fiscal year, fetched once and shared by
  // every time-series chart that wants to draw pins.
  const seasonRange = useMemo(() => seasonDateRange(yearForKpi), [yearForKpi])
  const { rows: bankHolidays } = useBankHolidays(
    seasonRange?.from ?? null,
    seasonRange?.to ?? null
  )

  // Events for the active season, fetched once and shared by the three
  // time-series charts that draw event bands.
  const { rows: events } = useEventsForRange(
    seasonRange?.from ?? null,
    seasonRange?.to ?? null
  )

  // Picker presets, all clamped to the active season's range.
  const presets: DateRangePreset[] = useMemo(() => {
    if (!seasonRange) return []
    const today = new Date().toISOString().slice(0, 10)
    const dminus = (n: number): string => {
      const d = new Date(today + 'T00:00:00')
      d.setDate(d.getDate() - n)
      return d.toISOString().slice(0, 10)
    }
    const monthStart = today.slice(0, 7) + '-01'
    return [
      { label: 'Full season',  from: seasonRange.from,                                          to: seasonRange.to                                              },
      { label: 'Year to date', from: seasonRange.from,                                          to: clampIso(today,         seasonRange.from, seasonRange.to)   },
      { label: 'This month',   from: clampIso(monthStart,    seasonRange.from, seasonRange.to), to: clampIso(today,         seasonRange.from, seasonRange.to)   },
      { label: 'Last 30 days', from: clampIso(dminus(30),    seasonRange.from, seasonRange.to), to: clampIso(today,         seasonRange.from, seasonRange.to)   },
      { label: 'Last 90 days', from: clampIso(dminus(90),    seasonRange.from, seasonRange.to), to: clampIso(today,         seasonRange.from, seasonRange.to)   },
    ]
  }, [seasonRange])

  const isCustomRange = !!(
    seasonRange && from && to &&
    (from !== seasonRange.from || to !== seasonRange.to)
  )
  // The full-season view reads the PRE-BUILT agg.DashboardOne_* tables (built
  // nightly by dbo.RebuildDashboardOneAggregates on the same 1 Mar – 28 Feb
  // year), not a season-wide scan of cache.AllSales per chart — every endpoint
  // treats a from/to as "compute ad hoc", so only a narrowed range sends one.
  // Same numbers (verified 2026-09-25), ~15 fewer season scans per page load.
  const qFrom = isCustomRange ? from : null
  const qTo   = isCustomRange ? to   : null
  const triggerLabel = from && to
    ? `${formatPrettyDate(from)} \u2014 ${formatPrettyDate(to)}`
    : 'Pick a date range'

  if (seasonsLoading) return <main className="app-main"><p>Loading seasons…</p></main>
  if (seasonsError)
    return (
      <main className="app-main">
        <p style={{ color: '#a33' }}>Couldn't load seasons: {seasonsError}</p>
      </main>
    )

  return (
    <>
      {/* ONE bar (Chris, 2026-10-28): Season at a glance + as-of, the year
          pills, the date range, then the refresh and its note. */}
      <SeasonBar
        seasons={seasons} activeYear={activeYear} onChange={setActiveYear}
        lead={<span ref={setHeadLead} className="season-bar__slot season-bar__slot--lead" />}
        trail={<>
          <button
            type="button"
            className="daterange-trigger daterange-trigger--compact"
            onClick={() => setPickerOpen(true)}
            disabled={!from || !to}
          >
            <span className="daterange-trigger__icon" aria-hidden="true">📅</span>
            <span className="daterange-trigger__label">{triggerLabel}</span>
          </button>
          {isCustomRange && (
            <span style={{ fontSize: '0.75rem', color: '#6b6b6b', fontStyle: 'italic' }} title="Every chart on the page is showing the selected range.">
              Filter active
            </span>
          )}
          <span ref={setHeadTrail} className="season-bar__slot" />
        </>}
      />
      <main className="app-main">
        <DashboardOneKpiBar season={yearForKpi} from={qFrom} to={qTo} me={me} headSlots={{ lead: headLead, trail: headTrail }} />
        <div className="dashboard-grid">
          <DailyRevenueChart        season={yearForKpi} className="grid-span-12" bankHolidays={bankHolidays} events={events} from={qFrom} to={qTo} />
          <RevenueYoyChart          season={yearForKpi} className="grid-span-12" bankHolidays={bankHolidays} events={events} from={qFrom} to={qTo} />
          <RevenueByDepartmentChart season={yearForKpi} className="grid-span-7"  from={qFrom} to={qTo} />
          <BookingSourceChart       season={yearForKpi} className="grid-span-5"  from={qFrom} to={qTo} />
          <MonthlyFootfallChart     season={yearForKpi} className="grid-span-12" bankHolidays={bankHolidays} events={events} from={qFrom} to={qTo} />
          <BookingLeadTimeChart     season={yearForKpi} className="grid-span-6"  from={qFrom} to={qTo} />
          <RevenueByDowChart        season={yearForKpi} className="grid-span-6"  from={qFrom} to={qTo} />
          <TopPostcodesChart        season={yearForKpi} className="grid-span-12" from={qFrom} to={qTo} initialMode="daily" showModeBar title="Top postcodes by bookings (daily services)" />
          <ServicesBreakdownTable   season={yearForKpi} className="grid-span-12" from={qFrom} to={qTo} initialMode="daily" />
          <CapacityCard             season={yearForKpi} className="grid-span-12" from={qFrom} to={qTo} />
          <Ga4CartAbandonmentCard   className="grid-span-12" />
          <YouTubeRevenueCard       className="grid-span-12" />
        </div>
        <p className="dashboard-footer">
          Showing <code>{activeYear}</code>{isCustomRange ? `, narrowed to ${formatPrettyDate(from!)} \u2013 ${formatPrettyDate(to!)}` : ''}. Data refreshes daily at 06:00 from <code>OntrackDW.RPT.*</code>.
        </p>
      </main>

      {pickerOpen && seasonRange && from && to && (
        <DateRangeModal
          initialFrom={from}
          initialTo={to}
          minDate={seasonRange.from}
          maxDate={seasonRange.to}
          presets={presets}
          onCancel={() => setPickerOpen(false)}
          onApply={(range) => { setFrom(range.from); setTo(range.to); setPickerOpen(false) }}
        />
      )}
    </>
  )
}

// ---------- Date range picker (global) -----------------------------------
//
// Two side-by-side single-month calendars (Left = From, Right = To) with a
// row of quick-preset chips at the top and a clear summary line beneath.
// Designed from day one as the global date filter for every dashboard;
// Pullman uses it first, the other dashboards pick it up later.

interface DateRangePreset {
  label: string
  from: string
  to: string
}

interface DateRangeValue {
  from: string
  to: string
}

interface DateRangeModalProps {
  initialFrom: string
  initialTo: string
  minDate?: string  // optional clamp on selectable dates (inclusive)
  maxDate?: string  // optional clamp on selectable dates (inclusive)
  presets?: DateRangePreset[]
  onCancel: () => void
  onApply: (range: DateRangeValue) => void
}

function isoToParts(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10))
  return { year: y, month: m, day: d }
}

function partsToIso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`
}

function daysInMonth(year: number, month: number): number {
  // month is 1..12. Date(year, month, 0) returns the last day of `month`.
  return new Date(year, month, 0).getDate()
}

// 0=Mon..6=Sun -- the order our calendar grid uses.
function dayOfWeekMon0(year: number, month: number, day: number): number {
  const d = new Date(Date.UTC(year, month - 1, day))
  const js = d.getUTCDay()  // 0=Sun..6=Sat
  return (js + 6) % 7
}

function clampIso(iso: string, minIso?: string, maxIso?: string): string {
  if (minIso !== undefined && iso < minIso) return minIso
  if (maxIso !== undefined && iso > maxIso) return maxIso
  return iso
}

// '2026-03-24' -> '24 Mar 2026'
function formatPrettyDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function daysBetweenInclusive(fromIso: string, toIso: string): number {
  const a = new Date(fromIso + 'T00:00:00').getTime()
  const b = new Date(toIso + 'T00:00:00').getTime()
  return Math.round((b - a) / 86400000) + 1
}

interface CalendarCell {
  iso: string
  day: number
  inMonth: boolean
  inRange: boolean    // strictly between from and to (or equal to either)
  isFrom: boolean
  isTo: boolean
  isToday: boolean
  isDisabled: boolean
}

function buildCalendarCell(
  iso: string,
  day: number,
  inMonth: boolean,
  fromIso: string,
  toIso: string,
  todayIso: string,
  minIso?: string,
  maxIso?: string
): CalendarCell {
  const validRange = fromIso <= toIso
  const inRange = validRange && iso >= fromIso && iso <= toIso
  const isFrom = iso === fromIso
  const isTo   = iso === toIso
  const isToday = iso === todayIso
  const isDisabled =
    (minIso !== undefined && iso < minIso) ||
    (maxIso !== undefined && iso > maxIso)
  return { iso, day, inMonth, inRange, isFrom, isTo, isToday, isDisabled }
}

// 6 rows of 7, padded with leading days from previous month and trailing
// days from next month so the grid looks tidy whatever weekday the 1st is.
function generateCalendarMonth(
  year: number,
  month: number,
  fromIso: string,
  toIso: string,
  todayIso: string,
  minIso?: string,
  maxIso?: string
): CalendarCell[][] {
  const firstWeekday = dayOfWeekMon0(year, month, 1)
  const daysThis = daysInMonth(year, month)
  const prevMonth = month === 1 ? 12 : month - 1
  const prevYear  = month === 1 ? year - 1 : year
  const daysPrev = daysInMonth(prevYear, prevMonth)
  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear  = month === 12 ? year + 1 : year

  const flat: CalendarCell[] = []

  for (let i = firstWeekday - 1; i >= 0; i--) {
    const day = daysPrev - i
    flat.push(buildCalendarCell(
      partsToIso(prevYear, prevMonth, day), day, false,
      fromIso, toIso, todayIso, minIso, maxIso
    ))
  }
  for (let day = 1; day <= daysThis; day++) {
    flat.push(buildCalendarCell(
      partsToIso(year, month, day), day, true,
      fromIso, toIso, todayIso, minIso, maxIso
    ))
  }
  let nextDay = 1
  while (flat.length < 42) {
    flat.push(buildCalendarCell(
      partsToIso(nextYear, nextMonth, nextDay), nextDay, false,
      fromIso, toIso, todayIso, minIso, maxIso
    ))
    nextDay++
  }

  const rows: CalendarCell[][] = []
  for (let r = 0; r < 6; r++) rows.push(flat.slice(r * 7, r * 7 + 7))
  return rows
}

const DOW_LABELS_MON_FIRST = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function MonthCalendar({
  year, month, grid, onPrev, onNext, onClickCell,
}: {
  year: number
  month: number
  grid: CalendarCell[][]
  onPrev: () => void
  onNext: () => void
  onClickCell: (iso: string) => void
}) {
  const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long' })
  return (
    <div className="cal-month">
      <header className="cal-month__head">
        <button type="button" className="cal-month__nav" aria-label="Previous month" onClick={onPrev}>‹</button>
        <span className="cal-month__title">{monthName} {year}</span>
        <button type="button" className="cal-month__nav" aria-label="Next month" onClick={onNext}>›</button>
      </header>
      <div className="cal-month__dayrow" aria-hidden="true">
        {DOW_LABELS_MON_FIRST.map((d) => (
          <span key={d} className="cal-month__dow">{d}</span>
        ))}
      </div>
      <div className="cal-month__grid">
        {grid.flatMap((row, ri) => row.map((cell, ci) => {
          const cls = [
            'cal-cell',
            cell.inMonth      ? '' : 'cal-cell--outside',
            cell.inRange      ? 'cal-cell--range'    : '',
            cell.isFrom       ? 'cal-cell--from'     : '',
            cell.isTo         ? 'cal-cell--to'       : '',
            cell.isToday      ? 'cal-cell--today'    : '',
            cell.isDisabled   ? 'cal-cell--disabled' : '',
          ].filter(Boolean).join(' ')
          return (
            <button
              key={`${ri}-${ci}-${cell.iso}`}
              type="button"
              className={cls}
              onClick={() => !cell.isDisabled && onClickCell(cell.iso)}
              disabled={cell.isDisabled}
              aria-label={cell.iso}
              tabIndex={cell.inMonth && !cell.isDisabled ? 0 : -1}
            >
              {cell.day}
            </button>
          )
        }))}
      </div>
    </div>
  )
}

function DateRangeModal({
  initialFrom, initialTo, minDate, maxDate, presets, onCancel, onApply,
}: DateRangeModalProps) {
  const [from, setFrom] = useState<string>(initialFrom)
  const [to,   setTo]   = useState<string>(initialTo)

  // Each calendar pane tracks its own visible month independently so the
  // user can navigate the From and To panels separately without one yanking
  // the other.
  const initFromParts = useMemo(() => isoToParts(initialFrom), [initialFrom])
  const initToParts   = useMemo(() => isoToParts(initialTo),   [initialTo])
  const [fromMonth, setFromMonth] = useState({ year: initFromParts.year, month: initFromParts.month })
  const [toMonth,   setToMonth]   = useState({ year: initToParts.year,   month: initToParts.month   })

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  const fromGrid = useMemo(
    () => generateCalendarMonth(fromMonth.year, fromMonth.month, from, to, todayIso, minDate, maxDate),
    [fromMonth, from, to, todayIso, minDate, maxDate]
  )
  const toGrid = useMemo(
    () => generateCalendarMonth(toMonth.year, toMonth.month, from, to, todayIso, minDate, maxDate),
    [toMonth, from, to, todayIso, minDate, maxDate]
  )

  const stepMonth = (target: 'from' | 'to', delta: -1 | 1) => {
    const setter = target === 'from' ? setFromMonth : setToMonth
    setter((cur) => {
      let m = cur.month + delta
      let y = cur.year
      if (m < 1)  { m = 12; y -= 1 }
      if (m > 12) { m = 1;  y += 1 }
      return { year: y, month: m }
    })
  }

  // Click on the From panel sets From; if it lands beyond To, drag To along
  // (so the range stays valid). Mirrored for the To panel.
  const onClickFromCell = (iso: string) => {
    setFrom(iso)
    if (iso > to) setTo(iso)
  }
  const onClickToCell = (iso: string) => {
    setTo(iso)
    if (iso < from) setFrom(iso)
  }

  const onPickPreset = (p: DateRangePreset) => {
    const f = clampIso(p.from, minDate, maxDate)
    const t = clampIso(p.to,   minDate, maxDate)
    setFrom(f); setTo(t)
    const fp = isoToParts(f); const tp = isoToParts(t)
    setFromMonth({ year: fp.year, month: fp.month })
    setToMonth({   year: tp.year, month: tp.month   })
  }

  const valid = from <= to
  const days  = valid ? daysBetweenInclusive(from, to) : 0
  const summary = valid
    ? `${formatPrettyDate(from)} \u2014 ${formatPrettyDate(to)} (${num0.format(days)} ${days === 1 ? 'day' : 'days'})`
    : `From must be on or before To`

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="modal modal--daterange" role="dialog" aria-modal="true" aria-labelledby="dr-title">
        <header className="modal__head">
          <h3 id="dr-title" style={{ margin: 0 }}>Pick a date range</h3>
          <button type="button" className="modal__close" aria-label="Close" onClick={onCancel}>×</button>
        </header>
        <div className="modal__body daterange-body">
          {presets && presets.length > 0 && (
            <div className="daterange-presets" role="toolbar" aria-label="Quick presets">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className="daterange-preset"
                  onClick={() => onPickPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          <div className="daterange-calendars">
            <div className="daterange-side">
              <div className="daterange-side__head">
                <span className="daterange-side__label">From</span>
                <span className="daterange-side__value">{formatPrettyDate(from)}</span>
              </div>
              <MonthCalendar
                year={fromMonth.year}
                month={fromMonth.month}
                grid={fromGrid}
                onPrev={() => stepMonth('from', -1)}
                onNext={() => stepMonth('from', +1)}
                onClickCell={onClickFromCell}
              />
            </div>
            <div className="daterange-side">
              <div className="daterange-side__head">
                <span className="daterange-side__label">To</span>
                <span className="daterange-side__value">{formatPrettyDate(to)}</span>
              </div>
              <MonthCalendar
                year={toMonth.year}
                month={toMonth.month}
                grid={toGrid}
                onPrev={() => stepMonth('to', -1)}
                onNext={() => stepMonth('to', +1)}
                onClickCell={onClickToCell}
              />
            </div>
          </div>

          <div className={`daterange-summary ${valid ? '' : 'daterange-summary--invalid'}`}>
            {summary}
          </div>
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onApply({ from, to })}
            disabled={!valid}
          >
            Apply range
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------- Pullman dashboard components --------------------------------

const PULLMAN_GREEN = '#0b3d2e'
const PULLMAN_GOLD  = '#c89b3c'
const PULLMAN_TEAL  = '#1f6e54'

function pctLabel(pct: number, decimals = 1): string {
  return `${(pct * 100).toFixed(decimals)}%`
}

// Last-season figure under a KPI (Compare to last season, 2026-10-23): grey, with the change.
function lySub(label: string, current: number, ly: PullmanKpis | null | undefined, pick: (k: PullmanKpis) => number, fmt: (n: number) => string) {
  if (!ly) return undefined
  const v = pick(ly)
  return { label, value: `${fmt(v)} · ${deltaLabel(current, v, fmt)}`, muted: true }
}

function PullmanRevenueBar({ kpis, loading, ly, lyLabel }: { kpis: PullmanKpis; loading: boolean; ly?: PullmanKpis | null; lyLabel?: string }) {
  const L = lyLabel ?? 'Last season'
  const info = (
    <>
      <h4>About these figures</h4>
      <p>Pullman revenue across the selected date range, drawn from the live MERAC ticketing system.</p>
      <h4>What each one means</h4>
      <p>
        <strong>Net revenue</strong> &mdash; total Pullman sales after VAT.<br/>
        <strong>Gross revenue</strong> &mdash; total Pullman sales including VAT.<br/>
        <strong>Net per service</strong> &mdash; net revenue &divide; services with revenue data ({kpis.services_with_revenue} of {kpis.service_count}). Forward-booked services with no revenue yet are excluded so the average isn&apos;t dragged down.<br/>
        <strong>Net per person</strong> &mdash; net revenue &divide; total people.
      </p>
    </>
  )

  return (
    <section className="kpi-section">
      <header className="kpi-section__head">
        <span className="kpi-section__label">Pullman revenue</span>
        <InfoPopover>{info}</InfoPopover>
      </header>
      <div className="kpi-bar">
        <KpiCard label="Net revenue"     value={gbpCompact(kpis.revenue_net_total)}        loading={loading} sub={lySub(L, kpis.revenue_net_total, ly, (k) => k.revenue_net_total, gbpCompact)} />
        <KpiCard label="Gross revenue"   value={gbpCompact(kpis.revenue_gross_total)}      loading={loading} sub={lySub(L, kpis.revenue_gross_total, ly, (k) => k.revenue_gross_total, gbpCompact)} />
        <KpiCard label="Net per service" value={gbp0.format(kpis.revenue_net_per_service)} loading={loading} sub={lySub(L, kpis.revenue_net_per_service, ly, (k) => k.revenue_net_per_service, (n) => gbp0.format(n))} />
        <KpiCard label="Net per person"  value={gbp0.format(kpis.revenue_net_per_person)}  loading={loading} sub={lySub(L, kpis.revenue_net_per_person, ly, (k) => k.revenue_net_per_person, (n) => gbp0.format(n))} />
      </div>
    </section>
  )
}

function PullmanKpiBar({ kpis, loading, ly, lyLabel }: { kpis: PullmanKpis; loading: boolean; ly?: PullmanKpis | null; lyLabel?: string }) {
  const L = lyLabel ?? 'Last season'
  const info = (
    <>
      <h4>About these figures</h4>
      <p>Headline numbers across the selected date range. Pullman dining train; capacity per service comes from the service&apos;s own seating plan (104 people on the current plan: 92 individual seats + 3 coupes &times; 4).</p>
      <h4>What each one means</h4>
      <p>
        <strong>Services</strong> &mdash; Pullman services scheduled in the range.<br/>
        <strong>People</strong> &mdash; total people sold (individual seats + coupes &times; 4).<br/>
        <strong>Avg load</strong> &mdash; total people &divide; total capacity of the services in the range.<br/>
        <strong>Coupe util</strong> &mdash; coupes sold &divide; coupes available on those services.<br/>
        <strong>Best / Worst</strong> &mdash; single best- and worst-loaded services in the range. Hover for the date.
      </p>
      <h4>The PB10 rule</h4>
      <p>In MERAC each &ldquo;price band 10&rdquo; record represents one entire coupe (private compartment) sold as a unit, seating four. Treating it as one ticket would understate Pullman bookings by up to 20 people per service.</p>
    </>
  )

  const bestTitle = kpis.highest
    ? `${formatPrettyDate(kpis.highest.service_date)} \u2014 ${kpis.highest.service_description}`
    : ''
  const worstTitle = kpis.lowest
    ? `${formatPrettyDate(kpis.lowest.service_date)} \u2014 ${kpis.lowest.service_description}`
    : ''

  return (
    <section className="kpi-section">
      <header className="kpi-section__head">
        <span className="kpi-section__label">Pullman at a glance</span>
        <InfoPopover>{info}</InfoPopover>
      </header>
      <div className="kpi-bar">
        <KpiCard label="Services"   value={num0.format(kpis.service_count)}      loading={loading} sub={lySub(L, kpis.service_count, ly, (k) => k.service_count, (n) => num0.format(n))} />
        <KpiCard label="People"     value={num0.format(kpis.people_total)}       loading={loading} sub={lySub(L, kpis.people_total, ly, (k) => k.people_total, (n) => num0.format(n))} />
        <KpiCard label="Avg load"   value={pctLabel(kpis.avg_pct_full)}          loading={loading} sub={ly ? { label: L, value: `${pctLabel(ly.avg_pct_full)} · ${deltaLabel(Math.round(kpis.avg_pct_full * 100), Math.round(ly.avg_pct_full * 100), (n) => `${n} pts`)}`, muted: true } : undefined} />
        <KpiCard label="Coupe util" value={pctLabel(kpis.coupe_utilisation_pct)} loading={loading} sub={ly ? { label: L, value: pctLabel(ly.coupe_utilisation_pct), muted: true } : undefined} />
        <div className="kpi-card" title={bestTitle}>
          <div className="kpi-card__label">Best service</div>
          <div className="kpi-card__value">
            {loading ? '\u2026' : kpis.highest
              ? `${num0.format(kpis.highest.people_total)} (${pctLabel(kpis.highest.pct_full, 0)})`
              : '\u2014'}
          </div>
        </div>
        <div className="kpi-card" title={worstTitle}>
          <div className="kpi-card__label">Worst service</div>
          <div className="kpi-card__value">
            {loading ? '\u2026' : kpis.lowest
              ? `${num0.format(kpis.lowest.people_total)} (${pctLabel(kpis.lowest.pct_full, 0)})`
              : '\u2014'}
          </div>
        </div>
      </div>
    </section>
  )
}

function PullmanDailyOccupancyChart({
  services, kpis, className,
}: {
  services: PullmanService[]
  kpis: PullmanKpis
  className?: string
}) {
  const monthTicks = useMemo(() => {
    const ticks: string[] = []
    let lastMonth = ''
    for (const s of services) {
      const ym = s.service_date.slice(0, 7)
      if (ym !== lastMonth) { ticks.push(s.service_date); lastMonth = ym }
    }
    return ticks
  }, [services])

  const formatTick = (d: string) => new Date(d).toLocaleDateString('en-GB', { month: 'short' })
  const formatTooltipLabel = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const avgPeoplePerService =
    kpis.service_count > 0 ? kpis.people_total / kpis.service_count : 0

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Total people sold per Pullman service, across the selected date range. Each point is one service.</p>
      <h4>How it&apos;s calculated</h4>
      <p><code>people = individual_seats + coupes &times; 4</code>. Hover any point for the service description and headcount.</p>
      <h4>Reference lines</h4>
      <p>The grey dashed line at 103 is full capacity. The gold dashed line is the average across the selected date range.</p>
    </>
  )

  return (
    <ChartCard
      title="Daily occupancy"
      loading={false}
      error={null}
      empty={services.length === 0}
      className={className}
      info={info}
    >
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={services} margin={{ top: 8, right: 60, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
          <XAxis
            dataKey="service_date"
            ticks={monthTicks}
            tickFormatter={formatTick}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            domain={[0, 103]}
            ticks={[0, 25, 50, 75, 103]}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={40}
          />
          <Tooltip
            labelFormatter={formatTooltipLabel}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(v: any, _name: any, item: any) => {
              const r = item?.payload as PullmanService | undefined
              const pct = r ? pctLabel(r.pct_full, 0) : ''
              return [
                `${num0.format(v as number)} people (${pct})`,
                r?.service_description ?? 'People',
              ]
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
          />
          <ReferenceLine
            y={103}
            stroke="#6b6b6b"
            strokeDasharray="4 4"
            label={{ value: 'Capacity 103', position: 'right', fill: '#6b6b6b', fontSize: 10 }}
          />
          {avgPeoplePerService > 0 && (
            <ReferenceLine
              y={avgPeoplePerService}
              stroke={PULLMAN_GOLD}
              strokeDasharray="4 4"
              label={{
                value: `Avg ${avgPeoplePerService.toFixed(0)}`,
                position: 'right',
                fill: PULLMAN_GOLD,
                fontSize: 10,
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="people_total"
            name="People"
            stroke={PULLMAN_GREEN}
            strokeWidth={2}
            dot={{ r: 2.5, fill: PULLMAN_GREEN }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function PullmanStackedSeatsChart({
  services, className,
}: {
  services: PullmanService[]
  className?: string
}) {
  const data = useMemo(() => services.map((s) => ({
    service_date: s.service_date,
    individual: s.individual_seats,
    coupe_people: s.coupes_sold * 4,
    coupes_sold: s.coupes_sold,
    total: s.people_total,
    description: s.service_description,
  })), [services])

  const monthTicks = useMemo(() => {
    const ticks: string[] = []
    let lastMonth = ''
    for (const r of data) {
      const ym = r.service_date.slice(0, 7)
      if (ym !== lastMonth) { ticks.push(r.service_date); lastMonth = ym }
    }
    return ticks
  }, [data])

  const formatTick = (d: string) => new Date(d).toLocaleDateString('en-GB', { month: 'short' })
  const formatTooltipLabel = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Each bar is a single Pullman service. The dark green portion is people in individual seats; the gold portion is people in coupes (a coupe seats four and is sold as one unit, so each coupe sold here counts as 4).</p>
      <h4>Why it&apos;s useful</h4>
      <p>Coupe utilisation typically outperforms open-seat utilisation by ~17 percentage points across the season. The gold portion shows visually how much of each service&apos;s loading came from coupe sales.</p>
    </>
  )

  return (
    <ChartCard
      title="Open seats vs coupes per service"
      loading={false}
      error={null}
      empty={data.length === 0}
      className={className}
      info={info}
    >
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis
            dataKey="service_date"
            ticks={monthTicks}
            tickFormatter={formatTick}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            domain={[0, 103]}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
            width={40}
          />
          <Tooltip
            labelFormatter={formatTooltipLabel}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(v: any, name: any, item: any) => {
              const r = item?.payload as { description?: string; coupes_sold?: number } | undefined
              if (name === 'Individual') {
                return [`${num0.format(v as number)} people`, r?.description ?? 'Individual']
              }
              if (name === 'Coupes') {
                const coupes = r?.coupes_sold ?? Math.round((v as number) / 4)
                return [`${num0.format(v as number)} people (${coupes} ${coupes === 1 ? 'coupe' : 'coupes'})`, 'Coupes']
              }
              return [String(v), String(name)]
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="individual"   stackId="a" name="Individual" fill={PULLMAN_GREEN} />
          <Bar dataKey="coupe_people" stackId="a" name="Coupes"     fill={PULLMAN_GOLD}  />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// P1 — busiest day by *revenue*: total Pullman net sales for each weekday, with
// the top day highlighted in gold. Distinct from the occupancy "By day of week"
// chart below (which shows average people) and from the Demand tab (fill/pace).
function PullmanRevenueByDowChart({
  rows, kpis, className,
}: {
  rows: PullmanByDow[]
  kpis: PullmanKpis
  className?: string
}) {
  const padded = useMemo(() => {
    const byNum = new Map(rows.map((r) => [r.day_of_week_num, r]))
    const names = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    const out: PullmanByDow[] = []
    for (let i = 1; i <= 7; i++) {
      out.push(byNum.get(i) ?? {
        day_of_week_num: i,
        day_of_week: names[i - 1],
        service_count: 0,
        people_total: 0,
        avg_people: 0,
        avg_pct_full: 0,
        revenue_gross: 0,
        revenue_net: 0,
        services_with_revenue: 0,
        avg_revenue_net: 0,
      })
    }
    return out
  }, [rows])

  // Busiest day = highest TOTAL net revenue across the range (the user's ask).
  const busiest = useMemo(() => {
    let best: PullmanByDow | null = null
    for (const r of padded) {
      if (r.revenue_net <= 0) continue
      if (!best || r.revenue_net > best.revenue_net) best = r
    }
    return best
  }, [padded])

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Total Pullman <strong>net</strong> sales revenue for each day of the week, across the selected date range. The busiest day by revenue is highlighted in gold.</p>
      <h4>How it&apos;s calculated</h4>
      <p>Each Pullman service&apos;s net revenue (from the live MERAC ticketing system) is summed by the weekday it runs on. Because it&apos;s a total, weekdays that run more often in the range naturally accumulate more &mdash; the tooltip also shows the average per service.</p>
    </>
  )

  return (
    <ChartCard
      title="Revenue by day of week"
      loading={false}
      error={null}
      empty={rows.length === 0 || kpis.revenue_net_total <= 0}
      className={className}
      info={info}
      actions={busiest ? (
        <span
          style={{
            fontSize: 11, fontWeight: 700, color: PULLMAN_GOLD,
            background: 'rgba(200, 155, 60, 0.12)', border: `1px solid ${PULLMAN_GOLD}`,
            borderRadius: 999, padding: '2px 10px', whiteSpace: 'nowrap',
          }}
        >
          Busiest: {busiest.day_of_week} · {gbp0.format(Math.round(busiest.revenue_net))}
        </span>
      ) : undefined}
    >
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={padded} margin={{ top: 8, right: 24, left: 12, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis dataKey="day_of_week" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis tickFormatter={(v) => gbp0.format(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={64} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(_v: any, _name: any, item: any) => {
              const r = item?.payload as PullmanByDow | undefined
              if (!r) return ['']
              return [
                `${gbp0.format(Math.round(r.revenue_net))} net — ${num0.format(r.service_count)} ${r.service_count === 1 ? 'service' : 'services'}, ${gbp0.format(Math.round(r.avg_revenue_net))}/service`,
                r.day_of_week,
              ]
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          <Bar dataKey="revenue_net" name="Net revenue" radius={[3, 3, 0, 0]}>
            {padded.map((r) => (
              <Cell
                key={r.day_of_week_num}
                fill={busiest && r.day_of_week_num === busiest.day_of_week_num ? PULLMAN_GOLD : PULLMAN_GREEN}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function PullmanByDowChart({
  rows, kpis, className,
}: {
  rows: PullmanByDow[]
  kpis: PullmanKpis
  className?: string
}) {
  // Pad to all 7 days so the chart's shape is stable across date ranges.
  const padded = useMemo(() => {
    const byNum = new Map(rows.map((r) => [r.day_of_week_num, r]))
    const names = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    const out: PullmanByDow[] = []
    for (let i = 1; i <= 7; i++) {
      out.push(byNum.get(i) ?? {
        day_of_week_num: i,
        day_of_week: names[i - 1],
        service_count: 0,
        people_total: 0,
        avg_people: 0,
        avg_pct_full: 0,
        revenue_gross: 0,
        revenue_net: 0,
        services_with_revenue: 0,
        avg_revenue_net: 0,
      })
    }
    return out
  }, [rows])

  const rangeAvg = kpis.service_count > 0 ? kpis.people_total / kpis.service_count : 0

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Average people per Pullman service, broken down by day of the week. Days with no services in the range render as empty bars.</p>
      <h4>How it&apos;s calculated</h4>
      <p>Total people on each weekday divided by the number of times that weekday actually had a service. The dashed gold line is the overall average for the date range.</p>
    </>
  )

  return (
    <ChartCard
      title="By day of week"
      loading={false}
      error={null}
      empty={rows.length === 0}
      className={className}
      info={info}
    >
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={padded} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
          <XAxis dataKey="day_of_week" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis domain={[0, 103]} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={40} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(_v: any, _name: any, item: any) => {
              const r = item?.payload as PullmanByDow | undefined
              if (!r) return ['']
              return [
                `${num0.format(Math.round(r.avg_people))} avg \u2014 ${num0.format(r.service_count)} ${r.service_count === 1 ? 'service' : 'services'}, ${pctLabel(r.avg_pct_full, 0)} load`,
                r.day_of_week,
              ]
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          {rangeAvg > 0 && (
            <ReferenceLine
              y={rangeAvg}
              stroke={PULLMAN_GOLD}
              strokeDasharray="4 4"
              label={{ value: `Avg ${rangeAvg.toFixed(0)}`, position: 'right', fill: PULLMAN_GOLD, fontSize: 10 }}
            />
          )}
          <Bar dataKey="avg_people" name="Avg people" fill={PULLMAN_GREEN} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function PullmanByServiceTypeChart({
  rows, kpis, className,
}: {
  rows: PullmanByServiceType[]
  kpis: PullmanKpis
  className?: string
}) {
  const data = useMemo(
    () => [...rows].sort((a, b) => b.avg_pct_full - a.avg_pct_full),
    [rows]
  )
  const rangePct = kpis.avg_pct_full

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Average load percentage by service brand &mdash; Spring Service ex-Pickering, Summer Service ex-Grosmont, Murder Mystery, Whisky Train, etc. Sorted highest to lowest.</p>
      <h4>How it&apos;s calculated</h4>
      <p>Total people on services with that brand divided by their total capacity (services &times; 103). The dashed gold line is the overall average for the date range &mdash; bars to the right are pulling weight; bars to the left are dragging.</p>
    </>
  )

  return (
    <ChartCard
      title="By service type"
      loading={false}
      error={null}
      empty={data.length === 0}
      className={className}
      info={info}
    >
      <ResponsiveContainer width="100%" height={Math.max(220, data.length * 36)}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 60, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 1]}
            tickFormatter={(v) => `${Math.round((v as number) * 100)}%`}
            tick={{ fontSize: 11, fill: '#6b6b6b' }}
            stroke="#e5e1d6"
          />
          <YAxis
            type="category"
            dataKey="service_type"
            width={210}
            tick={{ fontSize: 11, fill: '#1a1a1a' }}
            stroke="#e5e1d6"
          />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(_v: any, _name: any, item: any) => {
              const r = item?.payload as PullmanByServiceType | undefined
              if (!r) return ['']
              return [
                `${pctLabel(r.avg_pct_full, 1)} avg load \u2014 ${num0.format(r.service_count)} ${r.service_count === 1 ? 'service' : 'services'}, ${num0.format(r.people_total)} people`,
                r.service_type,
              ]
            }}
            contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
            cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
          />
          {rangePct > 0 && (
            <ReferenceLine
              x={rangePct}
              stroke={PULLMAN_GOLD}
              strokeDasharray="4 4"
              label={{ value: `Avg ${pctLabel(rangePct, 0)}`, position: 'top', fill: PULLMAN_GOLD, fontSize: 10 }}
            />
          )}
          <Bar dataKey="avg_pct_full" name="Avg load" fill={PULLMAN_TEAL} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

type PullmanSortKey = 'service_date' | 'individual_seats' | 'coupes_sold' | 'people_total' | 'pct_full'

// CSV export — emits a UTF-8-BOM CSV in the current on-screen sort order,
// with a totals row at the bottom matching the table's <tfoot>.
function exportPullmanCsv(
  services: PullmanService[],
  kpis: PullmanKpis,
  fromIso: string | null,
  toIso: string | null,
  note?: string,
): void {
  const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`
  const headers = ['Date', 'Day', 'Departs', 'Service', 'Individual seats', 'Coupes sold', 'People', '% capacity']
  const rows = services.map((s) => [
    esc(s.service_date ?? ''),
    esc(s.day_of_week),
    esc(s.departure_time),
    esc(s.service_description),
    String(s.individual_seats),
    String(s.coupes_sold),
    String(s.people_total),
    `${(s.pct_full * 100).toFixed(1)}%`,
  ])
  const totals = [
    esc(`Totals (${kpis.service_count} services)`), '', '', '',
    String(kpis.individual_seats_total),
    String(kpis.coupes_sold_total),
    String(kpis.people_total),
    `${(kpis.avg_pct_full * 100).toFixed(1)}%`,
  ]
  const lines = [headers, ...rows, totals]
  if (note) lines.push([esc(note)])
  const csv = lines.map((r) => r.join(',')).join('\r\n')
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
  const fname = `pullman-services-${fromIso ?? 'all'}-to-${toIso ?? 'all'}.csv`
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fname
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Print to PDF — opens a self-contained, NYMR-themed print window with the
// table laid out and triggers window.print(). The user can pick any printer
// or "Save as PDF" in the print dialog.
function printPullmanTable(
  services: PullmanService[],
  kpis: PullmanKpis,
  fromIso: string | null,
  toIso: string | null,
  note?: string,
): void {
  const w = window.open('', '_blank', 'width=960,height=720')
  if (!w) {
    alert('Please allow pop-ups for this site to print the report.')
    return
  }
  const fmtDay = (d: string | null) =>
    d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
  const escHtml = (v: string) =>
    v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const rowsHtml = services.map((s) => `
    <tr>
      <td>${fmtDay(s.service_date)}</td>
      <td>${escHtml(s.day_of_week.slice(0, 3))}</td>
      <td>${escHtml(s.departure_time)}</td>
      <td>${escHtml(s.service_description)}</td>
      <td class="num">${num0.format(s.individual_seats)}</td>
      <td class="num">${num0.format(s.coupes_sold)}</td>
      <td class="num"><strong>${num0.format(s.people_total)}</strong></td>
      <td class="num">${pctLabel(s.pct_full, 0)}</td>
    </tr>`).join('')
  const dateLine = fromIso && toIso ? `${fmtDay(fromIso)} \u2014 ${fmtDay(toIso)}` : ''
  const noteHtml = note ? `<p class="sub">${escHtml(note)}</p>` : ''
  w.document.write(`<!doctype html>
<html><head>
<meta charset="utf-8">
<title>Pullman Services Report</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; padding: 18px; }
  h1 { color: #0b3d2e; margin: 0 0 4px; font-size: 18px; }
  .sub { color: #6b6b6b; margin: 0 0 14px; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead th { background: #0b3d2e; color: #fff; text-align: left; padding: 6px 8px; }
  th.num { text-align: right; }
  td { padding: 4px 8px; border-bottom: 1px solid #eee; }
  td.num { text-align: right; }
  tfoot td { font-weight: 600; border-top: 2px solid #0b3d2e; background: #f7f3ea; padding: 6px 8px; }
  @media print { body { padding: 10mm; } }
</style>
</head><body>
<h1>Pullman Services Report</h1>
<p class="sub">${dateLine}</p>
${noteHtml}
<table>
  <thead>
    <tr>
      <th>Date</th><th>Day</th><th>Departs</th><th>Service</th>
      <th class="num">Individual</th><th class="num">Coupes</th>
      <th class="num">People</th><th class="num">% capacity</th>
    </tr>
  </thead>
  <tbody>${rowsHtml}</tbody>
  <tfoot>
    <tr>
      <td colspan="4">Totals (${num0.format(kpis.service_count)} services)</td>
      <td class="num">${num0.format(kpis.individual_seats_total)}</td>
      <td class="num">${num0.format(kpis.coupes_sold_total)}</td>
      <td class="num">${num0.format(kpis.people_total)}</td>
      <td class="num">${pctLabel(kpis.avg_pct_full, 1)}</td>
    </tr>
  </tfoot>
</table>
<script>window.onload = function() { setTimeout(function() { window.print(); }, 50); };</script>
</body></html>`)
  w.document.close()
}

// ---------------------------------------------------------------------------
// Services drill-down: the bookings behind one Pullman service. The popup
// totals reconcile to the clicked row's Individual / Coupes / People (both
// derive from the same SessionSeats price-band model).
//   GET /api/dashboards/pullman/service-bookings?shd=<shd_rec_no>
// ---------------------------------------------------------------------------
interface ServiceBookingRow {
  booking_ref: number
  account_name: string | null
  individual_seats: number
  coupes: number
  people: number
  carriages: string
  tables: string
  seat_refs: string
  coupe_refs: string
  exclusivity_seats: number
}
interface ServiceBookingsResponse {
  shd_rec_no: number
  bookings: ServiceBookingRow[]
  totals: { individual_seats: number; coupes: number; people: number; exclusivity_seats?: number; booking_count: number }
}

function ServiceBookingsModal({ service, excludeExclusivity, onClose }: { service: PullmanService; excludeExclusivity: boolean; onClose: () => void }) {
  const [data, setData] = useState<ServiceBookingsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await fetch(`/api/dashboards/pullman/service-bookings?shd=${service.shd_rec_no}`)
        if (!res.ok) throw new Error(`returned ${res.status}`)
        const json = (await res.json()) as ServiceBookingsResponse
        if (!cancelled) setData(json)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [service.shd_rec_no])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const dateLabel = new Date(service.service_date + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  })
  // With "Exclude Exclusivity Seat Bookings" on, each booking's exclusivity
  // seats come off its seats and people, exactly as the service row above.
  const bookings = useMemo(() => {
    const raw = data?.bookings ?? []
    if (!excludeExclusivity) return raw
    return raw.map((b) => {
      const excl = Math.max(0, Math.min(b.exclusivity_seats ?? 0, b.individual_seats))
      return excl === 0 ? b : { ...b, individual_seats: b.individual_seats - excl, people: Math.max(0, b.people - excl) }
    })
  }, [data, excludeExclusivity])
  const excludedSeats = excludeExclusivity ? bookings.reduce((a, b, i) => a + ((data?.bookings[i]?.individual_seats ?? 0) - b.individual_seats), 0) : 0
  const totals = data
    ? bookings.reduce(
        (acc, b) => { acc.individual_seats += b.individual_seats; acc.coupes += b.coupes; acc.people += b.people; return acc },
        { individual_seats: 0, coupes: 0, people: 0, booking_count: bookings.length },
      )
    : undefined
  // Cross-check the popup total against the service row it drilled from.
  const reconciles = totals
    ? totals.individual_seats === service.individual_seats
      && totals.coupes === service.coupes_sold
      && totals.people === service.people_total
    : true

  return (
    <div className="psb-modal-backdrop" onClick={onClose}>
      <div className="psb-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="psb-modal-head">
          <div>
            <div className="psb-modal-title">{service.service_description}</div>
            <div className="psb-modal-sub">
              {dateLabel} &middot; departs {service.departure_time} &middot; {num0.format(service.people_total)} people
              {' '}({num0.format(service.individual_seats)} seats + {num0.format(service.coupes_sold)} coupe{service.coupes_sold === 1 ? '' : 's'})
            </div>
          </div>
          <button type="button" className="psb-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="psb-modal-body">
          {loading && <p className="psb-msg">Loading bookings&hellip;</p>}
          {error && <p className="psb-msg psb-err">Couldn&rsquo;t load bookings &mdash; {error}</p>}
          {!loading && !error && data && data.bookings.length === 0 && (
            <p className="psb-msg">No seat-level bookings cached for this service yet.</p>
          )}
          {!loading && !error && data && data.bookings.length > 0 && totals && (
            <>
              {!reconciles && (
                <p className="psb-msg psb-warn">
                  Note: cached booking detail is being rebuilt &mdash; totals below may not yet match the service row.
                </p>
              )}
              {excludeExclusivity && excludedSeats > 0 && (
                <p className="psb-msg">
                  Excluding {num0.format(excludedSeats)} exclusivity seat{excludedSeats === 1 ? '' : 's'} (bought to keep a table private &mdash; no diner). Bookings marked &ldquo;excl.&rdquo; show their seats and people without them.
                </p>
              )}
              <div className="psb-table-wrap">
                <table className="psb-table">
                  <thead>
                    <tr>
                      <th>Guest</th>
                      <th className="ta-right">People</th>
                      <th className="ta-right">Seats</th>
                      <th className="ta-right">Coupes</th>
                      <th>Carriage</th>
                      <th>Table</th>
                      <th>Seat refs</th>
                      <th>Coupe ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b, i) => (
                      <tr key={`${b.booking_ref}-${i}`}>
                        <td>
                          {b.account_name || (b.booking_ref === 0 ? '(seat held — no booking)' : `Booking ${b.booking_ref}`)}
                          {excludeExclusivity && (b.exclusivity_seats ?? 0) > 0 && (
                            <span style={{ marginLeft: 6, fontSize: '0.75em', color: '#8a6d1f' }} title={`${b.exclusivity_seats} exclusivity seat${b.exclusivity_seats === 1 ? '' : 's'} excluded`}>
                              excl. {b.exclusivity_seats}
                            </span>
                          )}
                        </td>
                        <td className="ta-right">{num0.format(b.people)}</td>
                        <td className="ta-right">{b.individual_seats ? num0.format(b.individual_seats) : '—'}</td>
                        <td className="ta-right">{b.coupes ? num0.format(b.coupes) : '—'}</td>
                        <td>{b.carriages || '—'}</td>
                        <td>{b.tables || '—'}</td>
                        <td>{b.seat_refs || '—'}</td>
                        <td>{b.coupe_refs || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>{num0.format(totals.booking_count)} booking{totals.booking_count === 1 ? '' : 's'}</td>
                      <td className="ta-right">{num0.format(totals.people)}</td>
                      <td className="ta-right">{num0.format(totals.individual_seats)}</td>
                      <td className="ta-right">{num0.format(totals.coupes)}</td>
                      <td colSpan={4}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function PullmanServicesTable({
  services, kpis, className, from, to,
}: {
  services: PullmanService[]
  kpis: PullmanKpis
  className?: string
  from: string | null
  to: string | null
}) {
  const [sortKey, setSortKey] = useState<PullmanSortKey>('service_date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [drill, setDrill] = useState<PullmanService | null>(null)
  // "Exclude Exclusivity Seat Bookings" — on by default. Panel-local: the rows,
  // totals, Print, CSV and the drill-down popup follow it; the KPI tiles and
  // charts above are unchanged.
  const [excludeExclusivity, setExcludeExclusivity] = useState(true)
  const viewRows = useMemo(() => applyExclusivityExclusion(services, excludeExclusivity), [services, excludeExclusivity])
  const viewKpis = useMemo(() => panelKpis(viewRows, kpis), [viewRows, kpis])
  const excludedSeats = useMemo(
    () => excludeExclusivity ? services.reduce((a, s) => a + Math.max(0, Math.min(s.exclusivity_seats ?? 0, s.individual_seats)), 0) : 0,
    [services, excludeExclusivity],
  )
  const excludedServices = useMemo(
    () => excludeExclusivity ? services.filter((s) => (s.exclusivity_seats ?? 0) > 0).length : 0,
    [services, excludeExclusivity],
  )
  const exclusionNote = excludeExclusivity
    ? `Excluding ${num0.format(excludedSeats)} exclusivity seat${excludedSeats === 1 ? '' : 's'} across ${num0.format(excludedServices)} service${excludedServices === 1 ? '' : 's'} (seats bought to keep a table private — no diner).`
    : undefined

  const sorted = useMemo(() => {
    const out = [...viewRows]
    out.sort((a, b) => {
      const av = a[sortKey] as number | string
      const bv = b[sortKey] as number | string
      if (av < bv) return sortDir === 'asc' ? -1 :  1
      if (av > bv) return sortDir === 'asc' ?  1 : -1
      return 0
    })
    return out
  }, [viewRows, sortKey, sortDir])

  const onSort = (k: PullmanSortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(k)
      setSortDir(k === 'service_date' ? 'asc' : 'desc')
    }
  }

  const arrow = (k: PullmanSortKey) =>
    sortKey === k ? (sortDir === 'asc' ? ' \u25b2' : ' \u25bc') : ''

  const fmtDayShort = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Every Pullman service in the selected date range, with the same columns as the Excel report. Click any header to sort.</p>
      <h4>How it&apos;s calculated</h4>
      <p>One row per scheduled service. <strong>People</strong> = individual seats + coupes &times; 4. <strong>% capacity</strong> = people &divide; 103.</p>
      <p><strong>Exclude Exclusivity Seat Bookings</strong> (on by default) takes out seats sold as &ldquo;Exclusivity Seats&rdquo; &mdash; bought to keep a party&apos;s table private, so a taken seat with no diner. Individual, People and % capacity in this panel (and its Print, CSV and drill-down) are shown without them; the tiles and charts above still include them.</p>
      <p><strong>Click any service row</strong> to see the bookings behind it &mdash; guest, party size, carriage, table, seat &amp; coupe refs &mdash; reconciling to that row&apos;s Individual / Coupes / People.</p>
    </>
  )

  const isEmpty = services.length === 0
  const btnStyle = {
    background: 'transparent',
    border: '1px solid #c89b3c',
    borderRadius: 4,
    padding: '3px 10px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#0b3d2e',
    cursor: isEmpty ? 'not-allowed' : 'pointer',
    opacity: isEmpty ? 0.4 : 1,
    letterSpacing: '0.02em',
  }
  const actions = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button
        type="button"
        onClick={() => setExcludeExclusivity((v) => !v)}
        aria-pressed={excludeExclusivity}
        title={excludeExclusivity
          ? 'On: seats sold as Exclusivity Seats (bought to keep a table private — no diner) are taken out of Individual, People and % capacity in this panel. Click to include them.'
          : 'Off: Exclusivity Seats are counted as seats and people. Click to exclude them.'}
        style={{
          ...btnStyle,
          cursor: 'pointer',
          opacity: 1,
          background: excludeExclusivity ? '#0b3d2e' : 'transparent',
          color: excludeExclusivity ? '#fff' : '#0b3d2e',
          borderColor: excludeExclusivity ? '#0b3d2e' : '#c89b3c',
        }}
      >
        {excludeExclusivity ? '\u2713 ' : ''}Exclude Exclusivity Seat Bookings
      </button>
      <button
        type="button"
        onClick={() => printPullmanTable(sorted, viewKpis, from, to, exclusionNote)}
        title="Print or save as PDF"
        disabled={isEmpty}
        style={btnStyle}
      >
        Print
      </button>
      <button
        type="button"
        onClick={() => exportPullmanCsv(sorted, viewKpis, from, to, exclusionNote)}
        title="Download CSV (current sort order)"
        disabled={isEmpty}
        style={btnStyle}
      >
        CSV
      </button>
    </div>
  )

  return (
    <>
    <ChartCard
      title="Services"
      loading={false}
      error={null}
      empty={isEmpty}
      className={className}
      info={info}
      actions={actions}
    >
      <div className="pullman-table-wrap">
        <table className="pullman-table">
          <thead>
            <tr>
              <th onClick={() => onSort('service_date')} className="sortable">Date{arrow('service_date')}</th>
              <th>Day</th>
              <th>Departs</th>
              <th>Service</th>
              <th onClick={() => onSort('individual_seats')} className="sortable num">Individual{arrow('individual_seats')}</th>
              <th onClick={() => onSort('coupes_sold')}     className="sortable num">Coupes{arrow('coupes_sold')}</th>
              <th onClick={() => onSort('people_total')}    className="sortable num">People{arrow('people_total')}</th>
              <th onClick={() => onSort('pct_full')}        className="sortable num">% capacity{arrow('pct_full')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr
                key={s.shd_rec_no}
                className="psb-row"
                onClick={() => setDrill(s)}
                title="Click to see the bookings behind this service"
              >
                <td>{fmtDayShort(s.service_date)}</td>
                <td>{s.day_of_week.slice(0, 3)}</td>
                <td>{s.departure_time}</td>
                <td title={s.service_type}>{s.service_description}</td>
                <td className="num">{num0.format(s.individual_seats)}</td>
                <td className="num">{num0.format(s.coupes_sold)}</td>
                <td className="num" style={{ fontWeight: 600 }}>{num0.format(s.people_total)}</td>
                <td
                  className="num"
                  data-pct={s.pct_full > 0.85 ? 'high' : s.pct_full < 0.30 ? 'low' : ''}
                >
                  {pctLabel(s.pct_full, 0)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="pullman-table__totals">
              <td colSpan={4}>Totals ({num0.format(viewKpis.service_count)} services)</td>
              <td className="num">{num0.format(viewKpis.individual_seats_total)}</td>
              <td className="num">{num0.format(viewKpis.coupes_sold_total)}</td>
              <td className="num">{num0.format(viewKpis.people_total)}</td>
              <td className="num">{pctLabel(viewKpis.avg_pct_full, 1)}</td>
            </tr>
          </tfoot>
        </table>
        {exclusionNote && (
          <p style={{ margin: '8px 4px 0', fontSize: '0.78rem', color: '#6b6b6b' }}>{exclusionNote}</p>
        )}
      </div>
    </ChartCard>
    {drill && <ServiceBookingsModal service={drill} excludeExclusivity={excludeExclusivity} onClose={() => setDrill(null)} />}
    </>
  )
}

// Pullman Reports sub-tabs (2026-10-23, Kathryn Watson plan): Overview = the page as it
// always was; People / Capacity / Bookings Taken are being built and land as they ship.
type PullmanTab = 'overview' | 'people' | 'capacity' | 'bookings-taken' | 'live'
const PULLMAN_TABS: { key: PullmanTab; to: string; label: string }[] = [
  { key: 'overview',       to: '/pullman',                label: 'Overview' },
  { key: 'people',         to: '/pullman/people',         label: 'Pullman People' },
  { key: 'capacity',       to: '/pullman/capacity',       label: 'Pullman Capacity' },
  { key: 'bookings-taken', to: '/pullman/bookings-taken', label: 'Bookings Taken' },
  { key: 'live',           to: '/pullman/live',           label: 'Pullman Live' },
]
function PullmanSubNav() {
  const cls = ({ isActive }: { isActive: boolean }) => `dashboard-pill ${isActive ? 'active' : ''}`
  return (
    <nav className="pullman-subnav" aria-label="Pullman Reports sections">
      {PULLMAN_TABS.map((t) => <NavLink key={t.key} to={t.to} end={t.key === 'overview'} className={cls}>{t.label}</NavLink>)}
    </nav>
  )
}

// Compare to last season: the three breakdowns side by side (this season · last season).
function PullmanCompareTables({ now, ly, lyLabel }: { now: PullmanResponse; ly: PullmanResponse; lyLabel: string }) {
  const n0 = (n: number) => num0.format(n)
  const pct = (n: number) => pctLabel(n, 0)
  const gbp = (n: number) => gbpCompact(n)
  const Row = ({ label, a, b }: { label: string; a: { service_count: number; people_total: number; avg_pct_full: number; revenue_net?: number } | undefined; b: typeof a }) => (
    <tr>
      <td>{label}</td>
      <td className="ta-right">{a ? n0(a.service_count) : '—'}</td><td className="ta-right"><LyCell current={a?.service_count ?? 0} ly={b?.service_count} fmt={n0} /></td>
      <td className="ta-right">{a ? n0(a.people_total) : '—'}</td><td className="ta-right"><LyCell current={a?.people_total ?? 0} ly={b?.people_total} fmt={n0} /></td>
      <td className="ta-right">{a ? pct(a.avg_pct_full) : '—'}</td><td className="ta-right"><LyCell current={Math.round((a?.avg_pct_full ?? 0) * 100)} ly={b ? Math.round(b.avg_pct_full * 100) : null} fmt={(n) => `${n}%`} /></td>
      {a?.revenue_net !== undefined && <><td className="ta-right">{gbp(a.revenue_net)}</td><td className="ta-right"><LyCell current={a.revenue_net} ly={b?.revenue_net} fmt={gbp} /></td></>}
    </tr>
  )
  const head = (withRevenue: boolean) => (
    <thead><tr><th></th><th className="ta-right">Services</th><th className="ta-right pullman-compare__ly">{lyLabel}</th><th className="ta-right">People</th><th className="ta-right pullman-compare__ly">{lyLabel}</th><th className="ta-right">Avg load</th><th className="ta-right pullman-compare__ly">{lyLabel}</th>{withRevenue && <><th className="ta-right">Net revenue</th><th className="ta-right pullman-compare__ly">{lyLabel}</th></>}</tr></thead>
  )
  const types = Array.from(new Set([...now.by_service_type.map((r) => r.service_type), ...ly.by_service_type.map((r) => r.service_type)]))
  const months = Array.from(new Set([...now.by_month.map((r) => r.month_label), ...ly.by_month.map((r) => r.month_label)]))
  return (
    <section className="pullman-compare grid-span-12">
      <h3 className="pullman-compare__title">This season next to last season</h3>
      <div className="pullman-compare__grid">
        <div className="pullman-compare__scroll pullman-compare__wide">
          <table className="pullman-compare__table">
            <caption>By day of week</caption>
            {head(true)}
            <tbody>{[1, 2, 3, 4, 5, 6, 7].map((d) => { const a = now.by_dow.find((r) => r.day_of_week_num === d); const b = ly.by_dow.find((r) => r.day_of_week_num === d); if (!a && !b) return null; return <Row key={d} label={(a ?? b)!.day_of_week} a={a} b={b} /> })}</tbody>
          </table>
        </div>
        <div className="pullman-compare__scroll">
          <table className="pullman-compare__table">
            <caption>By service type</caption>
            {head(false)}
            <tbody>{types.map((t) => <Row key={t} label={t} a={now.by_service_type.find((r) => r.service_type === t)} b={ly.by_service_type.find((r) => r.service_type === t)} />)}</tbody>
          </table>
        </div>
        <div className="pullman-compare__scroll">
          <table className="pullman-compare__table">
            <caption>By month</caption>
            {head(false)}
            <tbody>{months.map((m) => <Row key={m} label={m} a={now.by_month.find((r) => r.month_label === m)} b={ly.by_month.find((r) => r.month_label === m)} />)}</tbody>
          </table>
        </div>
      </div>
      <p className="pullman-compare__note">Last season = {windowLabel(ly.from ?? '', ly.to ?? '')}, {n0(ly.kpis.service_count)} services. Timetables differ year to year: compare the service counts before the totals.</p>
    </section>
  )
}

function Pullman({ tab = 'overview' }: { tab?: PullmanTab }) {
  // null/null on first render -> single fetch with no params -> API returns
  // the full data range. We then seed the picker to "next 30 days from
  // today" (clamped to the data range) so the page lands on what's
  // imminently coming up rather than the entire history.
  const [from, setFrom] = useState<string | null>(null)
  const [to,   setTo]   = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [alaCarteOpen, setAlaCarteOpen] = useState(false)
  // Compare to last season (2026-10-23): a second fetch of the same endpoint for the
  // matching window last season — one version of the truth, nothing recomputed here.
  const [compare, setCompare] = useState(false)
  const [align, setAlign] = useState<CompareAlign>('calendar')
  const windows = useMemo(() => (from && to ? lyWindow(from, to, align) : null), [from, to, align])

  const { data, loading, error } = usePullmanServices(from, to)
  const lyRes = usePullmanServices(windows?.ly_from ?? null, windows?.ly_to ?? null, compare && !!windows)
  const lyLabel = windows ? `Last season (${shortSeasonLabel(windows.ly_season)})` : 'Last season'

  useEffect(() => {
    if (!data) return
    if (from !== null || to !== null) return
    if (!data.data_range.earliest || !data.data_range.latest) return

    const earliest = data.data_range.earliest
    const latest   = data.data_range.latest
    const today    = new Date().toISOString().slice(0, 10)
    const dplus = (n: number): string => {
      const d = new Date(today + 'T00:00:00')
      d.setDate(d.getDate() + n)
      return d.toISOString().slice(0, 10)
    }

    setFrom(clampIso(today,     earliest, latest))
    setTo(  clampIso(dplus(30), earliest, latest))
  }, [data, from, to])

  const presets: DateRangePreset[] = useMemo(() => {
    if (!data?.data_range.earliest || !data?.data_range.latest) return []
    const earliest = data.data_range.earliest
    const latest   = data.data_range.latest
    const today    = new Date().toISOString().slice(0, 10)

    const dminus = (n: number): string => {
      const d = new Date(today + 'T00:00:00')
      d.setDate(d.getDate() - n)
      return d.toISOString().slice(0, 10)
    }
    const dplus = (n: number): string => {
      const d = new Date(today + 'T00:00:00')
      d.setDate(d.getDate() + n)
      return d.toISOString().slice(0, 10)
    }

    const monthStart = today.slice(0, 7) + '-01'
    const yearStart  = today.slice(0, 4) + '-01-01'

    return [
      { label: 'Next 30 days', from: clampIso(today,       earliest, latest),   to: clampIso(dplus(30),  earliest, latest)  },
      { label: 'Next 90 days', from: clampIso(today,       earliest, latest),   to: clampIso(dplus(90),  earliest, latest)  },
      { label: 'All data',     from: earliest,                                  to: latest                                  },
      { label: 'Last 30 days', from: clampIso(dminus(30),  earliest, latest),   to: clampIso(today, earliest, latest)       },
      { label: 'Last 90 days', from: clampIso(dminus(90),  earliest, latest),   to: clampIso(today, earliest, latest)       },
      { label: 'This month',   from: clampIso(monthStart,  earliest, latest),   to: clampIso(today, earliest, latest)       },
      { label: 'This year',    from: clampIso(yearStart,   earliest, latest),   to: clampIso(today, earliest, latest)       },
    ]
  }, [data])

  const triggerLabel = from && to
    ? `${formatPrettyDate(from)} \u2014 ${formatPrettyDate(to)}`
    : 'Pick a date range'

  return (
    <main className="app-main">
      <header className="pullman-head">
        <div>
          {data?.data_range.earliest && data?.data_range.latest && (
            <p className="pullman-head__sub">
              Data available {formatPrettyDate(data.data_range.earliest)} — {formatPrettyDate(data.data_range.latest)}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {(tab === 'overview' || tab === 'people') && <CompareToggle on={compare} onToggle={() => setCompare((v) => !v)} align={align} onAlign={setAlign} windows={compare ? windows : null} />}
          <button
            type="button"
            className="btn btn--reports-alacarte"
            onClick={() => setAlaCarteOpen(true)}
            disabled={!data}
          >
            Reports A La Carte
          </button>
          <button
            type="button"
            className="daterange-trigger"
            onClick={() => setPickerOpen(true)}
            disabled={!data}
          >
            <span className="daterange-trigger__icon" aria-hidden="true">📅</span>
            <span className="daterange-trigger__label">{triggerLabel}</span>
          </button>
        </div>
      </header>

      <PullmanSubNav />

      {tab === 'people' && <PullmanPeople from={from} to={to} compare={compare} align={align} />}
      {tab === 'capacity' && <PullmanCapacity />}
      {tab === 'bookings-taken' && <PullmanBookingsTaken />}
      {tab === 'live' && <PullmanLiveSales />}

      {tab === 'overview' && loading && !data && <p>Loading…</p>}
      {tab === 'overview' && error && <p style={{ color: '#a33' }}>Couldn&apos;t load: {error}</p>}
      {tab === 'overview' && compare && lyRes.error && <p style={{ color: '#a33' }}>Couldn&apos;t load last season: {lyRes.error}</p>}
      {tab === 'overview' && compare && windows && lyRes.data && lyRes.data.kpis.service_count === 0 && (
        <p className="pullman-compare__note">No Pullman services are held for {windowLabel(windows.ly_from, windows.ly_to)} — last season&apos;s data arrives with the widened Pullman refresh.</p>
      )}

      {tab === 'overview' && data && (
        <>
          <PullmanRevenueBar kpis={data.kpis} loading={loading} ly={compare ? lyRes.data?.kpis : null} lyLabel={lyLabel} />
          <PullmanKpiBar     kpis={data.kpis} loading={loading} ly={compare ? lyRes.data?.kpis : null} lyLabel={lyLabel} />
          <div className="dashboard-grid">
            {compare && lyRes.data && lyRes.data.kpis.service_count > 0 && <PullmanCompareTables now={data} ly={lyRes.data} lyLabel={lyLabel} />}
            <PullmanDailyOccupancyChart services={data.services}        kpis={data.kpis} className="grid-span-12" />
            <PullmanStackedSeatsChart   services={data.services}                         className="grid-span-12" />
            <PullmanRevenueByDowChart   rows={data.by_dow}               kpis={data.kpis} className="grid-span-6"  />
            <PullmanByDowChart          rows={data.by_dow}               kpis={data.kpis} className="grid-span-6"  />
            <PullmanByServiceTypeChart  rows={data.by_service_type}      kpis={data.kpis} className="grid-span-6"  />
            <TopPostcodesChart          from={from} to={to} initialMode="pullman" title="Top postcodes by bookings" className="grid-span-6" />
            <PullmanServicesTable       services={data.services}         kpis={data.kpis} className="grid-span-12" from={from} to={to} />
          </div>
          <p className="dashboard-footer">
            Each MERAC &lsquo;price band 10&rsquo; record = 1 coupe = 4 people; capacity per service comes from its seating plan (104 on the current plan). Data refreshes hourly 08:00&ndash;18:00 (and at 06:20) from <code>MERAC.AZEPOS</code> via the Sync Hub.
          </p>
        </>
      )}

      <ReportsALaCarteModal
        open={alaCarteOpen}
        onClose={() => setAlaCarteOpen(false)}
      />

      {pickerOpen && data && (
        <DateRangeModal
          initialFrom={from ?? data.data_range.earliest!}
          initialTo={to     ?? data.data_range.latest!}
          minDate={data.data_range.earliest ?? undefined}
          maxDate={data.data_range.latest ?? undefined}
          presets={presets}
          onCancel={() => setPickerOpen(false)}
          onApply={(range) => { setFrom(range.from); setTo(range.to); setPickerOpen(false) }}
        />
      )}
    </main>
  )
}

// ---------- My Dashboard component --------------------------------------
//
// Renders a user's pinned visuals. Empty state shows an Add Chart card;
// populated state shows the same chart components used on Dashboard One,
// just driven by the user's pin list. Picker modal handles add/remove.

// PinnedPullman: render-prop wrapper that fetches the Pullman envelope and
// hands a slice through to a pinned Pullman child. The Pullman dashboard
// itself fetches once at the parent level and distributes via props; on
// My Dashboard each pinned Pullman component runs its own fetch via this
// wrapper. Inefficient if many Pullmans are pinned (N fetches for the same
// envelope), but the envelope is small (< 100 KB even multi-season) so this
// is acceptable for v1. If it becomes a problem, lift the fetch to a
// MyDashboard-level cache keyed by (from, to).
function PinnedPullman({
  from,
  to,
  children,
}: {
  from?: string | null
  to?: string | null
  children: (data: PullmanResponse, loading: boolean) => ReactNode
}) {
  const { data, loading, error } = usePullmanServices(from ?? null, to ?? null)
  if (error) {
    return (
      <div className="placeholder" style={{ borderColor: '#a33' }}>
        <strong>Couldn’t load Pullman data</strong>
        <p style={{ fontSize: '0.85rem', margin: 0 }}>{error}</p>
      </div>
    )
  }
  if (!data) {
    return <div className="placeholder">Loading Pullman data…</div>
  }
  return <>{children(data, loading)}</>
}

// Map a component_key to its rendered JSX. The KPI bar manages its own mode
// pill internally; chart components take an optional bankHolidays + events
// pair so they can show pins and bands the same way Dashboard One does.
function renderPinnedComponent(
  pin: PinRow,
  season: string | null,
  bankHolidays: BankHoliday[],
  events: EventRow[],
  from?: string | null,
  to?: string | null,
): { node: ReactNode; spanClass: string } | null {
  const { component_key, locked_mode, locked_trend, locked_events } = pin
  switch (component_key) {
    case 'kpi-bar':
      return { node: <DashboardOneKpiBar season={season} from={from} to={to} initialMode={locked_mode} />, spanClass: 'grid-span-12' }
    case 'daily-revenue':
      return { node: <DailyRevenueChart        season={season} bankHolidays={bankHolidays} events={events} from={from} to={to} initialMode={locked_mode} initialTrend={locked_trend} initialEvents={locked_events} />, spanClass: 'grid-span-12' }
    case 'revenue-yoy':
      return { node: <RevenueYoyChart          season={season} bankHolidays={bankHolidays} events={events} from={from} to={to} initialMode={locked_mode} initialTrend={locked_trend} initialEvents={locked_events} />, spanClass: 'grid-span-12' }
    case 'revenue-by-dept':
      return { node: <RevenueByDepartmentChart season={season} from={from} to={to} initialMode={locked_mode} />, spanClass: 'grid-span-7' }
    case 'booking-source':
      return { node: <BookingSourceChart       season={season} from={from} to={to} initialMode={locked_mode} />, spanClass: 'grid-span-5' }
    case 'monthly-footfall':
      return { node: <MonthlyFootfallChart     season={season} bankHolidays={bankHolidays} events={events} from={from} to={to} initialTrend={locked_trend} initialEvents={locked_events} />, spanClass: 'grid-span-12' }
    case 'booking-lead-time':
      return { node: <BookingLeadTimeChart     season={season} from={from} to={to} initialMode={locked_mode} />, spanClass: 'grid-span-6' }
    case 'revenue-by-dow':
      return { node: <RevenueByDowChart        season={season} from={from} to={to} initialMode={locked_mode} />, spanClass: 'grid-span-6' }
    case 'top-postcodes':
      return { node: <TopPostcodesChart        season={season} from={from} to={to} initialMode="daily" showModeBar title="Top postcodes by bookings" />, spanClass: 'grid-span-6' }
    case 'services-breakdown':
      return { node: <ServicesBreakdownTable   season={season} from={from} to={to} initialMode="daily" />, spanClass: 'grid-span-12' }
    case 'capacity':
      return { node: <CapacityCard             season={season} from={from} to={to} />, spanClass: 'grid-span-12' }
    case 'ga4-cart-abandonment':
      return { node: <Ga4CartAbandonmentCard />, spanClass: 'grid-span-12' }
    case 'youtube-revenue':
      return { node: <YouTubeRevenueCard />, spanClass: 'grid-span-12' }

    // ---- Pullman ----
    // Pullman charts have no Mode/Trend/Events pills, so locked_* values
    // are ignored here. Each pinned Pullman runs its own envelope fetch
    // via the PinnedPullman wrapper above.
    case 'pullman-revenue-bar':
      return { node: <PinnedPullman from={from} to={to}>{(data, loading) => <PullmanRevenueBar kpis={data.kpis} loading={loading} />}</PinnedPullman>, spanClass: 'grid-span-12' }
    case 'pullman-kpi-bar':
      return { node: <PinnedPullman from={from} to={to}>{(data, loading) => <PullmanKpiBar     kpis={data.kpis} loading={loading} />}</PinnedPullman>, spanClass: 'grid-span-12' }
    case 'pullman-daily-occupancy':
      return { node: <PinnedPullman from={from} to={to}>{(data) => <PullmanDailyOccupancyChart services={data.services} kpis={data.kpis} />}</PinnedPullman>, spanClass: 'grid-span-12' }
    case 'pullman-stacked-seats':
      return { node: <PinnedPullman from={from} to={to}>{(data) => <PullmanStackedSeatsChart   services={data.services} />}</PinnedPullman>, spanClass: 'grid-span-12' }
    case 'pullman-by-dow':
      return { node: <PinnedPullman from={from} to={to}>{(data) => <PullmanByDowChart          rows={data.by_dow}           kpis={data.kpis} />}</PinnedPullman>, spanClass: 'grid-span-6' }
    case 'pullman-revenue-by-dow':
      return { node: <PinnedPullman from={from} to={to}>{(data) => <PullmanRevenueByDowChart   rows={data.by_dow}           kpis={data.kpis} />}</PinnedPullman>, spanClass: 'grid-span-6' }
    case 'pullman-by-service-type':
      return { node: <PinnedPullman from={from} to={to}>{(data) => <PullmanByServiceTypeChart  rows={data.by_service_type}  kpis={data.kpis} />}</PinnedPullman>, spanClass: 'grid-span-6' }
    case 'pullman-services-table':
      return { node: <PinnedPullman from={from} to={to}>{(data) => <PullmanServicesTable       services={data.services}     kpis={data.kpis} from={from ?? null} to={to ?? null} />}</PinnedPullman>, spanClass: 'grid-span-12' }

    default:
      return null
  }
}

function MyDashboard({
  pins,
  loading,
  error,
  saving,
  save,
}: {
  pins: PinRow[]
  loading: boolean
  error: string | null
  saving: boolean
  save: (input: string[] | PinSpec[]) => Promise<boolean>
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  // Date-range picker (separate from the component picker above).
  const [dateRangeOpen, setDateRangeOpen] = useState(false)
  const [from, setFrom] = useState<string | null>(null)
  const [to,   setTo]   = useState<string | null>(null)
  const { requests, busy: requestBusy, create: createRequest, patch: patchRequest } = useChartRequests(true)

  // Locally hide declined requests once user dismisses (no DB write -- spec
  // doesn't ask for a server-side dismissed_at and the next refresh will
  // bring them back if needed; that's acceptable for v1).
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set())

  const pinnedKeys = useMemo(() => new Set(pins.map((p) => p.component_key)), [pins])

  // Show on My Dashboard: pending, in_progress, fulfilled-but-not-pinned,
  // and declined-but-not-locally-dismissed. Hide fulfilled-and-pinned
  // because the component itself is now visible on the board.
  const visibleRequests = useMemo(() => requests.filter((r) => {
    if (dismissedIds.has(r.id)) return false
    if (r.status === 'fulfilled' && r.fulfilled_component_key && pinnedKeys.has(r.fulfilled_component_key)) return false
    return true
  }), [requests, dismissedIds, pinnedKeys])

  const onPinFulfilled = async (componentKey: string) => {
    if (pinnedKeys.has(componentKey)) return
    const next = [...pins.map((p) => p.component_key), componentKey]
    await save(next)
  }

  const onSubmitRequest = async (title: string, description: string | null, file: File | null) => {
    const result = await createRequest(title, description, file)
    if (result.ok) {
      setRequestOpen(false)
    }
    return result
  }

  // patchRequest comes from the hook but we don't need it on this side --
  // status updates happen in the admin UI. Keep the reference live so the
  // linter doesn't whinge.
  void patchRequest

  // Same season machinery as DashboardOne so the rendered components have a
  // season to filter by. Once we have multiple parent dashboards (Pullman),
  // each catalog entry's `dashboard` field will tell us which season
  // selector applies; for now Dashboard One's seasons cover all components.
  const { seasons, loading: seasonsLoading, error: seasonsError } = useSeasons()
  const [activeYear, setActiveYear] = useState<string | null>(null)
  useEffect(() => {
    if (activeYear || seasons.length === 0) return
    const cur = seasons.find((s) => s.is_current) ?? seasons[0]
    setActiveYear(cur.year_label)
  }, [seasons, activeYear])

  // Bank holidays + events shared across any time-series components on the
  // user's board.
  const seasonRange = useMemo(() => seasonDateRange(activeYear), [activeYear])
  const { rows: bankHolidays } = useBankHolidays(seasonRange?.from ?? null, seasonRange?.to ?? null)
  const { rows: events }       = useEventsForRange(seasonRange?.from ?? null, seasonRange?.to ?? null)

  // When the active season changes, snap the picker back to its full range
  // -- a narrow filter from the previous season should never carry across.
  useEffect(() => {
    if (seasonRange) {
      setFrom(seasonRange.from)
      setTo(seasonRange.to)
    }
  }, [seasonRange])

  const presets: DateRangePreset[] = useMemo(() => {
    if (!seasonRange) return []
    const today = new Date().toISOString().slice(0, 10)
    const dminus = (n: number): string => {
      const d = new Date(today + 'T00:00:00')
      d.setDate(d.getDate() - n)
      return d.toISOString().slice(0, 10)
    }
    const monthStart = today.slice(0, 7) + '-01'
    return [
      { label: 'Full season',  from: seasonRange.from,                                          to: seasonRange.to                                              },
      { label: 'Year to date', from: seasonRange.from,                                          to: clampIso(today,         seasonRange.from, seasonRange.to)   },
      { label: 'This month',   from: clampIso(monthStart,    seasonRange.from, seasonRange.to), to: clampIso(today,         seasonRange.from, seasonRange.to)   },
      { label: 'Last 30 days', from: clampIso(dminus(30),    seasonRange.from, seasonRange.to), to: clampIso(today,         seasonRange.from, seasonRange.to)   },
      { label: 'Last 90 days', from: clampIso(dminus(90),    seasonRange.from, seasonRange.to), to: clampIso(today,         seasonRange.from, seasonRange.to)   },
    ]
  }, [seasonRange])

  const isCustomRange = !!(
    seasonRange && from && to &&
    (from !== seasonRange.from || to !== seasonRange.to)
  )
  const triggerLabel = from && to
    ? `${formatPrettyDate(from)} \u2014 ${formatPrettyDate(to)}`
    : 'Pick a date range'

  if (loading) return <main className="app-main"><p>Loading your dashboard…</p></main>
  if (error) return (
    <main className="app-main">
      <p style={{ color: '#a33' }}>Couldn't load your dashboard: {error}</p>
    </main>
  )

  // Empty state: one big Add Chart card.
  if (pins.length === 0) {
    return (
      <main className="app-main">
        {visibleRequests.length > 0 && (
          <div className="dashboard-grid" style={{ marginBottom: '1.5rem' }}>
            {visibleRequests.map((r) => (
              <RequestBanner
                key={r.id}
                req={r}
                isPinned={r.fulfilled_component_key ? pinnedKeys.has(r.fulfilled_component_key) : false}
                onPin={onPinFulfilled}
                onDismiss={() => setDismissedIds((s) => new Set([...s, r.id]))}
              />
            ))}
          </div>
        )}
        <div className="my-dashboard-empty">
          <h2>Your dashboard is empty</h2>
          <p>Pin charts from the catalog to build a personal landing page. Your selection is saved per user, so it follows you wherever you sign in.</p>
          <div className="my-dashboard-empty__actions">
            <button type="button" className="btn-primary" onClick={() => setPickerOpen(true)}>
              + Add chart
            </button>
            <button type="button" className="btn-secondary" onClick={() => setRequestOpen(true)}>
              Request a custom chart
            </button>
          </div>
        </div>
        {pickerOpen && (
          <MyDashboardPicker
            currentPins={[]}
            saving={saving}
            onCancel={() => setPickerOpen(false)}
            onSave={async (nextPins) => {
              const ok = await save(nextPins)
              if (ok) setPickerOpen(false)
            }}
          />
        )}
        {requestOpen && (
          <ChartRequestModal
            busy={requestBusy}
            onCancel={() => setRequestOpen(false)}
            onSubmit={onSubmitRequest}
          />
        )}
      </main>
    )
  }

  // Populated state: SeasonBar + the rendered components in pinned order.
  return (
    <>
      <SeasonBar seasons={seasons} activeYear={activeYear} onChange={setActiveYear} />
      {seasonsLoading && <main className="app-main"><p>Loading seasons…</p></main>}
      {seasonsError && (
        <main className="app-main">
          <p style={{ color: '#a33' }}>Couldn't load seasons: {seasonsError}</p>
        </main>
      )}
      {!seasonsLoading && !seasonsError && (
        <main className="app-main">
          <header className="my-dashboard-bar">
            <h2 className="my-dashboard-bar__title">My Dashboard</h2>
            <div className="my-dashboard-bar__actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setRequestOpen(true)}
                disabled={saving || requestBusy}
              >
                Request a custom chart
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPickerOpen(true)}
                disabled={saving}
              >
                Edit components
              </button>
            </div>
          </header>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="daterange-trigger"
              onClick={() => setDateRangeOpen(true)}
              disabled={!from || !to}
            >
              <span className="daterange-trigger__icon" aria-hidden="true">📅</span>
              <span className="daterange-trigger__label">{triggerLabel}</span>
            </button>
            {isCustomRange && (
              <span style={{ fontSize: '0.78rem', color: '#6b6b6b', fontStyle: 'italic' }}>
                Filter active — every pinned chart is showing the selected range.
              </span>
            )}
          </div>

          <div className="dashboard-grid">
            {visibleRequests.map((r) => (
              <RequestBanner
                key={`req-${r.id}`}
                req={r}
                isPinned={r.fulfilled_component_key ? pinnedKeys.has(r.fulfilled_component_key) : false}
                onPin={onPinFulfilled}
                onDismiss={() => setDismissedIds((s) => new Set([...s, r.id]))}
              />
            ))}
            {pins.map((pin) => {
              const rendered = renderPinnedComponent(pin, activeYear, bankHolidays, events, from, to)
              if (!rendered) {
                return (
                  <div key={pin.component_key} className="placeholder grid-span-12" style={{ borderColor: '#a33' }}>
                    <strong>Unknown component '{pin.component_key}'</strong>
                    <p style={{ fontSize: '0.85rem' }}>This pin refers to a component that no longer exists in the catalog. Edit your dashboard to remove it.</p>
                  </div>
                )
              }
              return (
                <div key={pin.component_key} className={rendered.spanClass}>
                  {rendered.node}
                </div>
              )
            })}
          </div>

          <p className="dashboard-footer">
            Showing <code>{activeYear}</code>{isCustomRange ? `, narrowed to ${formatPrettyDate(from!)} \u2013 ${formatPrettyDate(to!)}` : ''}. Data refreshes daily at 06:00 from <code>OntrackDW.RPT.*</code>.
          </p>
        </main>
      )}
      {pickerOpen && (
        <MyDashboardPicker
          currentPins={pins.map((p) => ({
            component_key: p.component_key,
            locked_mode: p.locked_mode,
            locked_trend: p.locked_trend,
            locked_events: p.locked_events,
          }))}
          saving={saving}
          onCancel={() => setPickerOpen(false)}
          onSave={async (nextPins) => {
            const ok = await save(nextPins)
            if (ok) setPickerOpen(false)
          }}
        />
      )}
      {requestOpen && (
        <ChartRequestModal
          busy={requestBusy}
          onCancel={() => setRequestOpen(false)}
          onSubmit={onSubmitRequest}
        />
      )}
      {dateRangeOpen && seasonRange && from && to && (
        <DateRangeModal
          initialFrom={from}
          initialTo={to}
          minDate={seasonRange.from}
          maxDate={seasonRange.to}
          presets={presets}
          onCancel={() => setDateRangeOpen(false)}
          onApply={(range) => { setFrom(range.from); setTo(range.to); setDateRangeOpen(false) }}
        />
      )}
    </>
  )
}

// Request modal -- title, description, optional skill-file upload. Used by
// MyDashboard's "Request custom chart" button.
function ChartRequestModal({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean
  onCancel: () => void
  onSubmit: (title: string, description: string | null, file: File | null) => Promise<{ ok: true; row: ChartRequest } | { ok: false; error: string }>
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  // Close on Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const submit = async () => {
    setFormError(null)
    const t = title.trim()
    if (!t) { setFormError('A short title is required'); return }
    if (t.length > 200) { setFormError('Title must be 200 chars or fewer'); return }
    if (description.length > 4000) { setFormError('Description must be 4000 chars or fewer'); return }
    if (file && file.size > 5 * 1024 * 1024) {
      setFormError(`File is too big (${Math.round(file.size / 1024)} KB; max 5 MB)`)
      return
    }
    const result = await onSubmit(t, description.trim() === '' ? null : description, file)
    if (!result.ok) setFormError(result.error)
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel() }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="req-title">
        <header className="modal__head">
          <h3 id="req-title" style={{ margin: 0 }}>Request a custom chart</h3>
          <button type="button" className="modal__close" aria-label="Close" onClick={onCancel} disabled={busy}>×</button>
        </header>
        <div className="modal__body event-form">
          <p className="admin-subtle" style={{ margin: 0 }}>
            Tell us what you'd like to see. If you've been using Claude to generate reports or charts, ask Claude to save you a skill file and upload it here -- we'll use it as a spec when we build the component.
          </p>
          <label>
            <span>Title</span>
            <input
              type="text" value={title} maxLength={200} required autoFocus
              placeholder="e.g. Revenue by visiting locomotive"
              onChange={(e) => setTitle(e.target.value)} disabled={busy}
            />
          </label>
          <label>
            <span>What should the chart show? (optional)</span>
            <textarea
              value={description} maxLength={4000} rows={4}
              placeholder="Plain-English description -- audience, time period, breakdown, anything that helps us scope it."
              onChange={(e) => setDescription(e.target.value)} disabled={busy}
            />
          </label>
          <label>
            <span>Skill file (optional, up to 5 MB)</span>
            <input
              type="file"
              accept=".md,.markdown,.txt,.json,.yaml,.yml,.zip,text/markdown,text/plain,application/json,application/zip"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            {file && (
              <span className="admin-subtle" style={{ marginTop: '0.25rem' }}>
                Selected: <code>{file.name}</code> ({Math.round(file.size / 1024)} KB)
              </span>
            )}
          </label>
          {formError && <p className="event-form__error">{formError}</p>}
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Submitting\u2026' : 'Submit request'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Banner card showing one of the user's open chart requests on My Dashboard.
// Pending / in_progress: status + spinner. Fulfilled (and not yet pinned):
// shows an Add to dashboard button. Hidden once the user pins the matching
// component, or for declined requests (admin sees those, user doesn't).
function RequestBanner({
  req,
  isPinned,
  onPin,
  onDismiss,
}: {
  req: ChartRequest
  isPinned: boolean
  onPin: (componentKey: string) => void
  onDismiss: () => void
}) {
  return (
    <div className="request-banner grid-span-12" data-status={req.status}>
      <div className="request-banner__head">
        <span className={`request-status-pill request-status-pill--${req.status}`}>
          {req.status.replace('_', ' ')}
        </span>
        <strong className="request-banner__title">{req.title}</strong>
        {req.skill_file_blob_uri && (
          <span className="admin-subtle" title="Skill file uploaded">↗ file attached</span>
        )}
      </div>
      {req.description && (
        <p className="request-banner__desc">{req.description}</p>
      )}
      {req.admin_notes && (
        <p className="request-banner__notes"><strong>Note from admin:</strong> {req.admin_notes}</p>
      )}
      <div className="request-banner__foot">
        {req.status === 'pending' && (
          <em className="admin-subtle">Submitted -- we'll work on this and let you know when it's ready.</em>
        )}
        {req.status === 'in_progress' && (
          <em className="admin-subtle">In progress -- we're building this for you.</em>
        )}
        {req.status === 'fulfilled' && req.fulfilled_component_key && !isPinned && (
          <button type="button" className="btn-primary" onClick={() => onPin(req.fulfilled_component_key!)}>
            Add to my dashboard
          </button>
        )}
        {req.status === 'fulfilled' && isPinned && (
          <em className="admin-subtle">✓ Already on your dashboard.</em>
        )}
        {req.status === 'declined' && (
          <button type="button" className="btn-link" onClick={onDismiss}>Dismiss</button>
        )}
      </div>
    </div>
  )
}

// Picker modal: catalog grid on the left, ordered selection on the right.
// Per-pin filter locks live on the right-hand selected list -- each tile
// has a lock toggle (🔓/🔒) which expands an inline panel of dropdowns for the
// filters that component supports. Locks are stored in PinSpec shape and
// round-tripped through PUT /api/my-dashboard.
//
// Which filters each component supports. Mirrors the prop surface added in
// renderPinnedComponent. Keep this in sync if a chart gains/loses a pill.
const COMPONENT_FILTERS: Record<string, { mode: boolean; trend: boolean; events: boolean }> = {
  'kpi-bar':           { mode: true,  trend: false, events: false },
  'daily-revenue':     { mode: true,  trend: true,  events: true  },
  'revenue-yoy':       { mode: true,  trend: true,  events: true  },
  'revenue-by-dept':   { mode: true,  trend: false, events: false },
  'booking-source':    { mode: true,  trend: false, events: false },
  'monthly-footfall':  { mode: false, trend: true,  events: true  },
  'booking-lead-time': { mode: true,  trend: false, events: false },
  'revenue-by-dow':    { mode: true,  trend: false, events: false },
  // Pullman has no Mode/Trend/Events pills — locks do not apply.
  'pullman-revenue-bar':     { mode: false, trend: false, events: false },
  'pullman-kpi-bar':         { mode: false, trend: false, events: false },
  'pullman-daily-occupancy': { mode: false, trend: false, events: false },
  'pullman-stacked-seats':   { mode: false, trend: false, events: false },
  'pullman-by-dow':          { mode: false, trend: false, events: false },
  'pullman-by-service-type': { mode: false, trend: false, events: false },
  'pullman-services-table':  { mode: false, trend: false, events: false },
}
function getComponentFilters(key: string) {
  return COMPONENT_FILTERS[key] ?? { mode: false, trend: false, events: false }
}
function pinHasLocks(p: PinSpec): boolean {
  return p.locked_mode !== null || p.locked_trend !== null || p.locked_events !== null
}
function pinLockSummary(p: PinSpec): string {
  const parts: string[] = []
  if (p.locked_mode)            parts.push(`Mode: ${p.locked_mode}`)
  if (p.locked_trend  !== null) parts.push(`Trend: ${p.locked_trend  ? 'on' : 'off'}`)
  if (p.locked_events !== null) parts.push(`Events: ${p.locked_events ? 'on' : 'off'}`)
  return parts.length ? parts.join(' · ') : 'No locks set'
}

function MyDashboardPicker({
  currentPins,
  saving,
  onCancel,
  onSave,
}: {
  currentPins: PinSpec[]
  saving: boolean
  onCancel: () => void
  onSave: (pins: PinSpec[]) => void | Promise<void>
}) {
  const { catalog, loading, error } = useComponentsCatalog(true)
  const [selected, setSelected] = useState<PinSpec[]>(currentPins)
  // Which selected tile has its lock panel open. Single-open keeps the
  // modal short -- if the user has 8 pins all expanded the right column
  // would be a cliff.
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const selectedKeys = useMemo(() => selected.map((p) => p.component_key), [selected])

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [saving, onCancel])

  const toggle = (key: string) => {
    setSelected((prev) =>
      prev.some((p) => p.component_key === key)
        ? prev.filter((p) => p.component_key !== key)
        : [...prev, { component_key: key, locked_mode: null, locked_trend: null, locked_events: null }]
    )
    // If we just removed an expanded pin, collapse.
    if (expandedKey === key) setExpandedKey(null)
  }

  const move = (key: string, delta: -1 | 1) => {
    setSelected((prev) => {
      const idx = prev.findIndex((p) => p.component_key === key)
      if (idx < 0) return prev
      const newIdx = idx + delta
      if (newIdx < 0 || newIdx >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[newIdx]] = [next[newIdx], next[idx]]
      return next
    })
  }

  const remove = (key: string) => {
    setSelected((prev) => prev.filter((p) => p.component_key !== key))
    if (expandedKey === key) setExpandedKey(null)
  }

  function setLockedMode(key: string, value: Mode | null) {
    setSelected((prev) => prev.map((p) => p.component_key === key ? { ...p, locked_mode: value } : p))
  }
  function setLockedTrend(key: string, value: boolean | null) {
    setSelected((prev) => prev.map((p) => p.component_key === key ? { ...p, locked_trend: value } : p))
  }
  function setLockedEvents(key: string, value: boolean | null) {
    setSelected((prev) => prev.map((p) => p.component_key === key ? { ...p, locked_events: value } : p))
  }
  function clearLocks(key: string) {
    setSelected((prev) =>
      prev.map((p) => p.component_key === key
        ? { ...p, locked_mode: null, locked_trend: null, locked_events: null }
        : p)
    )
  }

  const lookup = useMemo(() => {
    const m: Record<string, CatalogComponent> = {}
    for (const c of catalog) m[c.key] = c
    return m
  }, [catalog])

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onCancel() }}
    >
      <div className="modal modal--wide" role="dialog" aria-modal="true" aria-labelledby="picker-title">
        <header className="modal__head">
          <h3 id="picker-title" style={{ margin: 0 }}>Customise My Dashboard</h3>
          <button type="button" className="modal__close" aria-label="Close" onClick={onCancel} disabled={saving}>×</button>
        </header>
        <div className="modal__body picker">
          <section className="picker__catalog">
            <h4 className="picker__heading">Available components</h4>
            {loading && <p>Loading catalog…</p>}
            {error && <p style={{ color: '#a33' }}>Couldn't load: {error}</p>}
            {!loading && !error && (
              <div className="picker__grid">
                {catalog.map((c) => {
                  const isSelected = selectedKeys.includes(c.key)
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`picker__tile ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggle(c.key)}
                      disabled={saving}
                      aria-pressed={isSelected}
                      data-kind={c.kind}
                    >
                      <div className="picker__tile-head">
                        <span className="picker__tile-kind">{c.kind}</span>
                        {isSelected && <span className="picker__tile-tick">✓</span>}
                      </div>
                      <div className="picker__tile-name">{c.name}</div>
                      <div className="picker__tile-desc">{c.description}</div>
                    </button>
                  )
                })}
              </div>
            )}
          </section>
          <section className="picker__selected">
            <h4 className="picker__heading">On my Dashboard ({selected.length})</h4>
            {selected.length === 0 ? (
              <p className="picker__empty">Click a component on the left to add it here.</p>
            ) : (
              <ol className="picker__selected-list">
                {selected.map((pin, i) => {
                  const c = lookup[pin.component_key]
                  const filters = getComponentFilters(pin.component_key)
                  const supportsAnyLock = filters.mode || filters.trend || filters.events
                  const isExpanded = expandedKey === pin.component_key
                  const hasLocks = pinHasLocks(pin)
                  return (
                    <li key={pin.component_key} className={`picker__selected-item ${isExpanded ? 'expanded' : ''}`}>
                      <div className="picker__selected-row">
                        <div className="picker__selected-name">
                          {c?.name ?? pin.component_key}
                          {!c && (
                            <span className="picker__selected-warn" title="This component is no longer in the catalog">
                              (unknown)
                            </span>
                          )}
                          {hasLocks && !isExpanded && (
                            <span className="picker__lock-summary" title={pinLockSummary(pin)}>
                              {pinLockSummary(pin)}
                            </span>
                          )}
                        </div>
                        <div className="picker__selected-actions">
                          {supportsAnyLock && (
                            <button
                              type="button"
                              className={`btn-link picker__lock-toggle ${hasLocks ? 'has-locks' : ''}`}
                              onClick={() => setExpandedKey(isExpanded ? null : pin.component_key)}
                              disabled={saving}
                              title={isExpanded ? 'Hide locks' : (hasLocks ? pinLockSummary(pin) : 'Configure locks')}
                              aria-label={isExpanded ? 'Hide lock controls' : 'Show lock controls'}
                              aria-expanded={isExpanded}
                            >
                              {hasLocks ? '🔒' : '🔓'}
                            </button>
                          )}
                          <button type="button" className="btn-link" onClick={() => move(pin.component_key, -1)} disabled={saving || i === 0} aria-label="Move up">↑</button>
                          <button type="button" className="btn-link" onClick={() => move(pin.component_key, +1)} disabled={saving || i === selected.length - 1} aria-label="Move down">↓</button>
                          <button type="button" className="btn-link btn-link--danger" onClick={() => remove(pin.component_key)} disabled={saving} aria-label="Remove">×</button>
                        </div>
                      </div>
                      {isExpanded && supportsAnyLock && (
                        <div className="picker__lock-panel">
                          <p className="picker__lock-help">
                            Locked filters seed each time this chart loads on My Dashboard. Leave on “No lock” to use the chart’s default — you can still flip pills in-session.
                          </p>
                          {filters.mode && (
                            <label className="picker__lock-row">
                              <span className="picker__lock-label">Mode</span>
                              <select
                                value={pin.locked_mode ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value
                                  setLockedMode(pin.component_key, v === '' ? null : (v as Mode))
                                }}
                                disabled={saving}
                              >
                                <option value="">No lock</option>
                                <option value="all">All</option>
                                <option value="daily">Daily</option>
                                <option value="pullman">Pullman</option>
                                <option value="event">Event</option>
                              </select>
                            </label>
                          )}
                          {filters.trend && (
                            <label className="picker__lock-row">
                              <span className="picker__lock-label">Trend</span>
                              <select
                                value={pin.locked_trend === null ? '' : pin.locked_trend ? 'on' : 'off'}
                                onChange={(e) => {
                                  const v = e.target.value
                                  setLockedTrend(pin.component_key, v === '' ? null : v === 'on')
                                }}
                                disabled={saving}
                              >
                                <option value="">No lock</option>
                                <option value="off">Off</option>
                                <option value="on">On</option>
                              </select>
                            </label>
                          )}
                          {filters.events && (
                            <label className="picker__lock-row">
                              <span className="picker__lock-label">Events</span>
                              <select
                                value={pin.locked_events === null ? '' : pin.locked_events ? 'on' : 'off'}
                                onChange={(e) => {
                                  const v = e.target.value
                                  setLockedEvents(pin.component_key, v === '' ? null : v === 'on')
                                }}
                                disabled={saving}
                              >
                                <option value="">No lock</option>
                                <option value="off">Off</option>
                                <option value="on">On</option>
                              </select>
                            </label>
                          )}
                          {hasLocks && (
                            <button
                              type="button"
                              className="btn-link picker__lock-clear"
                              onClick={() => clearLocks(pin.component_key)}
                              disabled={saving}
                            >
                              Clear all locks for this pin
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ol>
            )}
          </section>
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => onSave(selected)} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function TopNav({ me }: { me: MeResponse }) {
  // The pages and who may see them live in src/navPages.ts — one list for this
  // bar and the Page Selector. Admin sits apart at the right.
  const pillClass = (style: string) => ({ isActive }: { isActive: boolean }) =>
    `dashboard-pill${style === 'plain' ? '' : ' ' + style}${isActive ? ' active' : ''}`
  const pages = NAV_PAGES.filter((p) => p.visible(me))
  return (
    <nav className="dashboard-nav" aria-label="Dashboards">
      <div className="dashboard-nav__pills">
        {pages.filter((p) => p.style !== 'admin').map((p) => (
          <NavLink key={p.to} to={p.to} className={pillClass(p.style)}>{p.label}</NavLink>
        ))}
      </div>
      {pages.filter((p) => p.style === 'admin').map((p) => (
        <NavLink key={p.to} to={p.to} className={pillClass(p.style)}>{p.label}</NavLink>
      ))}
    </nav>
  )
}

// ===========================================================================
//  Budget tab — gated on SG-Reports-Budgets (me.isBudget).
//  Data: /api/budget/summary + /api/budget/vs-actual (api/src/functions/budget.ts).
//  Source: 'Income Budgets 2627.xlsx' (FY2026/27), loaded into budget.IncomeBudgetLine.
// ===========================================================================

type BudgetScope = 'merac' | 'full'

interface BudgetLine {
  entity: string
  coa_group: string
  nominal_code: string
  nominal_desc: string
  merac_scope: boolean
  mode: string
  source_note: string | null
  months: number[]
  total: number
}
interface BudgetGroup {
  entity: string
  coa_group: string
  months: number[]
  total: number
  merac_total: number
}
interface BudgetSummaryData {
  season: string
  month_labels: string[]
  totals: { all: number; plc: number; trust: number; merac: number; non_merac: number }
  month_totals: number[]
  by_group: BudgetGroup[]
  lines: BudgetLine[]
}
interface BudgetVsActualMonth {
  fm: number
  label: string
  year_month: number
  budget_full: number
  budget_merac: number
  budget_daily: number
  budget_event: number
  budget_pullman: number
  actual_all_net: number | null
  actual_daily_net: number | null
  actual_event_net: number | null
  actual_pullman_net: number | null
  actual_all_gross: number | null
}
interface BudgetVsActualData {
  season: string
  today: string
  today_year_month: number
  months: BudgetVsActualMonth[]
}

function useBudgetSummary(season: string | null) {
  const [data, setData] = useState<BudgetSummaryData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!season) return
    let cancelled = false
    setData(null); setLoading(true); setError(null)
    ;(async () => {
      try {
        const res = await fetch(`/api/budget/summary?season=${encodeURIComponent(season)}`)
        if (!res.ok) throw new Error(`/api/budget/summary returned ${res.status}`)
        const json = (await res.json()) as BudgetSummaryData
        if (!cancelled) setData(json)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season])
  return { data, loading, error }
}

function useBudgetVsActual(season: string | null) {
  const [data, setData] = useState<BudgetVsActualData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!season) return
    let cancelled = false
    setData(null); setLoading(true); setError(null)
    ;(async () => {
      try {
        const res = await fetch(`/api/budget/vs-actual?season=${encodeURIComponent(season)}`)
        if (!res.ok) throw new Error(`/api/budget/vs-actual returned ${res.status}`)
        const json = (await res.json()) as BudgetVsActualData
        if (!cancelled) setData(json)
      } catch (e) {
        if (!cancelled) setError(String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [season])
  return { data, loading, error }
}

const BUDGET_GREEN = '#0b3d2e'
const BUDGET_BLUE = '#2a6f9e'   // actual line / actual cells (contrasting colour)

// ---- Editable actual/forecast layer (budget.IncomeEntry) -------------------
interface BudgetStreamsData {
  season: string
  groups: Record<string, number[]>          // group -> months[12] MERAC earned (pre-fill)
  traffic_walkup?: number[]                  // months[12] walk-up (blank booking-type) within Traffic
  events_walkup?: Record<string, number[]>   // event label -> months[12] walk-up on that event's dates
}
interface BudgetReconcile {
  fiscal_month_no: number
  event_label: string
  target_group: string
  amount: number
  reconciled_by: string
  reconciled_at: string
}
interface BudgetEntry {
  coa_group: string
  fiscal_month_no: number
  entry_type: 'actual' | 'forecast'
  value: number
  entered_by: string
  entered_at: string
  note: string | null
}

function useBudgetStreams(season: string | null) {
  const [data, setData] = useState<BudgetStreamsData | null>(null)
  useEffect(() => {
    if (!season) return
    let cancelled = false
    setData(null)
    ;(async () => {
      try {
        const res = await fetch(`/api/budget/streams?season=${encodeURIComponent(season)}`)
        if (res.ok && !cancelled) setData((await res.json()) as BudgetStreamsData)
      } catch { /* pre-fill is best-effort */ }
    })()
    return () => { cancelled = true }
  }, [season])
  return data
}

function useBudgetEntries(season: string | null) {
  const [entries, setEntries] = useState<BudgetEntry[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (!season) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/budget/entries?season=${encodeURIComponent(season)}`)
        if (res.ok && !cancelled) setEntries(((await res.json()) as { entries: BudgetEntry[] }).entries ?? [])
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [season, reloadKey])

  const save = async (coa_group: string, fiscal_month_no: number, entry_type: 'actual' | 'forecast', value: number) => {
    if (!season) return
    setSaving(true)
    try {
      await fetch('/api/budget/entry', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season, coa_group, fiscal_month_no, entry_type, value }),
      })
      setReloadKey((k) => k + 1)
    } finally { setSaving(false) }
  }
  const remove = async (coa_group: string, fiscal_month_no: number, entry_type: 'actual' | 'forecast') => {
    if (!season) return
    setSaving(true)
    try {
      const qs = new URLSearchParams({ season, coa_group, fiscal_month_no: String(fiscal_month_no), entry_type })
      await fetch(`/api/budget/entry?${qs.toString()}`, { method: 'DELETE' })
      setReloadKey((k) => k + 1)
    } finally { setSaving(false) }
  }
  return { entries, save, remove, saving }
}

function useBudgetReconciles(season: string | null) {
  const [reconciles, setReconciles] = useState<BudgetReconcile[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  useEffect(() => {
    if (!season) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/budget/reconciles?season=${encodeURIComponent(season)}`)
        if (res.ok && !cancelled) setReconciles(((await res.json()) as { reconciles: BudgetReconcile[] }).reconciles ?? [])
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [season, reloadKey])
  const reconcile = async (fiscal_month_no: number, event_label: string, amount: number, target_group = 'Traffic Special Events') => {
    if (!season) return
    await fetch('/api/budget/reconcile', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ season, fiscal_month_no, event_label, amount, target_group }),
    })
    setReloadKey((k) => k + 1)
  }
  const unreconcile = async (fiscal_month_no: number, event_label: string) => {
    if (!season) return
    const qs = new URLSearchParams({ season, fiscal_month_no: String(fiscal_month_no), event_label })
    await fetch(`/api/budget/reconcile?${qs.toString()}`, { method: 'DELETE' })
    setReloadKey((k) => k + 1)
  }
  return { reconciles, reconcile, unreconcile }
}

// iplicit actual income by fiscal month — per COA group (full group total) and
// per budgeted nominal — from cache.PnL (the nightly iplicit P&L pull). PLC only.
interface IplicitActualData {
  season: string
  groups: Record<string, { months: number[]; by_nominal: Record<string, number[]> }>
}
function useBudgetIplicitActual(season: string | null) {
  const [data, setData] = useState<IplicitActualData | null>(null)
  useEffect(() => {
    if (!season) { setData(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/api/budget/iplicit-actual?season=${encodeURIComponent(season)}`)
        if (res.ok && !cancelled) setData((await res.json()) as IplicitActualData)
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [season])
  return data
}

// Per-stream model combining budget (baseline), MERAC pre-fill, and manual
// actual/forecast entries into effective monthly series + manual flags + audit.
interface StreamModel {
  entity: string
  coa_group: string
  budget: number[]
  actual: (number | null)[]
  forecast: number[]
  actualManual: boolean[]
  forecastManual: boolean[]
  audit: Record<string, string>
  budgetTotal: number
  lines: BudgetLine[]      // nominal-line detail (read-only budget breakdown for the expander)
}

function buildStreamModels(summary: BudgetSummaryData | null, streams: BudgetStreamsData | null, entries: BudgetEntry[], reconciles: BudgetReconcile[] = []): StreamModel[] {
  if (!summary) return []
  const prefill = streams?.groups ?? {}
  const byKey = new Map<string, BudgetEntry>()
  for (const e of entries) byKey.set(`${e.coa_group}|${e.fiscal_month_no}|${e.entry_type}`, e)
  // Reconciliations MOVE walk-up out of Traffic into the target event group, per month.
  const recByMonth = Array(12).fill(0)         // total moved out of Traffic each month
  const recToGroup: Record<string, number[]> = {}
  for (const r of reconciles) {
    const i = r.fiscal_month_no - 1
    if (i < 0 || i > 11) continue
    recByMonth[i] += r.amount
    ;(recToGroup[r.target_group] ??= Array(12).fill(0))[i] += r.amount
  }
  return summary.by_group.map((g) => {
    const base = prefill[g.coa_group] ?? Array(12).fill(0)
    // Apply reconcile: Traffic loses the moved walk-up; the target event group gains it.
    const pf = base.map((v, i) => {
      let adj = v
      if (g.coa_group === 'Traffic') adj -= recByMonth[i]
      adj += (recToGroup[g.coa_group]?.[i] ?? 0)
      return adj
    })
    const actual: (number | null)[] = []
    const forecast: number[] = []
    const actualManual: boolean[] = []
    const forecastManual: boolean[] = []
    const audit: Record<string, string> = {}
    for (let m = 0; m < 12; m++) {
      const fm = m + 1
      const ea = byKey.get(`${g.coa_group}|${fm}|actual`)
      const ef = byKey.get(`${g.coa_group}|${fm}|forecast`)
      if (ea) { actual.push(ea.value); actualManual.push(true); audit[`actual:${m}`] = `${ea.entered_by} · ${ea.entered_at.slice(0, 10)}` }
      else { const v = pf[m] ?? 0; actual.push(v !== 0 ? v : null); actualManual.push(false) }
      if (ef) { forecast.push(ef.value); forecastManual.push(true); audit[`forecast:${m}`] = `${ef.entered_by} · ${ef.entered_at.slice(0, 10)}` }
      else { forecast.push(g.months[m]); forecastManual.push(false) }
    }
    const lines = summary.lines.filter((l) => l.entity === g.entity && l.coa_group === g.coa_group)
    return { entity: g.entity, coa_group: g.coa_group, budget: g.months, actual, forecast, actualManual, forecastManual, audit, budgetTotal: g.total, lines }
  })
}

function budgetForMonth(m: BudgetVsActualMonth, scope: BudgetScope, mode: Mode): number {
  if (scope === 'full') return m.budget_full
  switch (mode) {
    case 'daily':   return m.budget_daily
    case 'event':   return m.budget_event
    case 'pullman': return m.budget_pullman
    default:        return m.budget_merac
  }
}
const fmtCellNum = (v: number) => (v === 0 ? '—' : num0.format(Math.round(v)))
const pct1 = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`

function BudgetScopeToggle({ scope, onChange }: { scope: BudgetScope; onChange: (s: BudgetScope) => void }) {
  return (
    <div className="budget-scope-wrap">
      <div className="budget-scope-toggle" role="group" aria-label="Budget comparison scope">
        <button type="button" className={`scope-pill ${scope === 'merac' ? 'active' : ''}`} onClick={() => onChange('merac')}
                title="Like-for-like: compares only income that flows through MERAC tills & bookings — tickets, catering, retail, dining, events, Gift Aid, booking fees (about £8.2M) — against actual MERAC sales. The fair 'are we on plan?' view.">Like-for-like</button>
        <button type="button" className={`scope-pill ${scope === 'full' ? 'active' : ''}`} onClick={() => onChange('full')}
                title="Full budget: the entire £9.59M income plan, including Trust membership, bequests, grants & donations that never pass through MERAC — so actuals always look behind on this view.">Full budget</button>
      </div>
      <InfoPopover>
        <h4>Like-for-like vs Full budget</h4>
        <p><strong>Like-for-like</strong> compares only the income that actually passes through MERAC tills and bookings — tickets, catering, retail, dining, events, Gift Aid and booking fees (≈£8.2M). It's the fair “are we hitting plan?” view, because the actuals can only ever contain MERAC sales.</p>
        <p><strong>Full budget</strong> shows the entire £9.59M plan, adding income that never appears in MERAC — Trust membership, bequests, grants and donations (≈£1.4M). Good for the whole picture, but actuals will always look behind because they can't include this income.</p>
      </InfoPopover>
    </div>
  )
}

function BudgetKpiBar({ summary, vsActual }: { summary: BudgetSummaryData | null; vsActual: BudgetVsActualData | null }) {
  const actualToDate = useMemo(
    () => (vsActual ? vsActual.months.reduce((s, m) => s + (m.actual_all_net ?? 0), 0) : null),
    [vsActual]
  )
  return (
    <section className="kpi-section">
      <header className="kpi-section__head">
        <span className="kpi-section__label">Income budget at a glance</span>
      </header>
      <div className="kpi-bar">
        <KpiCard label="Total income budget"    value={summary ? gbpCompact(summary.totals.all)   : '—'} loading={!summary} />
        <KpiCard label="PLC (trading)"          value={summary ? gbpCompact(summary.totals.plc)   : '—'} loading={!summary} />
        <KpiCard label="Trust (charity)"        value={summary ? gbpCompact(summary.totals.trust) : '—'} loading={!summary} />
        <KpiCard label="Like-for-like target"   value={summary ? gbpCompact(summary.totals.merac) : '—'} loading={!summary} />
        <KpiCard label="Actual booked to date"  value={actualToDate !== null ? gbpCompact(actualToDate) : '—'} loading={!vsActual} />
      </div>
    </section>
  )
}

function EditCell({ value, manual, prefill, editable, baselineTitle, audit, onSave, onClear }: {
  value: number | null
  manual: boolean
  prefill?: boolean
  editable: boolean
  baselineTitle?: string
  audit?: string
  onSave: (v: number) => void
  onClear?: () => void
}) {
  const [editing, setEditing] = useState(false)
  if (editing) {
    return (
      <td className="bt-cell editing">
        <input
          type="number" autoFocus defaultValue={value ?? ''}
          onBlur={(e) => { const t = e.target.value.trim(); setEditing(false); if (t !== '' && Number.isFinite(Number(t))) onSave(Number(t)) }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); else if (e.key === 'Escape') setEditing(false) }}
        />
      </td>
    )
  }
  const cls = ['bt-cell',
    value != null && value < 0 ? 'neg' : '',
    manual ? 'manual' : (prefill && value != null ? 'prefill' : ''),
    editable ? 'editable' : ''].filter(Boolean).join(' ')
  const title = manual && audit ? `Manual entry — ${audit}${baselineTitle ? ` · ${baselineTitle}` : ''}` : baselineTitle
  return (
    <td className={cls} title={title} onClick={() => editable && setEditing(true)}>
      <span>{value == null ? '—' : fmtCellNum(value)}</span>
      {manual && onClear && <button type="button" className="bt-clear" title="Revert to budget / pre-fill" onClick={(e) => { e.stopPropagation(); onClear() }}>×</button>}
    </td>
  )
}

function WalkupReconcileCell({ defaultValue, reconciled, onReconcile, onUnreconcile }: {
  defaultValue: number
  reconciled: number | null
  onReconcile: (amount: number) => void
  onUnreconcile: () => void
}) {
  const [draft, setDraft] = useState(String(Math.round(defaultValue)))
  if (reconciled != null) {
    return (
      <div className="bt-recon reconciled">
        <span className="bt-recon-val">{fmtCellNum(reconciled)}</span>
        <button type="button" className="bt-recon-btn done" title="Reconciled to Traffic Special Events — click to move it back to daily" onClick={onUnreconcile}>✓ moved</button>
      </div>
    )
  }
  return (
    <div className="bt-recon">
      <input type="number" className="bt-recon-input" value={draft} onChange={(e) => setDraft(e.target.value)} />
      <button type="button" className="bt-recon-btn" title="Subtract this from MERAC Walk-up Sales for the month and book it as event income (Traffic Special Events)"
              onClick={() => { const v = Number(draft); if (Number.isFinite(v)) onReconcile(v) }}>Reconcile</button>
    </div>
  )
}

// Excel export of the income-budget table — per group: Budget, iplicit actual,
// Differential (over posted months) and MERAC actuals, by month + total.
type BtXlsxCell = { value: string | number | null; type?: typeof String | typeof Number; fontWeight?: 'bold'; format?: string; align?: 'left' | 'right' }
async function exportBudgetExcel(models: StreamModel[], monthLabels: string[], iplicitActual: IplicitActualData | null, season: string | null) {
  const { default: writeXlsxFile } = await import('write-excel-file/browser')
  const FMT = '£#,##0'
  const num = (v: number): BtXlsxCell => ({ value: Math.round(v), type: Number, format: FMT })
  const gap = (): BtXlsxCell => ({ value: null })
  const head: BtXlsxCell[] = [
    { value: 'Entity', type: String, fontWeight: 'bold' },
    { value: 'Income stream', type: String, fontWeight: 'bold' },
    { value: 'Line', type: String, fontWeight: 'bold' },
    ...monthLabels.map((l) => ({ value: l, type: String, fontWeight: 'bold', align: 'right' } as BtXlsxCell)),
    { value: 'Total', type: String, fontWeight: 'bold', align: 'right' },
  ]
  const rows: BtXlsxCell[][] = [head]
  for (const ent of [{ key: 'PLC', label: 'PLC — trading' }, { key: 'Trust', label: 'Trust — charity' }]) {
    for (const m of models.filter((x) => x.entity === ent.key)) {
      const ia = iplicitActual?.groups[m.coa_group]
      const line = (label: string, cells: BtXlsxCell[], total: number): BtXlsxCell[] =>
        [{ value: ent.label, type: String }, { value: m.coa_group, type: String }, { value: label, type: String }, ...cells, num(total)]
      rows.push(line('Budget', m.forecast.map(num), m.forecast.reduce((s, v) => s + v, 0)))
      if (ia) {
        rows.push(line('iplicit actual', ia.months.map((v) => (v === 0 ? gap() : num(v))), ia.months.reduce((s, v) => s + v, 0)))
        let pb = 0, pa = 0
        const diffCells = ia.months.map((v, i) => { if (v === 0) return gap(); pb += m.budget[i]; pa += v; return num(v - m.budget[i]) })
        rows.push(line('Differential vs budget', diffCells, pa - pb))
      }
      rows.push(line('MERAC actuals', m.actual.map((v) => (v == null ? gap() : num(v))), m.actual.reduce((s: number, v) => s + (v ?? 0), 0)))
    }
  }
  const blob = await writeXlsxFile([{ data: rows, sheet: 'Income budget' }] as never).toBlob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `income-budget-vs-actual-${(season ?? 'season').replace('/', '-')}.xlsx`
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
}

function BudgetTable({ models, monthLabels, currentFm, showActuals, onSave, onRemove, trafficWalkup, eventsWalkup, reconciles, onReconcile, onUnreconcile, iplicitActual, season }: {
  models: StreamModel[]
  monthLabels: string[]
  currentFm: number
  showActuals: boolean
  onSave: (group: string, fm: number, type: 'actual' | 'forecast', value: number) => void
  onRemove: (group: string, fm: number, type: 'actual' | 'forecast') => void
  trafficWalkup: number[]
  eventsWalkup: Record<string, number[]>
  reconciles: BudgetReconcile[]
  onReconcile: (fm: number, event_label: string, amount: number, target_group?: string) => void
  onUnreconcile: (fm: number, event_label: string) => void
  iplicitActual: IplicitActualData | null
  season: string | null
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExp = (k: string) => setExpanded((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n })
  const recByMonth = Array(12).fill(0)
  const recMap = new Map<string, number>()
  for (const r of reconciles) {
    if (r.fiscal_month_no >= 1 && r.fiscal_month_no <= 12) { recByMonth[r.fiscal_month_no - 1] += r.amount; recMap.set(`${r.fiscal_month_no}|${r.event_label}`, r.amount) }
  }
  const eventLabels = Object.keys(eventsWalkup).filter((k) => (eventsWalkup[k] ?? []).some((v) => v > 0))
  const walkupRemainTotal = trafficWalkup.reduce((s, v) => s + v, 0) - recByMonth.reduce((s, v) => s + v, 0)
  const entities = [
    { key: 'PLC', label: 'PLC — trading income' },
    { key: 'Trust', label: 'Trust — charity income' },
  ]
  const sumTo = (a: (number | null)[], n: number) => a.slice(0, n).reduce((s: number, v) => s + (v ?? 0), 0)
  const grandF = Array(12).fill(0)
  models.forEach((m) => m.forecast.forEach((v, i) => { grandF[i] += v }))
  const grandTotal = grandF.reduce((s, v) => s + v, 0)

  return (
    <section className="chart-card grid-span-12">
      <header className="chart-card__head">
        <h3 className="chart-card__title">Income budget by month {showActuals ? '— budget / actual' : ''}</h3>
        <button type="button" className="btn-secondary bt-export-btn" onClick={() => exportBudgetExcel(models, monthLabels, iplicitActual, season)} title="Download this table as an Excel workbook">⤓ Excel</button>
        <span className="budget-table-note">
          All figures £ · click a cell to type a value · ▸ expand a group for its nominal lines · <span className="bt-swatch manual" /> manual override
          {showActuals && <> · <strong>IPLICIT ACTUAL</strong> = iplicit finance actual (nightly, all nominals) · <strong>MERAC actuals</strong> = till pre-fill · <strong>Differential vs budget</strong> <span className="bt-diff-key pos">+green up</span>/<span className="bt-diff-key neg">−red down</span></>}
        </span>
      </header>
      <div className="chart-card__body">
        <div className="budget-table-wrap">
          <table className="budget-table">
            <thead>
              <tr>
                <th className="bt-rowhead">Income stream</th>
                {monthLabels.map((l, i) => <th key={l} className={i + 1 === currentFm ? 'bt-now' : ''}>{l}</th>)}
                <th className="bt-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {entities.map((ent) => {
                const groups = models.filter((m) => m.entity === ent.key)
                if (groups.length === 0) return null
                const entF = Array(12).fill(0)
                groups.forEach((m) => m.forecast.forEach((v, i) => { entF[i] += v }))
                const entTotal = entF.reduce((s, v) => s + v, 0)
                return (
                  <Fragment key={ent.key}>
                    <tr className="bt-section"><td colSpan={14}>{ent.label}</td></tr>
                    {groups.map((m) => {
                      const aToDate = sumTo(m.actual, currentFm)
                      const bToDate = m.budget.slice(0, currentFm).reduce((s, v) => s + v, 0)
                      const varPct = bToDate > 0 ? (aToDate / bToDate - 1) * 100 : null
                      const fcTotal = m.forecast.reduce((s, v) => s + v, 0)
                      const acTotal = m.actual.reduce((s: number, v) => s + (v ?? 0), 0)
                      // iplicit actual for this group (PLC only; from cache.PnL).
                      const ia = iplicitActual?.groups[m.coa_group]
                      const iaMonths = ia?.months ?? []
                      const iaTotal = iaMonths.reduce((s, v) => s + v, 0)
                      // Compare actual vs budget only over ELAPSED months that actually
                      // have an iplicit figure posted. iplicit posts some lines (e.g. Gift
                      // Aid) lumpily — a month with nothing posted must NOT read as -100%,
                      // it just isn't comparable yet.
                      let postedBudget = 0, postedActual = 0
                      for (let i = 0; i < currentFm && i < 12; i++) {
                        if (iaMonths[i] !== 0) { postedBudget += m.budget[i]; postedActual += iaMonths[i] }
                      }
                      const diffToDate = postedActual - postedBudget
                      const iaGroupPct = postedBudget !== 0 ? (postedActual / postedBudget - 1) * 100 : null
                      return (
                        <Fragment key={m.coa_group}>
                          <tr className={`bt-group ${expanded.has(m.coa_group) ? 'open' : ''}`}>
                            <td className="bt-rowhead">
                              <span className="bt-caret" style={{ cursor: 'pointer' }} title={expanded.has(m.coa_group) ? 'Hide nominal lines' : 'Show nominal lines'} onClick={() => toggleExp(m.coa_group)}>{expanded.has(m.coa_group) ? '▾' : '▸'}</span>
                              {m.coa_group}
                              {showActuals && ia && iaGroupPct != null && (
                                <span className={`bt-var ${iaGroupPct >= 0 ? 'pos' : 'neg'}`} title="iplicit actual vs budget, to date (only months posted in iplicit)">{pct1(iaGroupPct)}</span>
                              )}
                              {showActuals && !ia && varPct != null && (
                                <span className={`bt-var ${varPct >= 0 ? 'pos' : 'neg'}`} title="MERAC actual vs budget, to date">{pct1(varPct)}</span>
                              )}
                            </td>
                            {m.forecast.map((v, i) => (
                              <EditCell key={i} value={v} manual={m.forecastManual[i]} editable
                                baselineTitle={`Budget £${num0.format(Math.round(m.budget[i]))}`}
                                audit={m.audit[`forecast:${i}`]}
                                onSave={(nv) => onSave(m.coa_group, i + 1, 'forecast', nv)}
                                onClear={() => onRemove(m.coa_group, i + 1, 'forecast')} />
                            ))}
                            <td className="bt-total">{fmtCellNum(fcTotal)}</td>
                          </tr>
                          {showActuals && ia && (
                            <tr className="bt-iplicit-row">
                              <td className="bt-rowhead bt-iplicit-head">
                                IPLICIT ACTUAL
                                <InfoPopover>
                                  <h4>iplicit actual</h4>
                                  <p>Actual income for this group from <strong>iplicit</strong> (the finance system), by month — the sum of <strong>every</strong> iplicit income nominal mapped to this group, so it matches the P&amp;L group total. Includes nominals that aren&apos;t in the budget. Refreshed nightly from the iplicit P&amp;L.</p>
                                </InfoPopover>
                              </td>
                              {ia.months.map((v, i) => (
                                <td key={i} className={`bt-cell bt-iplicit ${v < 0 ? 'neg' : ''}`}>{fmtCellNum(v)}</td>
                              ))}
                              <td className="bt-total">{fmtCellNum(iaTotal)}</td>
                            </tr>
                          )}
                          {showActuals && ia && (
                            <tr className="bt-diff-row">
                              <td className="bt-rowhead bt-diff-head" title="iplicit actual less budget, for months reached to date">Differential vs budget</td>
                              {ia.months.map((v, i) => {
                                // Only elapsed months with an iplicit figure posted are comparable;
                                // a month with nothing posted is '—', not -100%.
                                if (i + 1 > currentFm || v === 0) return <td key={i} className="bt-cell bt-diff-empty">—</td>
                                const b = m.budget[i]
                                const diff = v - b
                                const pct = b !== 0 ? (diff / Math.abs(b)) * 100 : null
                                const up = diff >= 0
                                return (
                                  <td key={i} className={`bt-cell bt-diff ${up ? 'pos' : 'neg'}`}
                                      title={`iplicit actual £${num0.format(Math.round(v))} vs budget £${num0.format(Math.round(b))} — differential £${diff >= 0 ? '+' : '−'}${num0.format(Math.abs(Math.round(diff)))}`}>
                                    <span className="bt-diff-val">{diff >= 0 ? '+' : '−'}{num0.format(Math.abs(Math.round(diff)))}</span>
                                    {pct != null && <span className="bt-diff-pct">{pct >= 0 ? '+' : ''}{pct.toFixed(0)}%</span>}
                                  </td>
                                )
                              })}
                              <td className={`bt-total bt-diff ${diffToDate >= 0 ? 'pos' : 'neg'}`}
                                  title="iplicit actual less budget, over months posted in iplicit to date">
                                <span className="bt-diff-val">{diffToDate >= 0 ? '+' : '−'}{num0.format(Math.abs(Math.round(diffToDate)))}</span>
                                {iaGroupPct != null && <span className="bt-diff-pct">{iaGroupPct >= 0 ? '+' : ''}{iaGroupPct.toFixed(0)}%</span>}
                              </td>
                            </tr>
                          )}
                          {showActuals && (
                            <tr className="bt-actual-row">
                              <td className="bt-rowhead bt-actual-head" title="MERAC / AZEPOS till actuals (pre-filled), kept separate from the iplicit finance actual above">MERAC actuals</td>
                              {m.actual.map((v, i) => (
                                <EditCell key={i} value={v} manual={m.actualManual[i]} prefill editable
                                  baselineTitle={m.actualManual[i] ? undefined : (v != null ? 'MERAC pre-fill' : 'no actual yet')}
                                  audit={m.audit[`actual:${i}`]}
                                  onSave={(nv) => onSave(m.coa_group, i + 1, 'actual', nv)}
                                  onClear={() => onRemove(m.coa_group, i + 1, 'actual')} />
                              ))}
                              <td className="bt-total">{fmtCellNum(acTotal)}</td>
                            </tr>
                          )}
                          {expanded.has(m.coa_group) && m.lines.map((l) => {
                            const iaLine = ia?.by_nominal[l.nominal_code]
                            return (
                              <Fragment key={l.nominal_code}>
                                <tr className="bt-line">
                                  <td className="bt-rowhead">
                                    <span className="bt-line-code">{l.nominal_code}</span>{l.nominal_desc}
                                    {!l.merac_scope && <span className="merac-dot" title="Outside MERAC sales" />}
                                    {l.source_note && <span className="bt-flag" title={l.source_note}>⚠</span>}
                                  </td>
                                  {l.months.map((v, i) => <td key={i} className={v < 0 ? 'neg' : ''}>{fmtCellNum(v)}</td>)}
                                  <td className="bt-total">{fmtCellNum(l.total)}</td>
                                </tr>
                                {showActuals && iaLine && (
                                  <tr className="bt-line bt-line-iplicit">
                                    <td className="bt-rowhead bt-line-iplicit-head">iplicit actual</td>
                                    {iaLine.map((v, i) => <td key={i} className={`bt-iplicit ${v < 0 ? 'neg' : ''}`}>{fmtCellNum(v)}</td>)}
                                    <td className="bt-total">{fmtCellNum(iaLine.reduce((s, v) => s + v, 0))}</td>
                                  </tr>
                                )}
                              </Fragment>
                            )
                          })}
                          {m.coa_group === 'Traffic' && showActuals && (
                            <>
                              <tr className="bt-walkup-row">
                                <td className="bt-rowhead bt-walkup-head">— of which MERAC Walk-up Sales</td>
                                {Array.from({ length: 12 }).map((_, i) => (
                                  <td key={i} className="bt-cell prefill">{fmtCellNum((trafficWalkup[i] ?? 0) - recByMonth[i])}</td>
                                ))}
                                <td className="bt-total">{fmtCellNum(walkupRemainTotal)}</td>
                              </tr>
                              {eventLabels.map((label) => (
                                <tr key={label} className="bt-walkup-row bt-memo">
                                  <td className="bt-rowhead bt-walkup-head">
                                    <span className="bt-memo-label">MERAC Walk-up Sales (during {label} Dates)</span>
                                    <InfoPopover>
                                      <h4>Reconcile event walk-up</h4>
                                      <p>The till walk-up taken on the {label} dates. This is a <strong>memo line — not added to the total</strong>, because that money is already counted in <em>— of which MERAC Walk-up Sales</em> above.</p>
                                      <p>Press <strong>Reconcile</strong> on a month to <strong>subtract</strong> that value from MERAC Walk-up Sales and <strong>book it as an event sale</strong> under <em>Traffic Special Events</em>. The cell turns <span style={{ color: '#1f6e54' }}>green</span>; press <em>✓ moved</em> to put it back.</p>
                                    </InfoPopover>
                                  </td>
                                  {Array.from({ length: 12 }).map((_, i) => {
                                    const def = eventsWalkup[label]?.[i] ?? 0
                                    const rec = recMap.get(`${i + 1}|${label}`)
                                    if (def === 0 && rec == null) return <td key={i} className="bt-cell">—</td>
                                    return (
                                      <td key={i} className="bt-cell bt-recon-cell">
                                        <WalkupReconcileCell defaultValue={def} reconciled={rec ?? null}
                                          onReconcile={(amt) => onReconcile(i + 1, label, amt)}
                                          onUnreconcile={() => onUnreconcile(i + 1, label)} />
                                      </td>
                                    )
                                  })}
                                  <td className="bt-total bt-memo-total" title="Memo only — not summed">memo</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </Fragment>
                      )
                    })}
                    <tr className="bt-subtotal">
                      <td className="bt-rowhead">{ent.key} subtotal</td>
                      {entF.map((v, i) => <td key={i} className={v < 0 ? 'neg' : ''}>{fmtCellNum(v)}</td>)}
                      <td className="bt-total">{fmtCellNum(entTotal)}</td>
                    </tr>
                  </Fragment>
                )
              })}
              <tr className="bt-grand">
                <td className="bt-rowhead">Total income (forecast)</td>
                {grandF.map((v, i) => <td key={i} className={v < 0 ? 'neg' : ''}>{fmtCellNum(v)}</td>)}
                <td className="bt-total">{fmtCellNum(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function BudgetVsActualMonthlyChart({ vsActual, scope }: { vsActual: BudgetVsActualData | null; scope: BudgetScope }) {
  const data = useMemo(() => {
    if (!vsActual) return [] as { label: string; budget: number; actual: number | null }[]
    return vsActual.months.map((m) => ({
      label: m.label,
      budget: budgetForMonth(m, scope, 'all'),
      actual: m.year_month <= vsActual.today_year_month ? (m.actual_all_net ?? 0) : null,
    }))
  }, [vsActual, scope])
  const info = (
    <>
      <h4>What this shows</h4>
      <p>Budgeted income per month (gold line) against actual net revenue achieved (green bars). Actual appears only for months reached to date; revenue is recognised on the date of travel.</p>
      <h4>Scope</h4>
      <p><strong>Like-for-like</strong> compares only MERAC-tradeable income; <strong>Full budget</strong> adds Trust &amp; non-sales income that never appears in MERAC actuals.</p>
    </>
  )
  return (
    <ChartCard title="Monthly budget vs actual" loading={!vsActual} error={null} empty={!!vsActual && data.length === 0} className="grid-span-12" info={info}>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={60} />
          <Tooltip formatter={(v, n) => [v == null ? '—' : gbp0.format(v as number), n as string]} contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="actual" name="Actual net" fill={BUDGET_GREEN} radius={[3, 3, 0, 0]} />
          <Line type="monotone" dataKey="budget" name="Budget" stroke={BUDGET_GOLD} strokeWidth={2} dot={{ r: 2 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function BudgetPaceChart({ models, currentFm, monthLabels }: { models: StreamModel[]; currentFm: number; monthLabels: string[] }) {
  const [showActual, setShowActual] = useState(true)
  const [showForecast, setShowForecast] = useState(true)
  const [showParallel, setShowParallel] = useState(false)
  const [draftUplift, setDraftUplift] = useState(0)   // slider position
  const [uplift, setUplift] = useState(0)              // applied uplift £

  const { data, headline, anchorLabel } = useMemo(() => {
    type Row = { label: string; budgetCum: number; actualCum: number | null; forecastCum: number | null; parallelCum: number | null }
    type HL = { projectedYearEnd: number; projectedNoUplift: number; budgetFullYear: number; gap: number; gapPct: number; varToDate: number; actualToDate: number; budgetToDate: number }
    const rows: Row[] = []
    if (models.length === 0) return { data: rows, headline: null as HL | null, anchorLabel: null as string | null }

    const anchor = Math.min(11, Math.max(0, currentFm - 1))   // index of the current month
    const budgetM = Array(12).fill(0), actualM = Array(12).fill(0), forecastM = Array(12).fill(0)
    for (const m of models) for (let i = 0; i < 12; i++) { budgetM[i] += m.budget[i]; actualM[i] += (m.actual[i] ?? 0); forecastM[i] += m.forecast[i] }

    // Remaining (future) forecast — to phase the manual uplift across the rest
    // of the year in proportion to budget (reflective annual timing).
    let remTotal = 0
    for (let i = anchor + 1; i < 12; i++) remTotal += forecastM[i]

    let cumB = 0, cumA = 0, cumF = 0, cumFut = 0
    let actualToDate = 0, budgetToDate = 0
    for (let i = 0; i < 12; i++) {
      cumB += budgetM[i]
      let actualCum: number | null = null, forecastCum: number | null = null
      if (i <= anchor) {
        cumA += actualM[i]; actualCum = cumA; actualToDate = cumA; budgetToDate = cumB
        cumF = cumA; forecastCum = cumF                       // forecast tracks actual to date
      } else {
        cumF += forecastM[i]; cumFut += forecastM[i]
        const up = remTotal > 0 ? uplift * (cumFut / remTotal) : 0
        forecastCum = cumF + up
      }
      rows.push({ label: monthLabels[i] ?? '', budgetCum: cumB, actualCum, forecastCum, parallelCum: null })
    }
    // Track-parallel: hold today's £ deficit flat to year-end (budget shape, offset).
    const deficit = (rows[anchor]?.budgetCum ?? 0) - (rows[anchor]?.actualCum ?? 0)
    for (let i = anchor; i < 12; i++) rows[i].parallelCum = rows[i].budgetCum - deficit

    const projectedNoUplift = cumF
    const projectedYearEnd = cumF + uplift
    const headline: HL = {
      projectedYearEnd, projectedNoUplift, budgetFullYear: cumB,
      gap: projectedYearEnd - cumB, gapPct: cumB > 0 ? (projectedYearEnd / cumB - 1) * 100 : 0,
      varToDate: budgetToDate > 0 ? (actualToDate / budgetToDate - 1) * 100 : 0,
      actualToDate, budgetToDate,
    }
    return { data: rows, headline, anchorLabel: monthLabels[anchor] ?? null }
  }, [models, currentFm, monthLabels, uplift])

  const signedCompact = (v: number) => `${v < 0 ? '−' : '+'}${gbpCompact(Math.abs(v))}`
  const tog = (on: boolean, set: (b: boolean) => void, label: string, title: string) => (
    <button type="button" className={`trend-toggle ${on ? 'active' : ''}`} onClick={() => set(!on)} aria-pressed={on} title={title}>{label}</button>
  )

  const info = (
    <>
      <h4>What this shows</h4>
      <p>Cumulative income through the year, read straight from the editable table below:</p>
      <p>
        <strong style={{ color: BUDGET_GOLD }}>Budget</strong> — the original plan.<br />
        <strong style={{ color: BUDGET_BLUE }}>Actual</strong> — banked so far (MERAC pre-fill + any figures you've typed in).<br />
        <strong style={{ color: TREND_RED }}>Forecast</strong> — actual to date, then your forecast/overrides for the rest of the year, nudged by the uplift slider.<br />
        <strong style={{ color: '#c8791f' }}>Track-parallel</strong> — holds today's £ deficit flat to year-end (a simple worst case).
      </p>
      <h4>Steering it</h4>
      <p>Type actuals and course-corrections straight into the table (manual entries show red). Use the <strong>uplift slider → Apply</strong> for optimism/pessimism on top, or <strong>Close gap to plan</strong> to set the uplift that would bring the forecast back to budget.</p>
    </>
  )

  return (
    <ChartCard title="Cumulative pace to budget" loading={models.length === 0} error={null} empty={false} className="grid-span-12" info={info}
               modeBar={
                 <div className="bt-toggle-row">
                   {tog(showActual, setShowActual, 'Actual', 'Show the actual-to-date line')}
                   {tog(showForecast, setShowForecast, 'Forecast', 'Show the forecast line (actual to date + remaining forecast + uplift)')}
                   {tog(showParallel, setShowParallel, 'Parallel', 'Hold the current £ deficit flat to year-end (worst case)')}
                 </div>
               }
               actions={headline ? (
                 <>
                   {showForecast && (
                     <span className="budget-gap-badge">
                       Forecast year-end {gbpCompact(headline.projectedYearEnd)} · gap {signedCompact(headline.gap)} ({pct1(headline.gapPct)}){uplift > 0 ? ` · incl. +${gbpCompact(uplift)}` : ''}
                     </span>
                   )}
                   <span className="budget-pace-badge">{gbp0.format(headline.actualToDate)} actual vs {gbp0.format(headline.budgetToDate)} plan to date · {pct1(headline.varToDate)}</span>
                 </>
               ) : undefined}>
      {showForecast && headline && (
        <div className="budget-uplift-bar">
          <label htmlFor="budget-uplift">Uplift forecast</label>
          <input id="budget-uplift" type="range" min={0} max={3000000} step={50000} value={draftUplift} onChange={(e) => setDraftUplift(Number(e.target.value))} />
          <span className="uplift-val">+{gbpCompact(draftUplift)}</span>
          <button type="button" className="btn-primary" onClick={() => setUplift(draftUplift)} disabled={draftUplift === uplift}>Apply</button>
          <button type="button" className="btn-secondary" title="Set the uplift that brings the forecast back to budget"
            onClick={() => { const need = Math.max(0, Math.round((headline.budgetFullYear - headline.projectedNoUplift) / 50000) * 50000); setDraftUplift(need); setUplift(need) }}>Close gap to plan</button>
          {uplift > 0 && <button type="button" className="btn-secondary" onClick={() => { setUplift(0); setDraftUplift(0) }}>Clear</button>}
          {uplift > 0 && <span className="uplift-applied">applied +{gbpCompact(uplift)}, phased across remaining months</span>}
        </div>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={60} />
          <Tooltip formatter={(v, n) => [v == null ? '—' : gbp0.format(v as number), n as string]} contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {anchorLabel && <ReferenceLine x={anchorLabel} stroke="#6b6b6b" strokeDasharray="2 4" label={{ value: 'Now', fontSize: 10, fill: '#6b6b6b', position: 'insideTopRight' }} />}
          <Line type="monotone" dataKey="budgetCum"   name="Budget"         stroke={BUDGET_GOLD}  strokeWidth={2}   strokeDasharray="5 5" dot={false} />
          <Line type="monotone" dataKey="actualCum"   name="Actual"         stroke={BUDGET_BLUE}  strokeWidth={2.5} dot={false} connectNulls hide={!showActual} />
          <Line type="monotone" dataKey="forecastCum" name="Forecast"       stroke={TREND_RED}    strokeWidth={2}   strokeDasharray="2 3" dot={false} connectNulls hide={!showForecast} />
          <Line type="monotone" dataKey="parallelCum" name="Track-parallel" stroke="#c8791f"      strokeWidth={2}   strokeDasharray="4 4" dot={false} connectNulls hide={!showParallel} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function BudgetDailyVsActualChart({ season, scope }: { season: string | null; scope: BudgetScope }) {
  const [mode, setMode] = useState<Mode>('all')
  const effectiveMode: Mode = scope === 'full' ? 'all' : mode
  const { rows, loading, error } = useDailyRevenue(season, effectiveMode)
  const { data: vsa } = useBudgetVsActual(season)

  const data = useMemo(() => {
    const byYm = vsa ? new Map(vsa.months.map((m) => [m.year_month, m])) : null
    return rows.map((r) => {
      const [y, mm] = r.date.split('-').map(Number)
      const month = byYm?.get(y * 100 + mm)
      const days = new Date(y, mm, 0).getDate()
      const budget = month ? budgetForMonth(month, scope, effectiveMode) / days : null
      return { date: r.date, net_revenue: r.net_revenue, budget }
    })
  }, [rows, vsa, scope, effectiveMode])

  const todayIso = vsa?.today ?? null
  const todayMarker = useMemo(() => {
    if (!todayIso) return null
    let marker: string | null = null
    for (const r of data) { if (r.date <= todayIso) marker = r.date; else break }
    return marker
  }, [data, todayIso])

  const monthTicks = useMemo(() => {
    const t: string[] = []; let last = ''
    for (const r of data) { const ym = r.date.slice(0, 7); if (ym !== last) { t.push(r.date); last = ym } }
    return t
  }, [data])
  const formatTick = (d: string) => new Date(d).toLocaleDateString('en-GB', { month: 'short' })
  const formatTooltipLabel = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const info = (
    <>
      <h4>What this shows</h4>
      <p>The daily revenue trend (green) — actual net revenue per day — with the budget run-rate (gold dashed) plotted against it. Each month's budget is spread evenly across its days.</p>
      <h4>The “Today” marker</h4>
      <p>Left of it, actual vs budget is a true comparison. Right of it the green line is advance bookings only (revenue is booked to the travel date), so it climbs as each event approaches.</p>
      <h4>Scope &amp; mode</h4>
      <p>Like-for-like matches budget to MERAC sales by service type (mode pills). Full budget spreads the entire plan at the ‘All’ level.</p>
    </>
  )

  return (
    <ChartCard title="Daily revenue vs budget" loading={loading} error={error} empty={rows.length === 0} className="grid-span-12" info={info}
               modeBar={scope === 'merac' ? <ModePillBar mode={mode} onChange={setMode} /> : undefined}>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" />
          <XAxis dataKey="date" ticks={monthTicks} tickFormatter={formatTick} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
          <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={60} />
          <Tooltip labelFormatter={formatTooltipLabel} formatter={(v, n) => [v == null ? '—' : gbp0.format(v as number), n as string]} contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {todayMarker && <ReferenceLine x={todayMarker} stroke="#6b6b6b" strokeDasharray="2 4" label={{ value: 'Today', fontSize: 10, fill: '#6b6b6b', position: 'insideTopRight' }} />}
          <Line type="monotone" dataKey="net_revenue" name="Actual net (daily)" stroke={BUDGET_GREEN} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          <Line type="monotone" dataKey="budget" name="Budget run-rate" stroke={BUDGET_GOLD} strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

// Management P&L (from iplicit) on the Budget page. A single wide table shows the
// full operating year (March–Feb, spanning two calendar years) — one column per
// month across the green header row, plus a Year-to-date column. Each month's
// header cell carries a small lock toggle: a locked month is frozen and the
// nightly iplicit refresh won't overwrite it. Income shown positive; expenses
// listed as plain black costs (no brackets); only the operating result and grand
// total are signed (a loss reads as -£x). A £ pill (default off) toggles the
// currency symbol; a button downloads the whole P&L as a landscape PDF.
interface PnlLine { values: number[]; ytd: number }
interface PnlSub extends PnlLine { sub: string }
interface PnlCategory { category: string; total: PnlLine; subs: PnlSub[] }
interface PnlMonth { period_no: number; name: string; short: string; cal_year: number; locked: boolean }
interface PnlData {
  season: string
  entity: string
  entity_label: string
  months: PnlMonth[]
  current_period: number | null
  last_refresh: string | null
  has_data: boolean
  categories: PnlCategory[]
  operating: PnlLine
  exceptional: { subs: PnlSub[]; total: PnlLine }
  total: PnlLine
}

// A P&L amount. `signedLine` = result/total rows keep their sign (a loss shows
// as -£x); everything else (income and expenses) shows a plain positive
// magnitude in black. `showPound` toggles the £ symbol.
function pnlText(n: number, showPound: boolean, signedLine: boolean): string {
  const v = Math.round(n)
  if (v === 0) return '–'
  const body = showPound ? gbp0.format(Math.abs(v)) : num0.format(Math.abs(v))
  return signedLine && v < 0 ? `-${body}` : body
}

// One P&L line across the whole year: a label cell, one amount per month, then
// the year-to-date amount. `signedLine` (result/total rows) keeps the sign.
function PnlValueRow({ label, line, kind, indent, showPound }: {
  label: string; line: PnlLine
  kind?: 'sub' | 'subtotal' | 'result' | 'total'; indent?: boolean; showPound: boolean
}) {
  const cls = `pnl-row${kind ? ` pnl-row--${kind}` : ''}`
  const signedLine = kind === 'result' || kind === 'total'
  const cells = [...line.values, line.ytd]
  return (
    <tr className={cls}>
      <td className={`pnl-label${indent ? ' pnl-indent' : ''}`}>{label}</td>
      {cells.map((v, i) => (
        <td key={i} className={`ta-right pnl-num${i === cells.length - 1 ? ' pnl-ytd' : ''}`}>
          {pnlText(v, showPound, signedLine)}
        </td>
      ))}
    </tr>
  )
}

function PnlTable({ season }: { season: string | null }) {
  const [data, setData] = useState<PnlData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPound, setShowPound] = useState(false)
  const [lockBusy, setLockBusy] = useState<number | null>(null)

  useEffect(() => {
    if (!season) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`/api/budget/pnl?season=${encodeURIComponent(season)}`)
      .then(async (r) => {
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) }
        return r.json() as Promise<PnlData>
      })
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [season])

  async function toggleLock(m: PnlMonth) {
    if (!data) return
    setLockBusy(m.period_no)
    setError(null)
    try {
      const r = await fetch('/api/budget/pnl/lock', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season: data.season, entity: data.entity === 'NYMR01' ? 'TRUST' : 'PLC', period_no: m.period_no, locked: !m.locked }),
      })
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Lock failed (${r.status})`) }
      setData((prev) => prev ? { ...prev, months: prev.months.map((x) => x.period_no === m.period_no ? { ...x, locked: !m.locked } : x) } : prev)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLockBusy(null)
    }
  }

  async function downloadPdf() {
    if (!data) return
    const { buildPnlPdf } = await import('./components/pnlPdf')
    buildPnlPdf(data, showPound)
  }

  const monthCols = data?.months ?? []

  return (
    <section className="budget-pnl">
      <div className="sales-section-head">
        <h2>Management P&amp;L — {data?.entity_label ?? 'PLC'} <span style={{ fontWeight: 400, color: '#6b6b6b', fontSize: '0.85rem' }}>(from iplicit)</span></h2>
        <span className="sales-section-sub">
          Income shown positive; expenses listed as costs. Each month is a column, plus year-to-date. A month reads <strong>DRAFT</strong> while it updates nightly from iplicit; click it to mark <strong>FINAL</strong> — a signed-off month, frozen against the nightly update.
          {data?.last_refresh && ` · updated ${new Date(data.last_refresh).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}`}
        </span>
      </div>

      <div className="svc-table-wrap">
        {loading ? (
          <div className="sales-empty">Loading…</div>
        ) : error ? (
          <div className="sales-empty sales-empty--error">{error}</div>
        ) : !data || !data.has_data ? (
          <div className="sales-empty">No P&amp;L loaded for {season} yet — the nightly iplicit pull may not have run.</div>
        ) : (
          <>
            <div className="pnl-controls">
              <span className="pnl-controls__month">Season {data.season}</span>
              <div className="pnl-controls__right">
                <button type="button" className={`pnl-pound-pill${showPound ? ' active' : ''}`} aria-pressed={showPound} onClick={() => setShowPound((v) => !v)} title="Show or hide the £ symbol on amounts">£</button>
                <button type="button" className="btn-secondary pnl-pdf-btn" onClick={downloadPdf} title="Download the full P&L (all months) as a PDF">Download PDF</button>
              </div>
            </div>
            <table className="svc-table pnl-table pnl-table--wide">
              <thead>
                <tr>
                  <th className="pnl-label">&nbsp;</th>
                  {monthCols.map((m) => {
                    const isCur = m.period_no === data.current_period
                    return (
                      <th key={m.period_no} className={`ta-right pnl-mth${m.locked ? ' pnl-mth--locked' : ''}${isCur ? ' pnl-mth--current' : ''}`}>
                        <div className="pnl-mth__inner">
                          <span className="pnl-mth__label">{m.short}&nbsp;<span className="pnl-mth__yr">’{String(m.cal_year).slice(2)}</span></span>
                          <button
                            type="button"
                            className={`pnl-mth__status pnl-mth__status--${m.locked ? 'final' : 'draft'}`}
                            onClick={() => toggleLock(m)}
                            disabled={lockBusy === m.period_no}
                            aria-label={m.locked ? `${m.name} is FINAL (locked). Click to set back to DRAFT.` : `${m.name} is DRAFT. Click to mark FINAL and lock it.`}
                            title={m.locked ? 'FINAL — signed off and frozen; the nightly iplicit update will not change this month. Click to set back to DRAFT.' : 'DRAFT — updates nightly from iplicit. Click to mark FINAL and freeze it.'}
                          >
                            {m.locked ? 'FINAL' : 'DRAFT'}
                          </button>
                        </div>
                      </th>
                    )
                  })}
                  <th className="ta-right pnl-ytd">Year to date</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((c) => (
                  <Fragment key={c.category}>
                    <PnlValueRow label={c.category} line={c.total} kind="subtotal" showPound={showPound} />
                    {c.subs.map((s) => <PnlValueRow key={s.sub} label={s.sub} line={s} kind="sub" indent showPound={showPound} />)}
                  </Fragment>
                ))}
                <PnlValueRow label="Operating result (Profit &amp; Loss)" line={data.operating} kind="result" showPound={showPound} />
                {data.exceptional.subs.length > 0 && (
                  <>
                    <PnlValueRow label="Exceptional Items" line={data.exceptional.total} kind="subtotal" showPound={showPound} />
                    {data.exceptional.subs.map((s) => <PnlValueRow key={s.sub} label={s.sub} line={s} kind="sub" indent showPound={showPound} />)}
                  </>
                )}
                <PnlValueRow label="Total" line={data.total} kind="total" showPound={showPound} />
              </tbody>
            </table>
            <p className="budget-table-note" style={{ marginTop: 8 }}>
              Source: iplicit &ldquo;P&amp;L – parameterised&rdquo; enquiry, {data.entity_label}, season {data.season}. Operating result = Income less Direct, Semi-Direct and Indirect expenses. Each column is a calendar month; the final column is year-to-date.
            </p>
          </>
        )}
      </div>
    </section>
  )
}

// ---------- Gift Aid tab — gated on SG-Reports-Budgets (me.isBudget) ----------
// Rebuilds the GiftAidAnalysis Power BI report from the AZEPOS Donation Fare
// admissions. Donations = value of Donation Fares (IsGAItem); reclaim = 25%.
interface GiftAidMonthRow {
  fiscal_month_no: number
  month_label: string
  agreed: number
  eligible_reclaim: number
  claimed: number
  claimed_reclaim: number
  not_ga: number
  submit_claimed: number
  submit_claimed_reclaim: number
}
interface GiftAidData {
  season: string
  reclaim_rate: number
  has_data: boolean
  last_refresh: string | null
  agreed_base: number
  eligible_reclaim: number
  claimed_base: number
  claimed_reclaim: number
  not_ga_base: number
  submit_claimed_base: number
  pct_claimed: number | null
  by_month: GiftAidMonthRow[]
}

// ---------- Sales behind the Gift Aid figures (the reconciliation card) ------
// Sourced from cache.AllSales (same feed + definitions as Dashboard One), so the
// Online (website) figure ties to Dashboard One's booking-source "Unknown" slice.
// received = travelled to date; advance = booked for future travel. Last-year is
// the SAME point in the season (a year ago) for a like-for-like comparison.
interface GaSalesSplit { walkup: number; online: number; phoneemail: number; total: number }
interface GaSalesRates { walkup: number; online: number; phoneemail: number }
interface GaSalesSummary {
  season: string; prior_season: string | null; as_of: string; cutoff_py: string
  current: { received: GaSalesSplit; advance: GaSalesSplit; total: number }
  prior:   { received: GaSalesSplit; advance: GaSalesSplit; total: number }
  estimate: {
    ytd_eligible: number; ytd_reclaim: number; claimed_base: number; claimed_reclaim: number
    rates: GaSalesRates
    received_admission: GaSalesSplit; advance_admission: GaSalesSplit
    advance_eligible_by: GaSalesRates
    advance_eligible: number; advance_reclaim: number
    all_eligible: number; all_reclaim: number
  }
}

function GiftAidSalesCard({ sales }: { sales: GaSalesSummary | null }) {
  if (!sales) return null
  const c = sales.current, p = sales.prior
  const asOf = new Date(sales.as_of).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const rows: { label: string; recv: number; adv: number; total: number; pyRecv: number; pyAdv: number; note?: string }[] = [
    { label: 'Walk-up',          recv: c.received.walkup,     adv: c.advance.walkup,     total: c.received.walkup + c.advance.walkup,         pyRecv: p.received.walkup,     pyAdv: p.advance.walkup },
    { label: 'Online (website)', recv: c.received.online,     adv: c.advance.online,     total: c.received.online + c.advance.online,         pyRecv: p.received.online,     pyAdv: p.advance.online,     note: '= Dashboard One' },
    { label: 'Phone / email',    recv: c.received.phoneemail, adv: c.advance.phoneemail, total: c.received.phoneemail + c.advance.phoneemail, pyRecv: p.received.phoneemail, pyAdv: p.advance.phoneemail },
  ]
  const delta = (cur: number, py: number) => (py > 0 ? Math.round(((cur - py) / py) * 1000) / 10 : null)
  const ly = (cur: number, py: number) => {
    const d = delta(cur, py)
    return (
      <div style={{ fontSize: '0.68rem', color: '#8a8577', marginTop: 2, whiteSpace: 'nowrap' }}>
        LY {gbpCompact(py)}
        {d != null && <span style={{ color: d >= 0 ? '#177245' : TREND_RED, marginLeft: 4 }}>{d >= 0 ? '▲' : '▼'}{Math.abs(d)}%</span>}
      </div>
    )
  }
  const numCell = (cur: number, py: number, showLy: boolean) => (
    <td className="ta-right" style={{ padding: '6px 12px', fontVariantNumeric: 'tabular-nums' }}>
      <div style={{ fontWeight: 700, color: '#0b3d2e' }}>{gbpCompact(cur)}</div>
      {showLy && ly(cur, py)}
    </td>
  )

  return (
    <section className="kpi-section" style={{ marginBottom: 18 }}>
      <header className="kpi-section__head">
        <span className="kpi-section__label">Sales behind these Gift Aid figures</span>
        <InfoPopover>
          <h4>What this shows</h4>
          <p>The ticket &amp; admission sales this season that the Gift Aid analysis is drawn from, split by channel and by whether the trip has already run. Same source and definitions as <strong>Dashboard One</strong>, so <strong>Online (website)</strong> ties to Dashboard One&apos;s website (&ldquo;Unknown&rdquo;) sales.</p>
          <h4>Received vs Advance</h4>
          <p><strong>Received (YTD)</strong> = travel date on or before today. <strong>Advance</strong> = booked for future travel (still to run — can still cancel). <strong>Last year (LY)</strong> is the same point in last season, for a like-for-like comparison.</p>
          <h4>Online = website</h4>
          <p>Online here is website bookings only (MERAC records no source for them). Phone and email bookings are their own channel — exactly as Dashboard One splits them.</p>
        </InfoPopover>
      </header>
      <div className="svc-table-wrap" style={{ overflowX: 'auto', background: '#f7f3ea', border: '1px solid #e5e1d6', borderRadius: 8, padding: '4px 6px' }}>
        <table className="svc-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 12px' }}>Channel</th>
              <th className="ta-right" style={{ padding: '6px 12px' }}>Received (YTD)</th>
              <th className="ta-right" style={{ padding: '6px 12px' }}>Advance (forward book)</th>
              <th className="ta-right" style={{ padding: '6px 12px' }}>Season to date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td style={{ padding: '6px 12px' }}>
                  {r.label}
                  {r.note && <span style={{ fontSize: '0.66rem', color: BUDGET_GOLD, marginLeft: 6, fontWeight: 700 }}>{r.note}</span>}
                </td>
                {numCell(r.recv, r.pyRecv, true)}
                {numCell(r.adv, r.pyAdv, true)}
                <td className="ta-right" style={{ padding: '6px 12px', fontVariantNumeric: 'tabular-nums', color: '#3a5a4a' }}>{gbpCompact(r.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #d8d2c2' }}>
              <td style={{ padding: '6px 12px', fontWeight: 800 }}>Total</td>
              {numCell(c.received.total, p.received.total, true)}
              {numCell(c.advance.total, p.advance.total, true)}
              <td className="ta-right" style={{ padding: '6px 12px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{gbpCompact(c.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="budget-table-note" style={{ marginTop: 6 }}>
        Net revenue, from the Dashboard One sales feed. As at {asOf}. &ldquo;Season to date&rdquo; is in progress and will grow as more advance bookings are taken; the like-for-like comparison is the LY figure under Received and Advance (same point last season).
      </p>
    </section>
  )
}

function GiftAidDashboard({ me }: { me: MeResponse }) {
  const { seasons } = useSeasons()
  const [season, setSeason] = useState<string | null>(null)
  useEffect(() => {
    if (season || seasons.length === 0) return
    const cur = seasons.find((s) => s.is_current) ?? seasons[0]
    setSeason(cur.year_label)
  }, [seasons, season])

  const [data, setData] = useState<GiftAidData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Sales the analysis is drawn from (the reconciliation card) + the YTD/All basis.
  const [sales, setSales] = useState<GaSalesSummary | null>(null)
  const [coverage, setCoverage] = useState<'ytd' | 'all'>('ytd')
  useEffect(() => {
    if (!season) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`/api/budget/gift-aid?season=${encodeURIComponent(season)}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<GiftAidData> })
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [season])

  useEffect(() => {
    if (!season) return
    let cancelled = false
    setSales(null)
    fetch(`/api/budget/gift-aid/sales-summary?season=${encodeURIComponent(season)}`)
      .then(async (r) => { if (!r.ok) throw new Error(`sales-summary ${r.status}`); return r.json() as Promise<GaSalesSummary> })
      .then((d) => { if (!cancelled) setSales(d) })
      .catch(() => { if (!cancelled) setSales(null) })
    return () => { cancelled = true }
  }, [season])

  // Date axis: 'sale' = donation taken date (earned); 'submit' = HMRC claim date.
  const [basis, setBasis] = useState<'sale' | 'submit'>('sale')
  const monthly = useMemo(() => {
    const rows = data?.by_month ?? []
    if (basis === 'submit') {
      return rows.filter((m) => m.submit_claimed > 0)
        .map((m) => ({ label: m.month_label, claimed: m.submit_claimed, claimed_reclaim: m.submit_claimed_reclaim, agreed: 0, eligible_reclaim: 0 }))
    }
    return rows.filter((m) => m.agreed !== 0 || m.claimed !== 0)
      .map((m) => ({ label: m.month_label, claimed: m.claimed, claimed_reclaim: m.claimed_reclaim, agreed: m.agreed, eligible_reclaim: m.eligible_reclaim }))
  }, [data, basis])

  // At-a-glance figures under the YTD/All basis. YTD = actuals; All = actuals +
  // the channel-matched advance estimate (eligible only — you can't claim advance).
  const eff = useMemo(() => {
    const all = coverage === 'all' && !!sales
    const eligibleBase    = all ? sales!.estimate.all_eligible : (data?.agreed_base ?? 0)
    const eligibleReclaim = all ? sales!.estimate.all_reclaim  : (data?.eligible_reclaim ?? 0)
    const claimedBase     = data?.claimed_base ?? 0
    const claimedReclaim  = data?.claimed_reclaim ?? 0
    const pctClaimed      = eligibleBase > 0 ? Math.round((claimedBase / eligibleBase) * 1000) / 10 : null
    return { all, eligibleBase, eligibleReclaim, claimedBase, claimedReclaim, pctClaimed }
  }, [coverage, sales, data])

  if (!me.isBudget) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Gift Aid — restricted</strong>
          <p>This area is limited to members of <code>SG-Reports-Budgets</code>. Ask Chris Flynn for access.</p>
        </div>
      </main>
    )
  }

  const pct = (v: number, base: number) => (base > 0 ? `${Math.round((v / base) * 1000) / 10}%` : '—')

  return (
    <>
      <SeasonBar seasons={seasons} activeYear={season} onChange={setSeason} />
      <main className="app-main">
        <div className="budget-toolbar">
          <div>
            <p className="budget-sub">Confidential — visible only to <code>SG-Reports-Budgets</code>. Eligible = donation fares where the donor <strong>agreed</strong> Gift Aid; claimed = submitted to HMRC.</p>
          </div>
          <div className="budget-scope-wrap">
            <div className="budget-scope-toggle" role="group" aria-label="Date basis">
              <button type="button" className={`scope-pill ${basis === 'sale' ? 'active' : ''}`} onClick={() => setBasis('sale')}
                      title="Sale date — when the donation was taken at the till (the HMRC donation date). Advance bookings count in the month they were sold.">Sale date</button>
              <button type="button" className={`scope-pill ${basis === 'submit' ? 'active' : ''}`} onClick={() => setBasis('submit')}
                      title="Submission date — when the claim was submitted to HMRC (from the Gift Aid claim register).">Submission date</button>
            </div>
            <InfoPopover>
              <h4>Date basis</h4>
              <p><strong>Sale date</strong> groups by when the donation was taken — the complete, HMRC-correct donation date. (Travel date isn&apos;t used: donation fares are largely walk-up and have no booking arrival, so a travel axis would miss about half the donations.)</p>
              <p><strong>Submission date</strong> groups the claimed amounts by when they were submitted to HMRC.</p>
            </InfoPopover>
          </div>
        </div>
        {error && <div className="placeholder" style={{ borderColor: '#a33' }}><strong>Couldn&apos;t load Gift Aid</strong><p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p></div>}
        {loading && !data && <p>Loading…</p>}
        {data && !data.has_data && !loading && (
          <div className="placeholder"><strong>No Gift Aid data for {season} yet</strong><p>Run <code>Refresh-GiftAid.ps1</code> on ONTRACK to populate <code>cache.GiftAidMonthly</code>.</p></div>
        )}
        {data && data.has_data && (
          <>
            <GiftAidSalesCard sales={sales} />

            <div className="budget-scope-toggle" role="group" aria-label="Gift Aid basis" style={{ margin: '0 0 14px', display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: '0.8rem', color: '#6b6b6b', marginRight: 4 }}>Basis:</span>
              <button type="button" className={`scope-pill ${coverage === 'ytd' ? 'active' : ''}`} onClick={() => setCoverage('ytd')}
                      title="Year to date — Gift Aid on revenue actually received (trips travelled to date). The claimable actuals.">Year to date</button>
              <button type="button" className={`scope-pill ${coverage === 'all' ? 'active' : ''}`} onClick={() => setCoverage('all')}
                      title="All — YTD actuals PLUS an estimate of Gift Aid on advance bookings (future travel), applying each channel's realised gift-aid rate to its forward-booked admissions.">All (incl. advance est.)</button>
              <InfoPopover>
                <h4>Year to date vs All</h4>
                <p><strong>Year to date</strong> is the actual, claimable position — Gift Aid on trips that have already run. Advance bookings carry no Gift-Aid record until travelled (and can still cancel), so they are excluded.</p>
                <p><strong>All (incl. advance est.)</strong> adds an <em>estimate</em> of the Gift Aid on the forward book, by applying each channel&apos;s realised gift-aid rate (agreed &divide; received admissions) to that channel&apos;s advance admissions. It is an estimate, not a claim.</p>
              </InfoPopover>
              {coverage === 'all' && sales && (
                <span style={{ fontSize: '0.74rem', color: '#8a8577' }}>
                  + est. {gbpCompact(sales.estimate.advance_reclaim)} reclaim on {gbpCompact(sales.estimate.advance_admission.total)} advance admissions
                </span>
              )}
            </div>

            <section className="kpi-section">
              <header className="kpi-section__head">
                <span className="kpi-section__label">Gift Aid at a glance{coverage === 'all' ? ' — incl. advance estimate' : ''}</span>
                <InfoPopover>
                  <h4>How this is calculated</h4>
                  <p>NYMR&apos;s <strong>Donation Fare</strong> admissions are donations. We can only claim on the ones where the donor <strong>agreed Gift Aid</strong> (signed the declaration) — that&apos;s the <strong>eligible</strong> base. <strong>Reclaim = 25%</strong> of it.</p>
                  <p><strong>Claimed</strong> = the donations submitted to HMRC (in the Gift Aid claim register). <strong>% claimed</strong> = claimed ÷ eligible donations.</p>
                  <p>Donation fares where the donor did <em>not</em> agree Gift Aid can&apos;t be claimed and are shown separately.</p>
                </InfoPopover>
              </header>
              <div className="kpi-bar">
                <KpiCard label={eff.all ? 'Eligible to claim (25%) — est.' : 'Eligible to claim (25%)'} value={gbpCompact(eff.eligibleReclaim)} loading={loading}
                  sub={{ label: eff.all ? 'incl. advance estimate' : 'on agreed donations', value: gbpCompact(eff.eligibleBase) }} />
                <KpiCard label="Claimed (25%)" value={gbpCompact(eff.claimedReclaim)} loading={loading}
                  sub={{ label: 'claimed donations', value: gbpCompact(eff.claimedBase) }} />
                <KpiCard label={eff.all ? '% claimed (of est.)' : '% claimed'} value={eff.pctClaimed != null ? `${eff.pctClaimed}%` : '—'} loading={loading}
                  sub={{ label: 'claimed / eligible donations', value: `${gbpCompact(eff.claimedBase)} / ${gbpCompact(eff.eligibleBase)}` }} />
                <KpiCard label="Not gift-aided" value={gbpCompact(data.not_ga_base)} loading={loading}
                  sub={{ label: 'donated, declaration not signed', value: 'cannot claim', muted: true }} />
              </div>
            </section>

            <div className="dashboard-grid">
              <ChartCard title={basis === 'submit' ? 'Claimed by submission month' : 'Eligible vs claimed donations by month'} loading={false} error={null} empty={monthly.length === 0} className="grid-span-12"
                info={<><h4>What this shows</h4><p>{basis === 'submit' ? 'The donation base claimed from HMRC each month it was submitted.' : 'Each month, the donations eligible for Gift Aid (agreed) and the portion that has been claimed. Hover a bar for the 25% reclaim.'}</p></>}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthly} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e1d6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" />
                    <YAxis tickFormatter={(v) => gbpCompact(v as number)} tick={{ fontSize: 11, fill: '#6b6b6b' }} stroke="#e5e1d6" width={60} />
                    <Tooltip
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      formatter={(v: any, n: any) => [`${gbp0.format(Math.round(v as number))} (${gbp0.format(Math.round((v as number) * 0.25))} reclaim)`, n as string]}
                      contentStyle={{ background: '#fff', border: '1px solid #e5e1d6', fontSize: 12 }}
                      cursor={{ fill: 'rgba(11, 61, 46, 0.05)' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {basis === 'sale' && <Bar dataKey="agreed" name="Eligible (agreed)" fill="#c89b3c" radius={[3, 3, 0, 0]} />}
                    <Bar dataKey="claimed" name="Claimed" fill="#0b3d2e" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <section className="sales-section">
              <div className="sales-section-head">
                <h2>Gift Aid by month — {basis === 'submit' ? 'submission date' : 'sale date'}</h2>
                <span className="sales-section-sub">Eligible = agreed donations (× 25% reclaim); claimed = submitted to HMRC{coverage === 'all' ? ' · monthly figures are actuals (YTD); the advance estimate shows as its own line in the by-user table below' : ''}</span>
              </div>
              <div className="svc-table-wrap">
                {monthly.length === 0 ? (
                  <div className="sales-empty">No Gift Aid in this period.</div>
                ) : (
                  <table className="svc-table">
                    <thead>
                      {basis === 'submit' ? (
                        <tr><th>Month</th><th className="ta-right">Claimed donations</th><th className="ta-right">Claimed reclaim (25%)</th></tr>
                      ) : (
                        <tr><th>Month</th><th className="ta-right">Eligible donations</th><th className="ta-right">Eligible reclaim</th><th className="ta-right">Claimed reclaim</th><th className="ta-right">% claimed</th></tr>
                      )}
                    </thead>
                    <tbody>
                      {monthly.map((m) => basis === 'submit' ? (
                        <tr key={m.label}>
                          <td>{m.label}</td>
                          <td className="ta-right">{gbp0.format(Math.round(m.claimed))}</td>
                          <td className="ta-right">{gbp0.format(Math.round(m.claimed_reclaim))}</td>
                        </tr>
                      ) : (
                        <tr key={m.label}>
                          <td>{m.label}</td>
                          <td className="ta-right">{gbp0.format(Math.round(m.agreed))}</td>
                          <td className="ta-right">{gbp0.format(Math.round(m.eligible_reclaim))}</td>
                          <td className="ta-right">{gbp0.format(Math.round(m.claimed_reclaim))}</td>
                          <td className="ta-right">{pct(m.claimed, m.agreed)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      {basis === 'submit' ? (
                        <tr><td>Total</td><td className="ta-right">{gbp0.format(Math.round(data.submit_claimed_base))}</td><td className="ta-right">{gbp0.format(Math.round(data.submit_claimed_base * 0.25))}</td></tr>
                      ) : (
                        <tr><td>Total</td><td className="ta-right">{gbp0.format(Math.round(data.agreed_base))}</td><td className="ta-right">{gbp0.format(Math.round(data.eligible_reclaim))}</td><td className="ta-right">{gbp0.format(Math.round(data.claimed_reclaim))}</td><td className="ta-right">{data.pct_claimed != null ? `${data.pct_claimed}%` : '—'}</td></tr>
                      )}
                    </tfoot>
                  </table>
                )}
              </div>
            </section>

            <GiftAidByUserSection season={season} advanceEligible={coverage === 'all' && sales ? sales.estimate.advance_eligible : null} />

            <p className="dashboard-footer">
              Source: MERAC Gift Aid — donation element where the donor agreed Gift Aid (<code>DonationDetail.donGADFlag='G'</code>); claimed = the HMRC claim register (<code>dbo.Giftaid</code>). Reclaim = donations × {Math.round(data.reclaim_rate * 100)}%.
              {data.last_refresh && <> · updated {new Date(data.last_refresh).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}</>}
            </p>
          </>
        )}
      </main>
    </>
  )
}

// ---------------------------------------------------------------------------
//  Gift Aid by user — conversions table (cache.GiftAidByUser, db/49). One row
//  per user (or outlet / channel), EVERY metric as its own column:
//  Conversations | Eligible £ | Reclaim £ | Claimed £ | Conversion % | Share %.
//  'Till' rows credit the operator who took the walk-up donation; GA chosen at
//  web checkout appears as its own 'Online' line (never credited to a user).
//  A month filter (default: whole season) covers the by-month cut.
// ---------------------------------------------------------------------------

interface GaByUserRow {
  fiscal_month_no: number; channel: string; operator_name: string | null
  outlet: string; eligible_txns: number; ga_txns: number
  eligible_tickets: number; ga_tickets: number
  agreed_base: number; claimed_base: number; not_ga_base: number; user: string
}
interface GaByUserData { season: string; last_refresh: string | null; month_labels: string[]; rows: GaByUserRow[] }

type GaDim = 'user' | 'outlet' | 'channel'

const ADVANCE_ROW_KEY = 'Advance bookings (est.)'
function GiftAidByUserSection({ season, advanceEligible }: { season: string | null; advanceEligible: number | null }) {
  const [data, setData] = useState<GaByUserData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dim, setDim] = useState<GaDim>('user')
  const [month, setMonth] = useState<number | null>(null) // null = whole season
  const [breakdownMode, setBreakdownMode] = useState(false)
  const [detail, setDetail] = useState<{ key: string } | null>(null)
  const [priorData, setPriorData] = useState<GaByUserData | null>(null)

  useEffect(() => {
    if (!season) return
    let cancelled = false
    fetch(`/api/budget/gift-aid/by-user?season=${encodeURIComponent(season)}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<GaByUserData> })
      .then((d) => { if (!cancelled) { setData(d); setError(null) } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
    return () => { cancelled = true }
  }, [season])

  // Prior season (same grain) for the "LY" comparison columns.
  useEffect(() => {
    if (!season) { setPriorData(null); return }
    let cancelled = false
    const py = priorSeasonLabel(season)
    fetch(`/api/budget/gift-aid/by-user?season=${encodeURIComponent(py)}`)
      .then((r) => (r.ok ? (r.json() as Promise<GaByUserData>) : null))
      .then((d) => { if (!cancelled) setPriorData(d) })
      .catch(() => { if (!cancelled) setPriorData(null) })
    return () => { cancelled = true }
  }, [season])

  const labels = data?.month_labels ?? []
  const monthsPresent = useMemo(
    () => [...new Set((data?.rows ?? []).map((r) => r.fiscal_month_no))].sort((a, b) => a - b),
    [data]
  )

  interface GaAgg { key: string; conversations: number; gaTickets: number; eligTickets: number; agreed: number; claimed: number }
  const emptyAgg = (k: string): GaAgg => ({ key: k, conversations: 0, gaTickets: 0, eligTickets: 0, agreed: 0, claimed: 0 })
  const keyOf = (r: GaByUserRow) => (dim === 'user' ? r.user : dim === 'outlet' ? r.outlet : r.channel)
  const aggregate = (rows: GaByUserRow[]) => {
    const byKey = new Map<string, GaAgg>()
    for (const r of rows) {
      const k = keyOf(r)
      const a = byKey.get(k) ?? emptyAgg(k)
      a.conversations += r.ga_txns
      a.gaTickets += r.ga_tickets ?? 0
      a.eligTickets += r.eligible_tickets ?? 0
      a.agreed += r.agreed_base
      a.claimed += r.claimed_base
      byKey.set(k, a)
    }
    const total = [...byKey.values()].reduce(
      (t, r) => ({ key: 'Total', conversations: t.conversations + r.conversations, gaTickets: t.gaTickets + r.gaTickets, eligTickets: t.eligTickets + r.eligTickets, agreed: t.agreed + r.agreed, claimed: t.claimed + r.claimed }),
      emptyAgg('Total')
    )
    return { byKey, total }
  }

  const { rows: tableRows, total, priorByKey, priorTotal } = useMemo(() => {
    const cur = aggregate((data?.rows ?? []).filter((r) => month === null || r.fiscal_month_no === month))
    const rows = [...cur.byKey.values()].sort((a, b) => b.agreed - a.agreed)
    let total = cur.total
    // "All (incl. advance est.)" — the advance estimate is a season-level, channel-
    // matched figure (not attributable to a till user), so on the whole-season view
    // append it as its own clearly-labelled line and fold it into the total so the
    // table reconciles to the at-a-glance headline. Not shown for a single month.
    if (advanceEligible && advanceEligible > 0 && month === null) {
      rows.push({ key: ADVANCE_ROW_KEY, conversations: 0, gaTickets: 0, eligTickets: 0, agreed: advanceEligible, claimed: 0 })
      total = { ...total, agreed: total.agreed + advanceEligible }
    }
    // LY = prior season, matched by row key. When "whole season" is selected we
    // restrict the prior season to the SAME fiscal months present this season, so
    // it is a like-for-like comparison (this season is only part-way through).
    const scope = (fm: number) => (month === null ? monthsPresent.includes(fm) : fm === month)
    const pri = aggregate((priorData?.rows ?? []).filter((r) => scope(r.fiscal_month_no)))
    return { rows, total, priorByKey: pri.byKey, priorTotal: pri.total }
  }, [data, priorData, dim, month, monthsPresent, advanceEligible])

  const pctStr = (num: number, den: number) => (den > 0 ? `${Math.round((num / den) * 1000) / 10}%` : '—')
  const LY_GREY = '#b3ada1' // muted grey so the last-year columns sit back
  const n = (v: number) => (v ? num0.format(v) : '—')
  const g = (v: number) => (v ? gbp0.format(Math.round(v)) : '—')
  const lyTd = (content: string) => <td className="ta-right" style={{ color: LY_GREY, fontWeight: 400 }}>{content}</td>
  const cells = (r: GaAgg, ly: GaAgg, grandAgreed: number, grandAgreedLy: number) => (
    <>
      <td className="ta-right">{n(r.conversations)}</td>{lyTd(n(ly.conversations))}
      <td className="ta-right">{n(r.gaTickets)}</td>{lyTd(n(ly.gaTickets))}
      <td className="ta-right">{n(r.eligTickets)}</td>{lyTd(n(ly.eligTickets))}
      <td className="ta-right">{g(r.agreed)}</td>{lyTd(g(ly.agreed))}
      <td className="ta-right">{r.agreed ? gbp0.format(Math.round(r.agreed * 0.25)) : '—'}</td>{lyTd(ly.agreed ? gbp0.format(Math.round(ly.agreed * 0.25)) : '—')}
      <td className="ta-right">{g(r.claimed)}</td>{lyTd(g(ly.claimed))}
      <td className="ta-right">{pctStr(r.gaTickets, r.eligTickets)}</td>{lyTd(pctStr(ly.gaTickets, ly.eligTickets))}
      <td className="ta-right">{grandAgreed > 0 && r.agreed ? pctStr(r.agreed, grandAgreed) : '—'}</td>{lyTd(grandAgreedLy > 0 && ly.agreed ? pctStr(ly.agreed, grandAgreedLy) : '—')}
    </>
  )

  return (
    <section className="sales-section">
      <div className="sales-section-head">
        <h2>Gift Aid conversations by user</h2>
        <span className="sales-section-sub">
          Walk-up donations credit the till user who had the conversation; Gift Aid chosen at web checkout shows as <strong>Online</strong> (not credited to a user).
          Conversion % = gift-aided tickets ÷ <em>all</em> eligible admission tickets that user sold — donation fares <em>and</em> the standard fares customers buy when they decline
          (same eligibility rules as the Finance Power BI Gift Aid analysis); share % = of everyone&apos;s eligible donations.
          {' '}<span style={{ color: '#8a8577' }}>Grey <strong>LY</strong> columns = last season, same months (like-for-like), matched by the same row.</span>
        </span>
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0 10px' }}>
        <label style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>
          Rows:{' '}
          <select value={dim} onChange={(e) => setDim(e.target.value as GaDim)}>
            <option value="user">User</option>
            <option value="outlet">Outlet</option>
            <option value="channel">Channel</option>
          </select>
        </label>
        <label style={{ fontSize: '0.8rem', color: '#6b6b6b' }}>
          Month:{' '}
          <select value={month === null ? 'all' : String(month)} onChange={(e) => setMonth(e.target.value === 'all' ? null : Number(e.target.value))}>
            <option value="all">Whole season</option>
            {monthsPresent.map((m) => <option key={m} value={m}>{labels[m - 1] ?? m}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => setBreakdownMode((v) => !v)}
          title="Turn on, then click any row for its ticket-type split (type, share %, revenue, gift-aided/claimed)"
          style={{
            background: breakdownMode ? '#0b3d2e' : '#fff', color: breakdownMode ? '#fff' : '#0b3d2e',
            border: '1px solid #0b3d2e', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
          }}
        >Ticket breakdown{breakdownMode ? ' — click a row' : ''}</button>
      </div>
      {error && <p style={{ color: '#a33', fontSize: '0.85rem' }}>Couldn&apos;t load by-user data: {error}</p>}
      {!error && tableRows.length === 0 && (
        <div className="sales-empty">No by-user data yet — run <code>Refresh-GiftAid.ps1</code> on ONTRACK to populate <code>cache.GiftAidByUser</code>.</div>
      )}
      {tableRows.length > 0 && (
        <div className="svc-table-wrap" style={{ overflowX: 'auto' }}>
          <table className="svc-table">
            <thead>
              <tr>
                <th>{dim === 'user' ? 'User' : dim === 'outlet' ? 'Outlet' : 'Channel'}</th>
                <th className="ta-right">Conversations</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>CV LY</th>
                <th className="ta-right">GA tickets</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>GAT LY</th>
                <th className="ta-right">Eligible tickets</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>ET LY</th>
                <th className="ta-right">Eligible £</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>E£ LY</th>
                <th className="ta-right">Reclaim £ (25%)</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>R£ LY</th>
                <th className="ta-right">Claimed £</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>C£ LY</th>
                <th className="ta-right">Conversion %</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>Conv% LY</th>
                <th className="ta-right">Share of total %</th><th className="ta-right" style={{ color: LY_GREY, fontWeight: 600 }}>Share LY</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => {
                const isAdvance = r.key === ADVANCE_ROW_KEY
                return (
                <tr
                  key={r.key}
                  onClick={breakdownMode && !isAdvance ? () => setDetail({ key: r.key }) : undefined}
                  style={{
                    ...(r.key === 'Online' ? { background: '#f6f8f6' } : {}),
                    ...(isAdvance ? { fontStyle: 'italic', color: '#6b6b6b', background: '#faf7f0' } : {}),
                    ...(breakdownMode && !isAdvance ? { cursor: 'pointer' } : {}),
                  }}
                  title={breakdownMode && !isAdvance ? `Ticket breakdown for ${r.key}` : (isAdvance ? 'Estimated Gift Aid on advance bookings (channel-matched) — not attributable to a user' : undefined)}
                >
                  <td>{breakdownMode && !isAdvance ? <span style={{ textDecoration: 'underline dotted' }}>{r.key}</span> : r.key}</td>
                  {cells(r, priorByKey.get(r.key) ?? emptyAgg(r.key), total.agreed, priorTotal.agreed)}
                </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                {cells(total, priorTotal, total.agreed, priorTotal.agreed)}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {detail && season && (
        <GaTicketBreakdownModal
          season={season} dim={dim} k={detail.key} month={month}
          monthLabel={month === null ? 'whole season' : (labels[month - 1] ?? String(month))}
          onClose={() => setDetail(null)}
        />
      )}
    </section>
  )
}

interface GaTicketRow { ticket_type: string; tickets: number; ga_tickets: number; revenue: number }
interface GaTicketData { total: { tickets: number; ga_tickets: number; revenue: number }; rows: GaTicketRow[] }

function GaTicketBreakdownModal({ season, dim, k, month, monthLabel, onClose }: { season: string; dim: GaDim; k: string; month: number | null; monthLabel: string; onClose: () => void }) {
  const [data, setData] = useState<GaTicketData | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    const q = new URLSearchParams({ season, dim, key: k })
    if (month !== null) q.set('month', String(month))
    fetch(`/api/budget/gift-aid/by-user/ticket-breakdown?${q.toString()}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`) } return r.json() as Promise<GaTicketData> })
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
    return () => { cancelled = true }
  }, [season, dim, k, month])
  const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : '—')
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', width: 680, maxWidth: '94vw', maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <strong style={{ fontSize: '0.95rem' }}>Ticket breakdown — {k}</strong>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b6b6b' }}>×</button>
        </div>
        <p style={{ fontSize: '0.72rem', color: '#8a8a8a', margin: '0 0 8px' }}>
          Eligible admission tickets sold by {dim === 'user' ? 'this user' : dim === 'outlet' ? 'this outlet' : 'this channel'} ({monthLabel}), by ticket type.
          Share % = of their eligible tickets; Gift-aided = tickets on a Gift Aid declaration (claimed); Conversion % = gift-aided ÷ tickets of that type.
        </p>
        {error && <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>}
        {!data && !error && <p style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>Loading…</p>}
        {data && data.rows.length === 0 && <p style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>No ticket detail for this selection yet — populated by <code>Refresh-GiftAid.ps1</code>.</p>}
        {data && data.rows.length > 0 && (
          <table className="svc-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Ticket type</th>
                <th className="ta-right">Tickets</th>
                <th className="ta-right">Share %</th>
                <th className="ta-right">Revenue £</th>
                <th className="ta-right">Gift-aided (claimed)</th>
                <th className="ta-right">Conversion %</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.ticket_type}>
                  <td>{r.ticket_type}</td>
                  <td className="ta-right">{num0.format(r.tickets)}</td>
                  <td className="ta-right">{pct(r.tickets, data.total.tickets)}</td>
                  <td className="ta-right">{gbp0.format(Math.round(r.revenue))}</td>
                  <td className="ta-right">{r.ga_tickets ? num0.format(r.ga_tickets) : '—'}</td>
                  <td className="ta-right">{pct(r.ga_tickets, r.tickets)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td className="ta-right"><strong>{num0.format(data.total.tickets)}</strong></td>
                <td className="ta-right"><strong>100%</strong></td>
                <td className="ta-right"><strong>{gbp0.format(Math.round(data.total.revenue))}</strong></td>
                <td className="ta-right"><strong>{num0.format(data.total.ga_tickets)}</strong></td>
                <td className="ta-right"><strong>{pct(data.total.ga_tickets, data.total.tickets)}</strong></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  )
}

function BudgetDashboard({ me }: { me: MeResponse }) {
  const { seasons, loading: seasonsLoading, error: seasonsError } = useSeasons()
  const [activeYear, setActiveYear] = useState<string | null>(null)
  const [scope, setScope] = useState<BudgetScope>('merac')
  const [showActuals, setShowActuals] = useState(true)

  useEffect(() => {
    if (activeYear || seasons.length === 0) return
    const cur = seasons.find((s) => s.is_current) ?? seasons[0]
    setActiveYear(cur.year_label)
  }, [seasons, activeYear])

  const { data: summary, error: summaryError } = useBudgetSummary(activeYear)
  const { data: vsActual } = useBudgetVsActual(activeYear)
  const streams = useBudgetStreams(activeYear)
  const iplicitActual = useBudgetIplicitActual(activeYear)
  const { entries, save, remove } = useBudgetEntries(activeYear)
  const { reconciles, reconcile, unreconcile } = useBudgetReconciles(activeYear)

  const models = useMemo(() => buildStreamModels(summary, streams, entries, reconciles), [summary, streams, entries, reconciles])
  const monthLabels = summary?.month_labels ?? ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']
  const currentFm = useMemo(() => {
    if (!vsActual) return 12
    const idx = vsActual.months.findIndex((m) => m.year_month === vsActual.today_year_month)
    return idx >= 0 ? idx + 1 : 12
  }, [vsActual])

  if (!me.isBudget) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Budget — restricted</strong>
          <p>This area is limited to members of <code>SG-Reports-Budgets</code>. Ask Chris Flynn for access.</p>
        </div>
      </main>
    )
  }
  if (seasonsLoading) return <main className="app-main"><p>Loading…</p></main>
  if (seasonsError) return <main className="app-main"><p style={{ color: '#a33' }}>Couldn't load seasons: {seasonsError}</p></main>

  const noBudget = !!summary && summary.totals.all === 0

  return (
    <>
      <SeasonBar seasons={seasons} activeYear={activeYear} onChange={setActiveYear} />
      <main className="app-main">
        <div className="budget-toolbar">
          <div>
            <p className="budget-sub">Confidential — visible only to <code>SG-Reports-Budgets</code>. Edits are audited.</p>
          </div>
          <div className="bt-toggle-row">
            <button type="button" className={`trend-toggle ${showActuals ? 'active' : ''}`} onClick={() => setShowActuals((s) => !s)} aria-pressed={showActuals}
                    title="Show the actual row (MERAC pre-fill + your entries) under each income stream">Show actuals</button>
            <BudgetScopeToggle scope={scope} onChange={setScope} />
          </div>
        </div>
        {summaryError && (
          <div className="placeholder" style={{ borderColor: '#a33' }}>
            <strong>Couldn't load budget</strong>
            <p style={{ color: '#a33', fontSize: '0.85rem' }}>{summaryError}</p>
          </div>
        )}
        {noBudget ? (
          <div className="placeholder">
            <strong>No budget loaded for {activeYear}</strong>
            <p>The income budget is currently loaded for FY 2026/27 — select that season to view it.</p>
          </div>
        ) : (
          <>
            <BudgetKpiBar summary={summary} vsActual={vsActual} />
            <div className="dashboard-grid">
              <BudgetPaceChart models={models} currentFm={currentFm} monthLabels={monthLabels} />
              <BudgetTable models={models} monthLabels={monthLabels} currentFm={currentFm} showActuals={showActuals} onSave={save} onRemove={remove}
                trafficWalkup={streams?.traffic_walkup ?? []} eventsWalkup={streams?.events_walkup ?? {}}
                reconciles={reconciles} onReconcile={reconcile} onUnreconcile={unreconcile} iplicitActual={iplicitActual} season={activeYear} />
              <BudgetVsActualMonthlyChart vsActual={vsActual} scope={scope} />
              <BudgetDailyVsActualChart season={activeYear} scope={scope} />
            </div>
            <PnlTable season={activeYear} />
            <p className="dashboard-footer">
              Budget from <code>Income Budgets 2627.xlsx</code>; actual cells pre-fill from MERAC (daily refresh 06:00) and you can type the management-accounts figure or a forecast over any cell — manual entries show <span style={{ color: '#b22222', fontWeight: 600 }}>red</span> and are audited. The two charts below the table still use the like-for-like / full scope toggle.
            </p>
          </>
        )}
      </main>
    </>
  )
}

// ===========================================================================
//  Fundraising dashboard — Donorfy CRM. Gated on SG-Reports-Engagement
//  (me.isEngagement, same PII group as Engagement). Data: /api/fundraising/*
//  (api/src/functions/fundraising.ts), cache-only, refreshed nightly from
//  Donorfy. Empty until the first refresh runs.
// ===========================================================================

interface FrNamedAmount { name?: string; amount: number; count?: number; number?: number }
interface FrSummary {
  as_of: string | null
  season: { label: string; start: string; end: string } | null
  totals: { season_to_date: number; this_month: number; last_30_days: number; lifetime_total: number; gift_aid_claimed_season: number }
  counts: { donors_season: number; gifts_season: number; new_donors_month: number; active_rpis: number; rpi_monthly_value: number; rpi_annual_value: number }
  by_fund: FrNamedAmount[]
  by_product: FrNamedAmount[]
  by_campaign: FrNamedAmount[]
  ytd_by_month: { month_label: string; amount: number; prev_amount: number; count: number }[]
  top_donors: { month: FrNamedAmount[]; year: FrNamedAmount[]; lifetime: FrNamedAmount[] }
  recent_gifts: { date: string; name: string; amount: number; fund: string | null; method: string | null }[]
}
interface FrMovementRow {
  month_label: string
  opening_count: number; joiners_count: number; leavers_count: number; closing_count: number; net_count: number; net_pct: number
  opening_amount: number; joiners_amount: number; leavers_amount: number; closing_amount: number; net_amount: number
}
interface FrCancellation { rpi_number: number; donor_name: string | null; email: string | null; amount: number; frequency: string | null; cancelled_on: string; funds: string | null; campaigns: string | null }
interface FrPaymentFail { rpi_number: number; donor_name: string | null; email: string | null; amount: number; frequency: string | null; last_payment_date: string | null; status: string | null; missed_count: number; consecutive_missed: number }

const FR_GREEN = '#0b3d2e'
const FR_CAT_COLORS = ['#0b3d2e', '#1f6e54', '#3b6d11', '#639922', '#97c459', '#c89b3c']
const FR_TH_L: CSSProperties = { textAlign: 'left', padding: '5px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap' }
const FR_TH_R: CSSProperties = { ...FR_TH_L, textAlign: 'right' }
const FR_TD_L: CSSProperties = { padding: '5px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1', whiteSpace: 'nowrap' }
const FR_TD_R: CSSProperties = { ...FR_TD_L, textAlign: 'right' }
const FR_LOCK_BTN: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', fontWeight: 500, color: FR_GREEN, background: '#fff', border: `1px solid ${FR_GREEN}`, borderRadius: 6, padding: '3px 9px', cursor: 'pointer' }
const FR_OVERLAY: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
const FR_DIALOG: CSSProperties = { background: '#fff', borderRadius: 10, padding: '20px 22px', width: 320, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }
const FR_PIN_INPUT: CSSProperties = { width: '100%', padding: '8px 10px', fontSize: '1rem', border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box', letterSpacing: '0.25em' }
const FR_BTN_PRIMARY: CSSProperties = { background: FR_GREEN, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontWeight: 600, cursor: 'pointer' }
const FR_BTN_SECONDARY: CSSProperties = { background: '#fff', color: '#333', border: '1px solid #ccc', borderRadius: 6, padding: '7px 14px', cursor: 'pointer' }

function LockGlyph({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      {open ? <path d="M7 11V7a5 5 0 0 1 9.9-1" /> : <path d="M7 11V7a5 5 0 0 1 10 0v4" />}
    </svg>
  )
}

function frDateLabel(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function ExportXlsxLink({ report, label }: { report: string; label?: string }) {
  return (
    <a
      href={`/api/fundraising/export?report=${report}`}
      title="Download this table as an Excel workbook"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 500, color: FR_GREEN, textDecoration: 'none', border: `1px solid ${FR_GREEN}`, borderRadius: 6, padding: '3px 9px' }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      {label ?? 'Excel'}
    </a>
  )
}

function useFundraisingData(enabled: boolean) {
  const [summary, setSummary] = useState<FrSummary | null>(null)
  const [movement, setMovement] = useState<FrMovementRow[]>([])
  const [cancellations, setCancellations] = useState<FrCancellation[]>([])
  const [fails, setFails] = useState<FrPaymentFail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) { setLoading(false); return }
    let cancelled = false
    setLoading(true); setError(null)
    ;(async () => {
      try {
        const [sRes, mRes, cRes, fRes] = await Promise.all([
          fetch('/api/fundraising/summary'),
          fetch('/api/fundraising/movement?months=12'),
          fetch('/api/fundraising/cancellations?days=7'),
          fetch('/api/fundraising/payment-fails?limit=10'),
        ])
        for (const res of [sRes, mRes, cRes, fRes]) if (!res.ok) throw new Error(`${res.url} returned ${res.status}`)
        const s = (await sRes.json()) as FrSummary
        const m = (await mRes.json()) as { rows: FrMovementRow[] }
        const c = (await cRes.json()) as { rows: FrCancellation[] }
        const f = (await fRes.json()) as { rows: FrPaymentFail[] }
        if (cancelled) return
        setSummary(s); setMovement(m.rows ?? []); setCancellations(c.rows ?? []); setFails(f.rows ?? [])
      } catch (e) {
        if (!cancelled) setError(String((e as Error)?.message ?? e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [enabled])

  return { summary, movement, cancellations, fails, loading, error }
}

function FrTypeChart({ title, rows, cls }: { title: string; rows: FrNamedAmount[]; cls: string }) {
  const data = rows.slice(0, 8).map((r) => ({ name: r.name, amount: r.amount }))
  return (
    <ChartCard title={title} loading={false} error={null} empty={data.length === 0} className={cls}>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 30)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => gbpCompact(v as number)} />
          <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => gbp0.format(Number(v))} />
          <Bar dataKey="amount" name="Income">
            {data.map((_, i) => <Cell key={i} fill={FR_CAT_COLORS[i % FR_CAT_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function FrDonorTable({ title, rows, cls, showCount, lockable, locked, revealedNames, onToggle }: {
  title: string; rows: FrNamedAmount[]; cls: string; showCount?: boolean;
  lockable?: boolean; locked?: boolean; revealedNames?: (string | undefined)[] | null; onToggle?: () => void;
}) {
  const isLocked = !!lockable && !!locked
  return (
    <ChartCard title={title} loading={false} error={null} empty={rows.length === 0} className={cls}>
      {lockable && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            onClick={onToggle}
            title={isLocked ? 'Donor names hidden — click to enter PIN' : 'Donor names visible — click to hide'}
            style={FR_LOCK_BTN}
          >
            <LockGlyph open={!isLocked} />
            {isLocked ? 'Locked' : 'Unlocked'}
          </button>
        </div>
      )}
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={FR_TH_L}>#</th><th style={FR_TH_L}>Donor</th>{showCount && <th style={FR_TH_R}>Gifts</th>}<th style={FR_TH_R}>Amount</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={FR_TD_L}>{i + 1}</td>
                <td style={{ ...FR_TD_L, color: isLocked ? '#b0a89a' : undefined, letterSpacing: isLocked ? '0.15em' : undefined }}>
                  {isLocked ? '••••••' : (revealedNames?.[i] ?? r.name ?? '(unknown)')}
                </td>
                {showCount && <td style={FR_TD_R}>{num0.format(r.number ?? 0)}</td>}
                <td style={FR_TD_R}>{gbp0.format(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  )
}

function FundraisingDashboard({ me }: { me: MeResponse }) {
  const enabled = me.isEngagement
  const { summary, movement, cancellations, fails, loading, error } = useFundraisingData(enabled)

  const ytd = useMemo(() => summary?.ytd_by_month ?? [], [summary])
  const totals = summary?.totals ?? null
  const counts = summary?.counts ?? null

  // PIN-gated reveal of the season & lifetime donor names (names are not in the
  // summary payload; /api/fundraising/reveal serves them only on the right PIN).
  const [pinOpen, setPinOpen] = useState(false)
  const [pinValue, setPinValue] = useState('')
  const [pinErr, setPinErr] = useState<string | null>(null)
  const [pinBusy, setPinBusy] = useState(false)
  const [revealed, setRevealed] = useState<{ year: FrNamedAmount[]; lifetime: FrNamedAmount[] } | null>(null)

  async function submitPin() {
    setPinBusy(true); setPinErr(null)
    try {
      const res = await fetch(`/api/fundraising/reveal?pin=${encodeURIComponent(pinValue)}`)
      if (res.status === 403) { setPinErr('Incorrect PIN'); return }
      if (!res.ok) { setPinErr(`Error ${res.status}`); return }
      const d = (await res.json()) as { year: FrNamedAmount[]; lifetime: FrNamedAmount[] }
      setRevealed(d); setPinOpen(false); setPinValue('')
    } catch (e) { setPinErr(String((e as Error)?.message ?? e)) }
    finally { setPinBusy(false) }
  }
  const toggleReveal = () => { if (revealed) { setRevealed(null) } else { setPinErr(null); setPinValue(''); setPinOpen(true) } }

  if (!enabled) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Fundraising &mdash; restricted</strong>
          <p>This area is limited to members of <code>SG-Reports-Engagement</code>. Ask Chris Flynn for access.</p>
        </div>
      </main>
    )
  }

  const empty = !loading && !error && !summary?.recent_gifts?.length && movement.length === 0
  // Movement table metric rows (transposed: months across, metrics down — matches Donorfy's view).
  const countRows: [string, (r: FrMovementRow) => string][] = [
    ['Opening', (r) => num0.format(r.opening_count)],
    ['Joiners', (r) => num0.format(r.joiners_count)],
    ['Leavers', (r) => num0.format(r.leavers_count)],
    ['Closing', (r) => num0.format(r.closing_count)],
    ['Net gain/loss', (r) => (r.net_count > 0 ? '+' : '') + num0.format(r.net_count)],
    ['Net gain/loss %', (r) => `${r.net_pct.toFixed(2)}%`],
  ]
  const amountRows: [string, (r: FrMovementRow) => string][] = [
    ['Opening', (r) => gbp0.format(r.opening_amount)],
    ['Joiners', (r) => gbp0.format(r.joiners_amount)],
    ['Leavers', (r) => gbp0.format(r.leavers_amount)],
    ['Closing', (r) => gbp0.format(r.closing_amount)],
    ['Net', (r) => (r.net_amount > 0 ? '+' : '') + gbp0.format(r.net_amount)],
  ]

  return (
    <main className="app-main">
      {pinOpen && (
        <div style={FR_OVERLAY} onClick={() => setPinOpen(false)}>
          <div style={FR_DIALOG} onClick={(e) => e.stopPropagation()}>
            <strong style={{ fontSize: '0.95rem' }}>Enter PIN to reveal donor names</strong>
            <p style={{ fontSize: '0.8rem', color: '#6b6b6b', margin: '6px 0 10px' }}>Names on the season &amp; lifetime top-donor boards are hidden.</p>
            <input
              type="password" inputMode="text" autoFocus value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitPin() }}
              placeholder="PIN" style={FR_PIN_INPUT}
            />
            {pinErr && <p style={{ color: '#a33', fontSize: '0.8rem', margin: '8px 0 0' }}>{pinErr}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setPinOpen(false)} style={FR_BTN_SECONDARY}>Cancel</button>
              <button onClick={submitPin} disabled={pinBusy} style={FR_BTN_PRIMARY}>{pinBusy ? '…' : 'Reveal'}</button>
            </div>
          </div>
        </div>
      )}
      <div className="budget-toolbar">
        <div>
          <p className="budget-sub">
            Donorfy CRM &mdash; donations, regular giving &amp; donors. Visible only to <code>SG-Reports-Engagement</code>.
            {summary?.season ? ` Season ${summary.season.label}.` : ''}
            {summary?.as_of ? ` Data as of ${new Date(summary.as_of).toLocaleString('en-GB')}.` : ' Awaiting first refresh.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ExportXlsxLink report="all" label="Export all (.xlsx)" />
        </div>
      </div>

      {error && (
        <div className="placeholder" style={{ borderColor: '#a33' }}>
          <strong>Couldn't load fundraising data</strong>
          <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>
        </div>
      )}

      {empty ? (
        <div className="placeholder">
          <strong>Awaiting first refresh</strong>
          <p>The Donorfy cache is empty &mdash; figures appear after the first nightly refresh runs.</p>
        </div>
      ) : (
        <>
          <div className="kpi-bar">
            <KpiCard label="Total fundraising (season to date)" value={totals ? gbpCompact(totals.season_to_date) : '…'} loading={loading} />
            <KpiCard label="This month" value={totals ? gbpCompact(totals.this_month) : '…'} loading={loading} />
            <KpiCard label="Donors (season)" value={counts ? num0.format(counts.donors_season) : '…'} loading={loading} />
            <KpiCard label="New donors (month)" value={counts ? num0.format(counts.new_donors_month) : '…'} loading={loading} />
            <KpiCard label="Active regular gifts" value={counts ? num0.format(counts.active_rpis) : '…'} loading={loading} />
            <KpiCard label="Regular giving / month" value={counts ? gbpCompact(counts.rpi_monthly_value) : '…'} loading={loading} />
            <KpiCard label="Regular giving / year" value={counts ? gbpCompact(counts.rpi_annual_value) : '…'} loading={loading} />
            <KpiCard label="Gift Aid claimed (season)" value={totals ? gbpCompact(totals.gift_aid_claimed_season) : '…'} loading={loading} />
          </div>

          <div className="dashboard-grid">
            {/* Donations by month — this season vs last */}
            <ChartCard title="Donations by month (this season vs last)" loading={loading} error={error} empty={ytd.length === 0} className="grid-span-12">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={ytd} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => gbpCompact(Number(v))} />
                  <Tooltip formatter={(v) => gbp0.format(Number(v))} />
                  <Legend />
                  <Bar dataKey="prev_amount" name="Last season" fill="#c7c2b3" />
                  <Bar dataKey="amount" name="This season" fill={FR_GREEN} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Income by type */}
            <FrTypeChart title="Income by fund (season)" rows={summary?.by_fund ?? []} cls="grid-span-4" />
            <FrTypeChart title="Income by product (season)" rows={summary?.by_product ?? []} cls="grid-span-4" />
            <FrTypeChart title="Income by campaign (season)" rows={summary?.by_campaign ?? []} cls="grid-span-4" />

            {/* Regular Giving movement — the centrepiece */}
            <ChartCard title="Regular Giving movement (last 12 months)" loading={loading} error={error} empty={movement.length === 0} className="grid-span-12">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <ExportXlsxLink report="movement" label="Excel" />
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                  <thead>
                    <tr>
                      <th style={FR_TH_L}></th>
                      {movement.map((r, i) => <th key={i} style={FR_TH_R}>{r.month_label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ ...FR_TD_L, fontWeight: 600, color: FR_GREEN }} colSpan={movement.length + 1}>Counts</td></tr>
                    {countRows.map(([label, fn], ri) => (
                      <tr key={`c${ri}`}>
                        <td style={FR_TD_L}>{label}</td>
                        {movement.map((r, i) => <td key={i} style={FR_TD_R}>{fn(r)}</td>)}
                      </tr>
                    ))}
                    <tr><td style={{ ...FR_TD_L, fontWeight: 600, color: FR_GREEN }} colSpan={movement.length + 1}>Amounts (monthly value)</td></tr>
                    {amountRows.map(([label, fn], ri) => (
                      <tr key={`a${ri}`}>
                        <td style={FR_TD_L}>{label}</td>
                        {movement.map((r, i) => <td key={i} style={FR_TD_R}>{fn(r)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            {/* Top donors */}
            <FrDonorTable title="Top donors — this month" rows={summary?.top_donors.month ?? []} cls="grid-span-4" />
            <FrDonorTable title="Top donors — this season" rows={summary?.top_donors.year ?? []} cls="grid-span-4"
              lockable locked={!revealed} revealedNames={revealed ? revealed.year.map((r) => r.name) : null} onToggle={toggleReveal} />
            <FrDonorTable title="Top donors — lifetime" rows={summary?.top_donors.lifetime ?? []} cls="grid-span-4" showCount
              lockable locked={!revealed} revealedNames={revealed ? revealed.lifetime.map((r) => r.name) : null} onToggle={toggleReveal} />

            {/* Cancellations */}
            <ChartCard title="Cancelled regular gifts (last 7 days)" loading={loading} error={error} empty={cancellations.length === 0} className="grid-span-6">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <ExportXlsxLink report="cancellations" label="Excel (90d)" />
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={FR_TH_L}>Donor</th><th style={FR_TH_L}>Email</th><th style={FR_TH_R}>Amount</th><th style={FR_TH_R}>Cancelled</th></tr></thead>
                  <tbody>
                    {cancellations.map((r, i) => (
                      <tr key={i}>
                        <td style={FR_TD_L}>{r.donor_name ?? '—'}</td>
                        <td style={FR_TD_L}>{r.email ?? '—'}</td>
                        <td style={FR_TD_R}>{gbp0.format(r.amount)}{r.frequency ? `/${r.frequency.toLowerCase()}` : ''}</td>
                        <td style={FR_TD_R}>{frDateLabel(r.cancelled_on)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            {/* Payment fails */}
            <ChartCard title="Recent missed / failed recurring payments" loading={loading} error={error} empty={fails.length === 0} className="grid-span-6">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <ExportXlsxLink report="payment-fails" label="Excel" />
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={FR_TH_L}>Donor</th><th style={FR_TH_L}>Email</th><th style={FR_TH_R}>Amount</th><th style={FR_TH_R}>Missed</th><th style={FR_TH_R}>Last paid</th></tr></thead>
                  <tbody>
                    {fails.map((r, i) => (
                      <tr key={i}>
                        <td style={FR_TD_L}>{r.donor_name ?? '—'}</td>
                        <td style={FR_TD_L}>{r.email ?? '—'}</td>
                        <td style={FR_TD_R}>{gbp0.format(r.amount)}</td>
                        <td style={{ ...FR_TD_R, color: r.consecutive_missed >= 3 ? '#a32d2d' : undefined }}>{num0.format(r.consecutive_missed)}</td>
                        <td style={FR_TD_R}>{frDateLabel(r.last_payment_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            {/* Recent gifts */}
            <ChartCard title="Recent gifts" loading={loading} error={error} empty={(summary?.recent_gifts?.length ?? 0) === 0} className="grid-span-12">
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={FR_TH_L}>Date</th><th style={FR_TH_L}>Donor</th><th style={FR_TH_L}>Fund</th><th style={FR_TH_L}>Method</th><th style={FR_TH_R}>Amount</th></tr></thead>
                  <tbody>
                    {(summary?.recent_gifts ?? []).map((r, i) => (
                      <tr key={i}>
                        <td style={FR_TD_L}>{frDateLabel(r.date)}</td>
                        <td style={FR_TD_L}>{r.name}</td>
                        <td style={FR_TD_L}>{r.fund ?? '—'}</td>
                        <td style={FR_TD_L}>{r.method ?? '—'}</td>
                        <td style={FR_TD_R}>{gbp0.format(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>
        </>
      )}
    </main>
  )
}

// ===========================================================================
//  Engagement dashboard — gated on SG-Reports-Engagement (me.isEngagement).
//  Membership health (Ontrack MBR, via cache.Membership*) + Gift Aid.
//  Data: /api/engagement/* (api/src/functions/engagement.ts). Figures are
//  pre-shaped by the on-prem refresh (daily 06:00); the cache may be empty
//  until the first refresh runs, in which case the page shows "awaiting data".
// ===========================================================================

interface EngMonthly {
  MonthDate: string
  Members: number | null
  Memberships: number | null
  RenewedLastMonth: number | null
  NewMembershipsLastMonth: number | null
  NonRenewalRemovals: number | null
  CancellationRemovals: number | null
  RenewalsNowDue: number | null
  RenewalsOverdue: number | null
  RenewalFeesLastMonth: number | null
  NewMembershipFeesLastMonth: number | null
}
interface EngCategory { Category: string | null; MembershipCount: number; GiftAidCount: number }
interface EngPayment { PaymentMethod: string; MembershipCount: number }
interface EngCurrent {
  memberships: number
  members: number
  active_memberships: number
  active_members: number
  giftaid_members: number
  giftaid_pct: number
  new_last_month: number
  renewed_last_month: number
  renewals_now_due: number
  renewals_overdue: number
  as_of: string | null
}
interface EngSummary { monthly: EngMonthly[]; by_category: EngCategory[]; by_payment: EngPayment[]; current: EngCurrent }
interface EngRenewal {
  MembershipNumber: number | null
  MembersNames: string | null
  Category: string | null
  RenewalDate: string | null
  PaymentMethod: string | null
  DdInactive: boolean | null
}
interface EngLapsed { MembersNames: string | null; Category: string | null; LapsedDate: string | null; Reason: string | null }
interface EngGiftAid { season: string; claim: number; donation: number; lines: number; claimed_lines: number }

function engMonthLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}
function engDateLabel(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const ENG_GREEN = '#0b3d2e'
const ENG_GREEN_LIGHT = '#1f6e54'
const ENG_AMBER = '#b45309'
const ENG_CAT_COLORS = ['#0b3d2e', '#1f6e54', '#3b6d11', '#639922', '#97c459', '#c89b3c']
const ENG_TH: CSSProperties = { textAlign: 'left', padding: '5px 8px', color: '#6b6b6b', fontWeight: 500, fontSize: '0.78rem', borderBottom: '1px solid #e5e1d6', position: 'sticky', top: 0, background: '#fff' }
const ENG_TD: CSSProperties = { padding: '5px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f0ece1' }
// The three Moors Line List download buttons (All Records / Print Ed / Digital Ed) — plain links to the API, which streams the .xlsx
const ML_BTN: CSSProperties = { display: 'inline-block', fontSize: '0.74rem', fontWeight: 600, color: '#fff', background: '#1f6e54', padding: '3px 9px', borderRadius: 4, textDecoration: 'none', whiteSpace: 'nowrap', lineHeight: 1.4 }

function useEngagementData(enabled: boolean) {
  const [summary, setSummary] = useState<EngSummary | null>(null)
  const [renewals, setRenewals] = useState<EngRenewal[]>([])
  const [lapsed, setLapsed] = useState<EngLapsed[]>([])
  const [giftAid, setGiftAid] = useState<EngGiftAid | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) { setLoading(false); return }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const [sRes, rRes, lRes, gRes] = await Promise.all([
          fetch('/api/engagement/summary'),
          fetch('/api/engagement/renewals'),
          fetch('/api/engagement/lapsed'),
          fetch('/api/engagement/giftaid'),
        ])
        for (const res of [sRes, rRes, lRes, gRes]) {
          if (!res.ok) throw new Error(`${res.url} returned ${res.status}`)
        }
        const s = (await sRes.json()) as EngSummary
        const r = (await rRes.json()) as { rows: EngRenewal[] }
        const l = (await lRes.json()) as { rows: EngLapsed[] }
        const g = (await gRes.json()) as EngGiftAid
        if (cancelled) return
        setSummary(s)
        setRenewals(r.rows ?? [])
        setLapsed(l.rows ?? [])
        setGiftAid(g)
      } catch (e) {
        if (!cancelled) setError(String((e as Error)?.message ?? e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [enabled])

  return { summary, renewals, lapsed, giftAid, loading, error }
}

function EngagementDashboard({ me }: { me: MeResponse }) {
  const enabled = me.isEngagement
  const { summary, renewals, lapsed, giftAid, loading, error } = useEngagementData(enabled)

  const monthly = useMemo(() => summary?.monthly ?? [], [summary])
  const membersTrend = useMemo(
    () => monthly.map((m) => ({ label: engMonthLabel(m.MonthDate), members: Number(m.Members ?? 0) })),
    [monthly]
  )
  const flowTrend = useMemo(
    () => monthly.map((m) => ({
      label: engMonthLabel(m.MonthDate),
      joins: Number(m.NewMembershipsLastMonth ?? 0),
      lapses: Number(m.NonRenewalRemovals ?? 0) + Number(m.CancellationRemovals ?? 0),
    })),
    [monthly]
  )
  const incomeTrend = useMemo(
    () => monthly.map((m) => ({
      label: engMonthLabel(m.MonthDate),
      fees: Number(m.RenewalFeesLastMonth ?? 0) + Number(m.NewMembershipFeesLastMonth ?? 0),
    })),
    [monthly]
  )
  const dueTrend = useMemo(
    () => monthly.map((m) => ({
      label: engMonthLabel(m.MonthDate),
      due: Number(m.RenewalsNowDue ?? 0),
      overdue: Number(m.RenewalsOverdue ?? 0),
    })),
    [monthly]
  )
  const categories = useMemo(
    () => (summary?.by_category ?? []).slice(0, 10).map((c) => ({ name: c.Category ?? 'Unknown', count: Number(c.MembershipCount) })),
    [summary]
  )
  const cur = summary?.current ?? null

  if (!enabled) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Engagement &mdash; restricted</strong>
          <p>This area is limited to members of <code>SG-Reports-Engagement</code>. Ask Chris Flynn for access.</p>
        </div>
      </main>
    )
  }

  const empty = !loading && !error && monthly.length === 0 && categories.length === 0
  const giftAidPct = cur && cur.memberships > 0 ? `${Math.round(cur.giftaid_pct * 100)}%` : '—'
  const claimedPct = giftAid && giftAid.lines > 0 ? Math.round((giftAid.claimed_lines / giftAid.lines) * 100) : 0

  return (
    <main className="app-main">
      <div className="budget-toolbar">
        <div>
          <p className="budget-sub">Membership health &amp; Gift Aid &mdash; visible only to <code>SG-Reports-Engagement</code>.</p>
        </div>
      </div>

      {error && (
        <div className="placeholder" style={{ borderColor: '#a33' }}>
          <strong>Couldn't load engagement data</strong>
          <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>
        </div>
      )}

      {empty ? (
        <div className="placeholder">
          <strong>Awaiting first refresh</strong>
          <p>The membership cache is empty &mdash; figures appear after the next 06:00 refresh on the Ontrack server.</p>
        </div>
      ) : (
        <>
          <div className="kpi-bar">
            <KpiCard label="Current memberships" value={cur ? num0.format(cur.active_memberships) : '…'} loading={loading} />
            <KpiCard label="Current Members" value={cur ? num0.format(cur.active_members) : '…'} loading={loading} />
            <KpiCard label="New (last month)" value={cur ? num0.format(cur.new_last_month) : '…'} loading={loading} />
            <KpiCard label="Renewed (last month)" value={cur ? num0.format(cur.renewed_last_month) : '…'} loading={loading} />
            <KpiCard label="Renewals due now" value={cur ? num0.format(cur.renewals_now_due) : '…'} loading={loading} />
            <KpiCard label="Gift Aid uptake" value={giftAidPct} loading={loading} />
            <a
              className="kpi-card"
              href="/api/engagement/active-members"
              title="Download the Active Members name &amp; email list (.xlsx) - refreshed weekly"
              style={{ textDecoration: 'none', cursor: 'pointer', border: '1px solid #0b3d2e', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}
            >
              <span className="kpi-card__label" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0b3d2e' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Active Members
              </span>
              <span style={{ fontSize: '0.8rem', color: '#1f6e54', fontWeight: 500 }}>Download .xlsx</span>
            </a>
            <a
              className="kpi-card"
              href="/api/engagement/fee-income"
              title="Download the Membership Fee Income workbook (.xlsx) - fee income New vs Renewal by category, and current active members by category - refreshed nightly from Ontrack by the Sync Hub"
              style={{ textDecoration: 'none', cursor: 'pointer', border: '1px solid #0b3d2e', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}
            >
              <span className="kpi-card__label" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0b3d2e' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Fee Income
              </span>
              <span style={{ fontSize: '0.8rem', color: '#1f6e54', fontWeight: 500 }}>Download .xlsx</span>
            </a>
            <div
              className="kpi-card"
              title="Moors Line mailing list (.xlsx) - built from the reports cache, which the Sync Hub refreshes daily at 05:30 from Ontrack (MBR.MoorsLine_Mailing_Details). 'Not Required' never appears in the Print or Digital edition."
              style={{ border: '1px solid #0b3d2e', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}
            >
              <span className="kpi-card__label" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0b3d2e' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Moors Line List
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <a href="/api/engagement/moors-line?edition=all" style={ML_BTN} title="All Records - every row exactly as the Ontrack procedure produces it (.xlsx)">All Records</a>
                <a href="/api/engagement/moors-line?edition=print" style={ML_BTN} title="Print Ed - Despatch Option = Print only, one row per membership: the joint member sits in Name2 beside Name1 (.xlsx)">Print Ed</a>
                <a href="/api/engagement/moors-line?edition=digital" style={ML_BTN} title="Digital Ed - Despatch Option = Digital only (.xlsx)">Digital Ed</a>
              </div>
            </div>
            <a
              className="kpi-card"
              href="/api/engagement/agm-voting-papers"
              title="Download the AGM Voting Papers list (.xlsx) - every current member with the AGM Voting Papers field set, with name, address, postcode, email, telephone and their setting - refreshed daily from Ontrack"
              style={{ textDecoration: 'none', cursor: 'pointer', border: '1px solid #0b3d2e', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}
            >
              <span className="kpi-card__label" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0b3d2e' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                AGM Voting Papers
              </span>
              <span style={{ fontSize: '0.8rem', color: '#1f6e54', fontWeight: 500 }}>Download .xlsx</span>
            </a>
          </div>

          <div className="dashboard-grid">
            <ChartCard title="Members at start of month" loading={loading} error={error} empty={membersTrend.length === 0} className="grid-span-6">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={membersTrend} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={48} />
                  <Tooltip formatter={(v) => num0.format(Number(v))} />
                  <Line type="monotone" dataKey="members" name="Members" stroke={ENG_GREEN} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="New vs lapsed (per month)" loading={loading} error={error} empty={flowTrend.length === 0} className="grid-span-6">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={flowTrend} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} />
                  <Tooltip formatter={(v) => num0.format(Number(v))} />
                  <Legend />
                  <Bar dataKey="joins" name="New" fill={ENG_GREEN_LIGHT} />
                  <Bar dataKey="lapses" name="Lapsed" fill={ENG_AMBER} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Membership income (per month)" loading={loading} error={error} empty={incomeTrend.length === 0} className="grid-span-6">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={incomeTrend} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => gbpCompact(Number(v))} />
                  <Tooltip formatter={(v) => gbp0.format(Number(v))} />
                  <Bar dataKey="fees" name="Fees" fill={ENG_GREEN} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Renewals due &amp; overdue" loading={loading} error={error} empty={dueTrend.length === 0} className="grid-span-6">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dueTrend} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} />
                  <Tooltip formatter={(v) => num0.format(Number(v))} />
                  <Legend />
                  <Line type="monotone" dataKey="due" name="Due" stroke={ENG_GREEN_LIGHT} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="overdue" name="Overdue" stroke={ENG_AMBER} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Memberships by category" loading={loading} error={error} empty={categories.length === 0} className="grid-span-12">
              <ResponsiveContainer width="100%" height={Math.max(220, categories.length * 30)}>
                <BarChart data={categories} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => num0.format(Number(v))} />
                  <Bar dataKey="count" name="Memberships">
                    {categories.map((_, i) => (
                      <Cell key={i} fill={ENG_CAT_COLORS[i % ENG_CAT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Renewals due (next 90 days)" loading={loading} error={error} empty={renewals.length === 0} className="grid-span-6">
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={ENG_TH}>Member</th><th style={ENG_TH}>Category</th><th style={ENG_TH}>Due</th><th style={ENG_TH}>Payment</th></tr></thead>
                  <tbody>
                    {renewals.map((r, i) => (
                      <tr key={i}>
                        <td style={ENG_TD}>{r.MembersNames ?? '—'}</td>
                        <td style={ENG_TD}>{r.Category ?? '—'}</td>
                        <td style={ENG_TD}>{engDateLabel(r.RenewalDate)}</td>
                        <td style={{ ...ENG_TD, color: r.DdInactive ? '#a32d2d' : undefined }}>{r.DdInactive ? 'DD inactive' : (r.PaymentMethod ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            <ChartCard title="Recently lapsed" loading={loading} error={error} empty={lapsed.length === 0} className="grid-span-6">
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={ENG_TH}>Member</th><th style={ENG_TH}>Category</th><th style={ENG_TH}>Lapsed</th><th style={ENG_TH}>Reason</th></tr></thead>
                  <tbody>
                    {lapsed.map((r, i) => (
                      <tr key={i}>
                        <td style={ENG_TD}>{r.MembersNames ?? '—'}</td>
                        <td style={ENG_TD}>{r.Category ?? '—'}</td>
                        <td style={ENG_TD}>{engDateLabel(r.LapsedDate)}</td>
                        <td style={ENG_TD}>{r.Reason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            <section className="chart-card grid-span-12">
              <header className="chart-card__head">
                <h3 className="chart-card__title">Gift Aid (admissions){giftAid ? ` — ${shortFy(giftAid.season)}` : ''}</h3>
              </header>
              <div className="chart-card__body">
                <div className="kpi-bar">
                  <KpiCard label="Gift Aid claimed" value={giftAid ? gbpCompact(giftAid.claim) : '…'} loading={loading} />
                  <KpiCard label="Donations" value={giftAid ? gbpCompact(giftAid.donation) : '…'} loading={loading} />
                  <KpiCard label="Lines claimed" value={giftAid ? `${claimedPct}%` : '…'} loading={loading} />
                </div>
              </div>
            </section>
          </div>

          <p className="dashboard-footer">
            Membership trends &amp; flows come from Ontrack's month-start snapshot
            (<code>MBR.MembershipStatisticsAtStartOfMonth</code>); composition &amp; the action lists come from
            current membership records; Gift Aid is admissions Gift Aid from the daily sales cache. Refreshed daily at 06:00.
            {cur?.as_of ? ` Snapshot as of ${engDateLabel(cur.as_of)}.` : ''}
          </p>
        </>
      )}
    </main>
  )
}

function AdminPanel({ me }: { me: MeResponse }) {
  if (!me.isAdmin) {
    return (
      <main className="app-main">
        <h2>Access denied</h2>
        <p>You need the <code>admin</code> role to view this page.</p>
      </main>
    )
  }
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `admin-page-tab ${isActive ? 'active' : ''}`
  return (
    <main className="app-main">
      <nav className="admin-page-tabs" aria-label="Admin sections">
        <NavLink to="/admin/access"   className={tabClass}>Access</NavLink>
        <NavLink to="/admin/requests" className={tabClass}>Chart requests</NavLink>
        <NavLink to="/admin/security" className={tabClass}>Security</NavLink>
        <NavLink to="/admin/session-codes" className={tabClass}>Session codes</NavLink>
        <NavLink to="/admin/viewing" className={tabClass}>Viewing</NavLink>
      </nav>
      <Routes>
        <Route index            element={<Navigate to="access" replace />} />
        <Route path="access"     element={<AdminAccess me={me} />} />
        {/* Admin→Events editor removed from the nav — event bands now come from the
            canonical Sync Hub feed (app.MeracEventSnapshot), not hand-curated
            app.Events. Route kept (no nav link) so old bookmarks don't 404. */}
        <Route path="events"     element={<AdminEvents />} />
        <Route path="requests"   element={<AdminRequests />} />
        <Route path="security"   element={<AdminSecurity />} />
        {/* Session codes (db/110): the MERAC session-code registry behind the Pullman Bookings feed. */}
        <Route path="session-codes" element={<AdminSessionCodes />} />
        {/* Viewing (db/112): who looks at what, from app.PageViews. */}
        <Route path="viewing" element={<AdminViewing />} />
        <Route path="*"          element={<Navigate to="access" replace />} />
      </Routes>
    </main>
  )
}

function AdminSecurity() {
  const [pin, setPin] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const reveal = async () => {
    setBusy(true); setErr(null)
    try {
      const res = await fetch('/api/fundraising/pin')
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `returned ${res.status}`) }
      const d = (await res.json()) as { pin: string | null }
      setPin(d.pin ?? '(not configured)')
    } catch (e) {
      setErr(String((e as Error)?.message ?? e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <p className="admin-subtle" style={{ marginTop: 0 }}>
        Fundraising donor-name reveal PIN. This unlocks the hidden donor names on the Fundraising tab's
        season &amp; lifetime top-donor boards. Only <code>SG-Reports-Admins</code> can view it here.
      </p>
      {pin === null ? (
        <button type="button" className="btn-primary" onClick={reveal} disabled={busy}>
          {busy ? 'Revealing…' : 'Reveal Fundraising PIN'}
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', border: '1px solid #0b3d2e', borderRadius: 8, background: '#f6f8f6', maxWidth: 420 }}>
          <span style={{ color: '#6b6b6b', fontSize: '0.85rem' }}>Fundraising reveal PIN</span>
          <code style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.2em', color: '#0b3d2e' }}>{pin}</code>
          <button type="button" onClick={() => setPin(null)} style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #ccc', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>Hide</button>
        </div>
      )}
      {err && <p style={{ color: '#a33', marginTop: 10 }}>Couldn't reveal: {err}</p>}
    </>
  )
}

function AdminEvents() {
  const [refreshKey, setRefreshKey] = useState(0)
  const { rows, loading, error } = useEvents(refreshKey)
  const [editing, setEditing] = useState<EventRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [opError, setOpError] = useState<string | null>(null)

  const refresh = () => setRefreshKey((k) => k + 1)

  const onSave = async (input: EventInput, id: number | null) => {
    setBusy(true)
    setOpError(null)
    try {
      const url = id ? `/api/events/${id}` : '/api/events'
      const method = id ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `${method} ${url} returned ${res.status}`)
      }
      setEditing(null)
      setCreating(false)
      refresh()
    } catch (e) {
      setOpError(String((e as Error)?.message ?? e))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (ev: EventRow) => {
    if (!window.confirm(`Delete "${ev.name}"? This can't be undone.`)) return
    setBusy(true)
    setOpError(null)
    try {
      const res = await fetch(`/api/events/${ev.id}`, { method: 'DELETE' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `DELETE returned ${res.status}`)
      }
      refresh()
    } catch (e) {
      setOpError(String((e as Error)?.message ?? e))
    } finally {
      setBusy(false)
    }
  }

  const today = new Date().toISOString().slice(0, 10)
  const sorted = [...rows].sort((a, b) => a.start_date.localeCompare(b.start_date))

  return (
    <>
      <header className="events-admin__head">
        <p className="admin-subtle" style={{ marginTop: 0, marginBottom: 0 }}>
          Events drive the date-band overlays that will appear on time-series charts. Edit forecasts, costs, dates, colour and notes here. Changes take effect on the next dashboard load.
        </p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setCreating(true)}
          disabled={busy}
        >
          + Add event
        </button>
      </header>

      {opError && <p style={{ color: '#a33' }}>Couldn't save: {opError}</p>}
      {loading && <p>Loading events…</p>}
      {error && <p style={{ color: '#a33' }}>Couldn't load: {error}</p>}

      {!loading && !error && (
        <table className="admin-table events-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Dates</th>
              <th style={{ textAlign: 'right' }}>Forecast revenue</th>
              <th style={{ textAlign: 'right' }}>Forecast cost</th>
              <th style={{ textAlign: 'right' }}>Actual revenue</th>
              <th aria-label="Actions" style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((ev) => {
              const status =
                ev.end_date < today
                  ? 'past'
                  : ev.start_date <= today
                  ? 'ongoing'
                  : 'upcoming'
              return (
                <tr key={ev.id} data-status={status}>
                  <td>
                    <div className="event-row__name">
                      {ev.color && (
                        <span className="event-color-swatch" style={{ background: ev.color }} title={ev.color} />
                      )}
                      <span style={{ fontWeight: 600 }}>{ev.name}</span>
                      <span className={`event-status-pill event-status-pill--${status}`}>{status}</span>
                    </div>
                    {ev.notes && (
                      <div className="event-row__notes-preview" title={ev.notes}>{ev.notes}</div>
                    )}
                  </td>
                  <td>{formatEventDateRange(ev.start_date, ev.end_date)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {ev.forecast_revenue !== null ? gbp0.format(ev.forecast_revenue) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {ev.forecast_cost !== null ? gbp0.format(ev.forecast_cost) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {ev.actual_revenue !== null ? gbp0.format(ev.actual_revenue) : '—'}
                  </td>
                  <td className="event-row__actions">
                    <button type="button" className="btn-link" onClick={() => setEditing(ev)} disabled={busy}>Edit</button>
                    <button type="button" className="btn-link btn-link--danger" onClick={() => onDelete(ev)} disabled={busy}>Delete</button>
                  </td>
                </tr>
              )
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
                No events yet. Click <strong>Add event</strong> to create one.
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {(editing || creating) && (
        <EventModal
          initial={editing}
          busy={busy}
          onCancel={() => { setEditing(null); setCreating(false); setOpError(null) }}
          onSave={(input) => onSave(input, editing?.id ?? null)}
        />
      )}
    </>
  )
}

function EventModal({
  initial,
  busy,
  onCancel,
  onSave,
}: {
  initial: EventRow | null
  busy: boolean
  onCancel: () => void
  onSave: (input: EventInput) => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [startDate, setStartDate] = useState(initial?.start_date ?? '')
  const [endDate, setEndDate] = useState(initial?.end_date ?? '')
  const [forecastRevenue, setForecastRevenue] = useState<string>(
    initial?.forecast_revenue != null ? String(initial.forecast_revenue) : ''
  )
  const [forecastCost, setForecastCost] = useState<string>(
    initial?.forecast_cost != null ? String(initial.forecast_cost) : ''
  )
  const [actualRevenue, setActualRevenue] = useState<string>(
    initial?.actual_revenue != null ? String(initial.actual_revenue) : ''
  )
  const [color, setColor] = useState<string>(initial?.color ?? '#0b3d2e')
  const [colorEnabled, setColorEnabled] = useState<boolean>(initial?.color != null)
  const [notes, setNotes] = useState<string>(initial?.notes ?? '')
  const [formError, setFormError] = useState<string | null>(null)

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const parseMoney = (raw: string): number | null | 'invalid' => {
    const t = raw.trim()
    if (t === '') return null
    const n = Number(t)
    if (!Number.isFinite(n) || n < 0 || n > 9_999_999_999) return 'invalid'
    return n
  }

  const submit = () => {
    setFormError(null)
    if (!name.trim()) { setFormError('Name is required'); return }
    if (name.length > 200) { setFormError('Name must be 200 chars or less'); return }
    if (!startDate) { setFormError('Start date is required'); return }
    if (!endDate) { setFormError('End date is required'); return }
    if (endDate < startDate) { setFormError('End date must be on or after start date'); return }
    const fr = parseMoney(forecastRevenue)
    const fc = parseMoney(forecastCost)
    const ar = parseMoney(actualRevenue)
    if (fr === 'invalid') { setFormError('Forecast revenue must be a non-negative number'); return }
    if (fc === 'invalid') { setFormError('Forecast cost must be a non-negative number'); return }
    if (ar === 'invalid') { setFormError('Actual revenue must be a non-negative number'); return }
    if (notes.length > 4000) { setFormError('Notes must be 4000 chars or less'); return }
    onSave({
      name: name.trim(),
      start_date: startDate,
      end_date: endDate,
      forecast_revenue: fr as number | null,
      forecast_cost: fc as number | null,
      actual_revenue: ar as number | null,
      color: colorEnabled ? color : null,
      notes: notes.trim() === '' ? null : notes,
    })
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel() }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
        <header className="modal__head">
          <h3 id="event-modal-title" style={{ margin: 0 }}>
            {initial ? `Edit “${initial.name}”` : 'Add new event'}
          </h3>
          <button type="button" className="modal__close" aria-label="Close" onClick={onCancel} disabled={busy}>×</button>
        </header>
        <div className="modal__body event-form">
          <label>
            <span>Name</span>
            <input
              type="text" value={name} maxLength={200} required
              onChange={(e) => setName(e.target.value)} disabled={busy}
              autoFocus
            />
          </label>
          <div className="form-row">
            <label>
              <span>Start date</span>
              <input type="date" value={startDate} required
                     onChange={(e) => setStartDate(e.target.value)} disabled={busy} />
            </label>
            <label>
              <span>End date</span>
              <input type="date" value={endDate} required
                     onChange={(e) => setEndDate(e.target.value)} disabled={busy} />
            </label>
          </div>
          <div className="form-row form-row--three">
            <label>
              <span>Forecast revenue (£)</span>
              <input type="number" min="0" step="0.01" value={forecastRevenue}
                     onChange={(e) => setForecastRevenue(e.target.value)} disabled={busy}
                     placeholder="e.g. 145000" />
            </label>
            <label>
              <span>Forecast cost (£)</span>
              <input type="number" min="0" step="0.01" value={forecastCost}
                     onChange={(e) => setForecastCost(e.target.value)} disabled={busy} />
            </label>
            <label>
              <span>Actual revenue (£)</span>
              <input type="number" min="0" step="0.01" value={actualRevenue}
                     onChange={(e) => setActualRevenue(e.target.value)} disabled={busy} />
            </label>
          </div>
          <div className="event-form__color">
            <span className="event-form__label">Colour</span>
            <label className="event-form__color-toggle">
              <input
                type="checkbox" checked={colorEnabled}
                onChange={(e) => setColorEnabled(e.target.checked)} disabled={busy}
              />
              <span>Use a colour</span>
            </label>
            <input
              type="color" value={color}
              onChange={(e) => setColor(e.target.value)}
              disabled={busy || !colorEnabled}
              aria-label="Event colour"
            />
            <code style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>
              {colorEnabled ? color : '(no colour)'}
            </code>
          </div>
          <label>
            <span>Notes</span>
            <textarea
              value={notes} maxLength={4000} rows={3}
              onChange={(e) => setNotes(e.target.value)} disabled={busy}
              placeholder="Optional context — confirmed running dates, programme caveats, etc."
            />
          </label>
          {formError && <p className="event-form__error">{formError}</p>}
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : (initial ? 'Save changes' : 'Create event')}
          </button>
        </div>
      </div>
    </div>
  )
}

function AdminAccess({ me }: { me: MeResponse }) {
  const [data, setData] = useState<AdminUsersResponse | null>(null)
  const [disabledData, setDisabledData] = useState<{ users: DisabledUserRow[]; log: DisabledLogRow[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'admin' | 'power' | 'standard' | 'budget' | 'engagement' | 'events' | 'exec' | 'disabled'>('all')
  // Disable / Enable (db/114): a two-step inline confirm — no browser prompts — one at a time.
  const [confirming, setConfirming] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!me.isAdmin) { setLoading(false); return }
    try {
      const [res, dres] = await Promise.all([fetch('/api/access/users'), fetch('/api/access/disabled')])
      if (!res.ok) throw new Error(`/api/access/users returned ${res.status}`)
      setData((await res.json()) as AdminUsersResponse)
      if (dres.ok) setDisabledData((await dres.json()) as { users: DisabledUserRow[]; log: DisabledLogRow[] })
      setError(null)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [me.isAdmin])
  useEffect(() => { void load() }, [load])

  const act = async (action: 'disable' | 'enable', u: { upn: string; name?: string | null; groups?: string[] }) => {
    setBusy(true); setActionError(null)
    try {
      const body = action === 'disable' ? { upn: u.upn, name: u.name ?? null, groups: u.groups ?? [], reason: reason.trim() || null } : { upn: u.upn }
      const res = await fetch(`/api/access/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const j = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) throw new Error(j.error || `Request failed (${res.status})`)
      setConfirming(null); setReason('')
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  const fmtWhen = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
  const isMe = (upn: string) => upn.toLowerCase() === me.upn.toLowerCase()

  // Parent (AdminPanel) already enforces the admin gate, but be defensive.
  if (!me.isAdmin) {
    return (
      <>
        <h3>Access denied</h3>
        <p>You need the <code>admin</code> role to view this page.</p>
      </>
    )
  }

  const users = data?.users ?? []
  const disabledUsers = disabledData?.users ?? []
  const visible =
    filter === 'all'          ? users
    : filter === 'budget'     ? users.filter((u) => u.groups.includes('SG-Reports-Budgets'))
    : filter === 'engagement' ? users.filter((u) => u.groups.includes('SG-Reports-Engagement'))
    : filter === 'events'     ? users.filter((u) => u.groups.includes('SG-Reports-Events'))
    : filter === 'exec'       ? users.filter((u) => u.groups.includes('SG-Exec-Reports'))
    : filter === 'disabled'   ? []
    : users.filter((u) => u.role === filter)
  const counts = {
    all: users.length,
    admin:    users.filter((u) => u.role === 'admin').length,
    power:    users.filter((u) => u.role === 'power').length,
    standard: users.filter((u) => u.role === 'standard').length,
    budget:   users.filter((u) => u.groups.includes('SG-Reports-Budgets')).length,
    engagement: users.filter((u) => u.groups.includes('SG-Reports-Engagement')).length,
    events:   users.filter((u) => u.groups.includes('SG-Reports-Events')).length,
    exec:     users.filter((u) => u.groups.includes('SG-Exec-Reports')).length,
    disabled: disabledUsers.length,
  }

  const filterTabs: { key: typeof filter; label: string; count: number }[] = [
    { key: 'all',      label: 'All',      count: counts.all },
    { key: 'admin',    label: 'Admins',   count: counts.admin },
    { key: 'power',    label: 'Power',    count: counts.power },
    { key: 'standard', label: 'Standard', count: counts.standard },
    { key: 'budget',   label: 'Budget',   count: counts.budget },
    { key: 'engagement', label: 'Engagement', count: counts.engagement },
    { key: 'events',   label: 'Events',   count: counts.events },
    { key: 'exec',     label: 'Exec',     count: counts.exec },
    { key: 'disabled', label: 'Disabled Users', count: counts.disabled },
  ]

  // The Enable control, shared by the role tables and the Disabled Users table.
  const enableControl = (upn: string) => (
    confirming === upn ? (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <button type="button" className="btn-link" onClick={() => void act('enable', { upn })} disabled={busy}>{busy ? 'Enabling…' : 'Confirm enable'}</button>
        <button type="button" className="btn-link" onClick={() => setConfirming(null)} disabled={busy}>Cancel</button>
      </span>
    ) : (
      <button type="button" className="btn-link" onClick={() => { setConfirming(upn); setActionError(null) }} disabled={busy}>Enable</button>
    )
  )

  return (
    <>
      <p className="admin-subtle" style={{ marginTop: 0 }}>
        Live members of <code>SG-Reports-Admins</code>, <code>SG-Reports-Power</code>, and <code>SG-Reports-Users</code> from Entra, plus the orthogonal <code>SG-Reports-Budgets</code>, <code>SG-Reports-Engagement</code>, <code>SG-Reports-Events</code> and <code>SG-Exec-Reports</code> capability groups (shown as a pill on each member who holds them; <strong>not</strong> role tiers, so admins are not auto-granted them). Nested groups (Senior / Wider Management Team) are flattened to the people inside them.
        <strong> Disable</strong> stops a user signing in to Reports without touching Entra or deleting anything — they keep their groups and their history (page views, changes, digests) and are listed under <strong>Disabled Users</strong>, where <strong>Enable</strong> restores them at once.
      </p>

      {loading && <p>Loading users…</p>}
      {error && <p style={{ color: '#a33' }}>Couldn't load: {error}</p>}
      {actionError && <p style={{ color: '#a33' }}>Couldn't update: {actionError}</p>}

      {data && (
        <>
          <div className="admin-cards">
            {data.groups.map((g) => (
              <div key={g.id} className="admin-card" data-role={g.role}>
                <div className="admin-card__role">{g.role}</div>
                <div className="admin-card__name">{g.name}</div>
                <div className="admin-card__count">{g.member_count} {g.member_count === 1 ? 'member' : 'members'}</div>
              </div>
            ))}
          </div>

          <nav className="admin-tabs" aria-label="Filter by role">
            {filterTabs.map((t) => (
              <button
                key={t.key}
                className={`admin-tab ${filter === t.key ? 'active' : ''}`}
                onClick={() => { setFilter(t.key); setConfirming(null) }}
                type="button"
              >
                {t.label} <span className="admin-tab__count">{t.count}</span>
              </button>
            ))}
          </nav>

          {filter === 'disabled' ? (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>UPN</th>
                    <th>Groups when disabled</th>
                    <th>Disabled</th>
                    <th>Reason</th>
                    <th aria-label="Actions" style={{ width: 1 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {disabledUsers.map((d) => (
                    <tr key={d.upn}>
                      <td><div style={{ fontWeight: 600 }}>{d.name ?? d.upn}</div></td>
                      <td><code className="admin-upn">{d.upn}</code></td>
                      <td>
                        {d.groups.map((g) => (
                          <span key={g} className="admin-grouplet">{g === 'SG-Exec-Reports' ? 'Exec' : g.replace('SG-Reports-', '')}</span>
                        ))}
                        {d.groups.length === 0 && <span className="admin-subtle">—</span>}
                      </td>
                      <td>{fmtWhen(d.disabled_at)}<div className="admin-subtle">by {d.disabled_by}</div></td>
                      <td>{d.reason ?? <span className="admin-subtle">—</span>}</td>
                      <td className="event-row__actions">{enableControl(d.upn)}</td>
                    </tr>
                  ))}
                  {disabledUsers.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
                      No users are disabled.
                    </td></tr>
                  )}
                </tbody>
              </table>
              {(disabledData?.log.length ?? 0) > 0 && (
                <>
                  <h4 style={{ marginBottom: '0.4rem' }}>History</h4>
                  <ul className="admin-subtle" style={{ margin: 0, paddingLeft: '1.1rem' }}>
                    {disabledData!.log.slice(0, 25).map((l) => (
                      <li key={l.id}>{fmtWhen(l.at)} — <strong>{l.action === 'disable' ? 'Disabled' : 'Enabled'}</strong> <code className="admin-upn">{l.upn}</code> by {l.by}{l.reason ? ` — ${l.reason}` : ''}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>UPN</th>
                  <th>Role</th>
                  <th>Groups</th>
                  <th>Status</th>
                  <th aria-label="Actions" style={{ width: 1 }}></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr key={u.id} style={u.disabled ? { opacity: 0.6 } : undefined}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.name}</div>
                      {u.job_title && <div className="admin-subtle">{u.job_title}</div>}
                    </td>
                    <td><code className="admin-upn">{u.upn}</code></td>
                    <td><span className="role-pill" data-role={u.role}>{u.role}</span></td>
                    <td>
                      {/* SG-Exec-Reports breaks the SG-Reports-* naming, so the
                          plain prefix strip would leave it unshortened. */}
                      {u.groups.map((g) => (
                        <span key={g} className="admin-grouplet">
                          {g === 'SG-Exec-Reports' ? 'Exec' : g.replace('SG-Reports-', '')}
                        </span>
                      ))}
                    </td>
                    <td>
                      {u.disabled
                        ? <span style={{ color: '#a33', fontWeight: 600 }} title={`Disabled ${fmtWhen(u.disabled.at)} by ${u.disabled.by}${u.disabled.reason ? ` — ${u.disabled.reason}` : ''}`}>Disabled</span>
                        : u.account_enabled ? 'Active' : <span style={{ color: '#a33' }}>Entra account disabled</span>}
                    </td>
                    <td className="event-row__actions">
                      {u.disabled ? enableControl(u.upn)
                        : isMe(u.upn) ? <span className="admin-subtle">you</span>
                        : confirming === u.upn ? (
                          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" maxLength={400} disabled={busy} style={{ fontSize: '0.8rem', padding: '2px 6px', minWidth: 170 }} />
                            <button type="button" className="btn-link btn-link--danger" onClick={() => void act('disable', u)} disabled={busy}>{busy ? 'Disabling…' : 'Confirm disable'}</button>
                            <button type="button" className="btn-link" onClick={() => { setConfirming(null); setReason('') }} disabled={busy}>Cancel</button>
                          </span>
                        ) : (
                          <button type="button" className="btn-link btn-link--danger" onClick={() => { setConfirming(u.upn); setReason(''); setActionError(null) }} disabled={busy}>Disable</button>
                        )}
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
                    No users match this filter.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}

          <p className="admin-subtle" style={{ marginTop: '1.5rem' }}>
            To add or remove users, use the Entra admin centre or ask Chris. Components, dashboards, and seasons management will be added to this page later.
          </p>
        </>
      )}
    </>
  )
}

function AdminRequests() {
  const { requests, loading, error, busy, patch, refresh } = useChartRequests(true)
  const [editing, setEditing] = useState<ChartRequest | null>(null)
  const [opError, setOpError] = useState<string | null>(null)

  const onQuickStatus = async (r: ChartRequest, status: ChartRequestStatus) => {
    setOpError(null)
    const result = await patch(r.id, { status })
    if (!result.ok) setOpError(result.error)
  }

  if (loading) return <p>Loading requests…</p>
  if (error) return <p style={{ color: '#a33' }}>Couldn't load: {error}</p>

  return (
    <>
      <p className="admin-subtle" style={{ marginTop: 0 }}>
        Custom-chart requests submitted by users. Use the buttons to move a request through its lifecycle. Marking a request <strong>fulfilled</strong> requires picking the component_key the user should now see on their dashboard — they'll get an Add to dashboard prompt the next time they visit My Dashboard.
      </p>

      {opError && <p style={{ color: '#a33' }}>Couldn't update: {opError}</p>}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Title</th>
            <th>Requester</th>
            <th>Created</th>
            <th>Skill</th>
            <th aria-label="Actions" style={{ width: 1 }}></th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id}>
              <td>
                <span className={`request-status-pill request-status-pill--${r.status}`}>
                  {r.status.replace('_', ' ')}
                </span>
              </td>
              <td>
                <div style={{ fontWeight: 600 }}>{r.title}</div>
                {r.description && (
                  <div className="event-row__notes-preview" title={r.description}>{r.description}</div>
                )}
                {r.fulfilled_component_key && (
                  <div className="admin-subtle" style={{ marginTop: '0.25rem' }}>
                    fulfilled with <code>{r.fulfilled_component_key}</code>
                  </div>
                )}
                {r.admin_notes && (
                  <div className="admin-subtle" style={{ marginTop: '0.25rem' }}>note: {r.admin_notes}</div>
                )}
              </td>
              <td><code className="admin-upn">{r.upn}</code></td>
              <td>{new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
              <td>
                {r.skill_file_blob_uri ? (
                  <a href={`/api/chart-requests/${r.id}/skill-file`} className="btn-link" download>Download</a>
                ) : (
                  <span className="admin-subtle">—</span>
                )}
              </td>
              <td className="event-row__actions">
                {r.status === 'pending' && (
                  <button type="button" className="btn-link" onClick={() => onQuickStatus(r, 'in_progress')} disabled={busy}>Start</button>
                )}
                {(r.status === 'pending' || r.status === 'in_progress') && (
                  <button type="button" className="btn-link" onClick={() => setEditing(r)} disabled={busy}>Fulfil…</button>
                )}
                {(r.status === 'pending' || r.status === 'in_progress') && (
                  <button type="button" className="btn-link btn-link--danger" onClick={() => setEditing(r)} disabled={busy}>Decline…</button>
                )}
                {(r.status === 'fulfilled' || r.status === 'declined') && (
                  <button type="button" className="btn-link" onClick={() => setEditing(r)} disabled={busy}>Edit</button>
                )}
              </td>
            </tr>
          ))}
          {requests.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>
              No chart requests yet.
            </td></tr>
          )}
        </tbody>
      </table>

      {editing && (
        <AdminRequestModal
          req={editing}
          busy={busy}
          onCancel={() => { setEditing(null); setOpError(null) }}
          onSave={async (body) => {
            const result = await patch(editing.id, body)
            if (result.ok) {
              setEditing(null)
              refresh()
            } else {
              setOpError(result.error)
            }
          }}
        />
      )}
    </>
  )
}

// Modal for admin to update a chart request's status, fulfilled component,
// or admin_notes. The fulfilled-component dropdown is populated from the
// catalog, so admins can only pick a key that actually exists.
function AdminRequestModal({
  req,
  busy,
  onCancel,
  onSave,
}: {
  req: ChartRequest
  busy: boolean
  onCancel: () => void
  onSave: (body: { status?: ChartRequestStatus; fulfilled_component_key?: string | null; admin_notes?: string | null }) => Promise<void>
}) {
  const { catalog } = useComponentsCatalog(true)
  const [status, setStatus] = useState<ChartRequestStatus>(req.status)
  const [fulfilledKey, setFulfilledKey] = useState<string>(req.fulfilled_component_key ?? '')
  const [adminNotes, setAdminNotes] = useState<string>(req.admin_notes ?? '')
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const submit = async () => {
    setFormError(null)
    if (status === 'fulfilled' && !fulfilledKey) {
      setFormError("Pick a component to fulfil this request with.")
      return
    }
    if (adminNotes.length > 4000) {
      setFormError('Admin notes must be 4000 chars or fewer')
      return
    }
    await onSave({
      status,
      fulfilled_component_key: status === 'fulfilled' ? fulfilledKey : null,
      admin_notes: adminNotes.trim() === '' ? null : adminNotes.trim(),
    })
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel() }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="adm-req-title">
        <header className="modal__head">
          <h3 id="adm-req-title" style={{ margin: 0 }}>Update request: “{req.title}”</h3>
          <button type="button" className="modal__close" aria-label="Close" onClick={onCancel} disabled={busy}>×</button>
        </header>
        <div className="modal__body event-form">
          <p className="admin-subtle" style={{ margin: 0 }}>
            Requested by <code>{req.upn}</code> on {new Date(req.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.
          </p>
          {req.description && (
            <div>
              <span className="event-form__label">Their description</span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>{req.description}</p>
            </div>
          )}
          <label>
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as ChartRequestStatus)} disabled={busy}>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="declined">Declined</option>
            </select>
          </label>
          {status === 'fulfilled' && (
            <label>
              <span>Fulfil with component</span>
              <select value={fulfilledKey} onChange={(e) => setFulfilledKey(e.target.value)} disabled={busy}>
                <option value="">— pick a component —</option>
                {catalog.map((c) => (
                  <option key={c.key} value={c.key}>{c.name} ({c.key})</option>
                ))}
              </select>
            </label>
          )}
          <label>
            <span>Admin notes (visible to the requester)</span>
            <textarea
              value={adminNotes} maxLength={4000} rows={3}
              placeholder="e.g. ‘Decline reason’, or ‘Released as the new Pullman occupancy chart’"
              onChange={(e) => setAdminNotes(e.target.value)} disabled={busy}
            />
          </label>
          {formError && <p className="event-form__error">{formError}</p>}
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving\u2026' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function NoAccess({ me }: { me: MeResponse }) {
  if (me.disabled) {
    return (
      <main className="app-main">
        <h2>Your access to NYMR Reports has been disabled.</h2>
        <p>
          Your account (<code>{me.upn}</code>) has been disabled by a Reports administrator.
          Contact Chris Flynn or Jason Plenty if you think this is a mistake.
        </p>
        <p style={{ marginTop: '1rem' }}>
          <a href="/logout">Sign out</a>
        </p>
      </main>
    )
  }
  return (
    <main className="app-main">
      <h2>You're signed in, but you don't have access yet.</h2>
      <p>
        Your account (<code>{me.upn}</code>) is signed in, but isn't a member of
        <code> SG-Reports-Admins</code>, <code>SG-Reports-Power</code> or
        <code> SG-Reports-Users</code>. Contact Chris Flynn or Jason Plenty to be added.
      </p>
      <p style={{ marginTop: '1rem' }}>
        <a href="/logout">Sign out</a>
      </p>
    </main>
  )
}

// Page-view logging for Admin -> Viewing (db/112): one post per route change
// once the user is signed in. Sub-views (Customer Service pills) report
// themselves via reportPageView(path, view).
function PageViewTracker({ pathname }: { pathname: string }) {
  useEffect(() => {
    if (pathname.startsWith('/customer-service')) return // the screen reports its own sub-view
    reportPageView(pathname)
  }, [pathname])
  return null
}

export default function App() {
  const { me, loading, error } = useCurrentUser()
  const authed = me?.role !== null && me?.role !== undefined
  const myDash = useMyDashboard(Boolean(authed))
  const nav = useNavPrefs(authed ? me?.upn ?? null : null)
  const location = useLocation()

  // Pullman Live (/pullman-live, formerly /pullman-offline) is a full-screen, immersive, OFFLINE-FIRST on-board tab. It
  // must render even with no signal — BEFORE the online identity gate below —
  // because on a moving train /.auth/me and /api/me are unreachable, so `me`
  // stays null and the "Loading…" gate would otherwise trap the crew on a dead
  // screen despite the whole service being cached in IndexedDB. It only needs
  // me.upn for a cosmetic label; crew sign-in is via app.PullmanCrew. Placed
  // after all hooks above so hook order stays stable across renders.
  if (location.pathname.startsWith('/pullman-live') || location.pathname.startsWith('/pullman-offline')) {
    return (
      <Suspense fallback={<main className="app-main"><p>Loading…</p></main>}>
        <PullmanOffline me={{ upn: me?.upn ?? null }} />
      </Suspense>
    )
  }

  if (loading || !me) {
    return (
      <main className="app-main">
        <p>Loading…</p>
        {error && <p style={{ color: '#a33' }}>Error: {error}</p>}
      </main>
    )
  }

  if (me.role === null) {
    return (
      <>
        <Header me={me} />
        <NoAccess me={me} />
      </>
    )
  }

  const tracker = <PageViewTracker pathname={location.pathname} />

  // '/' is a landing route only — once the pin count is known it redirects to a
  // canonical URL: My Dashboard if the user has pins, otherwise Dashboard One.
  // Redirecting (rather than rendering Dashboard One inline) keeps the nav pill
  // highlighting honest, since Dashboard One now lives at '/dashboard-one'.
  // Dashboard One is rendered directly while the pin count loads so there's no
  // blank flash.
  // A "1st" page chosen in the Page Selector wins, while the user can still
  // see it (a security-group change simply falls back).
  const firstPage = nav.prefs.first_page ? pageByPath(nav.prefs.first_page) : undefined
  const indexElement = firstPage && firstPage.visible(me)
    ? <Navigate to={firstPage.to} replace />
    : myDash.loading || !nav.loaded
    ? <DashboardOne me={me} />
    : myDash.pins.length > 0
      ? <Navigate to="/my-dashboard" replace />
      : <Navigate to="/dashboard-one" replace />

  return (
    <>
      <Header me={me} nav={nav} />
      {!nav.prefs.flip_page_bar && <TopNav me={me} />}
      {tracker}
      <Routes>
        <Route path="/"              element={indexElement} />
        {/* Dashboard One's own canonical route, so it stays reachable even when
            '/' redirects to My Dashboard. */}
        <Route path="/dashboard-one" element={<DashboardOne me={me} />} />
        <Route path="/my-dashboard"  element={
          <MyDashboard
            pins={myDash.pins}
            loading={myDash.loading}
            error={myDash.error}
            saving={myDash.saving}
            save={myDash.save}
          />
        } />
        <Route path="/dashboard-two" element={<DashboardTwo />} />
        <Route path="/pullman"       element={<Pullman />} />
        <Route path="/pullman/people"         element={<Pullman tab="people" />} />
        <Route path="/pullman/capacity"       element={<Pullman tab="capacity" />} />
        <Route path="/pullman/bookings-taken" element={<Pullman tab="bookings-taken" />} />
        <Route path="/pullman/live"           element={<Pullman tab="live" />} />
        <Route path="/bookings"      element={<BookingsDashboard />} />
        <Route path="/pullman-demand" element={<PullmanDemandDashboard />} />
        <Route path="/sales"         element={<SalesScreen isExec={me.isExec} />} />
        <Route path="/booking-offices" element={<BookingOfficesScreen isExec={me.isExec} />} />
        <Route path="/customer-service" element={me.isCustomerService ? <CustomerServiceScreen /> : <Navigate to="/" replace />} />
        <Route path="/research"      element={<ResearchDashboard me={me} />} />
        <Route path="/timetable-studio" element={<TimetableStudioScreen me={me} />} />
        <Route path="/executive"     element={me.isExec ? <ExecutiveScreen /> : <Navigate to="/" replace />} />
        <Route path="/budget"        element={<BudgetDashboard me={me} />} />
        <Route path="/gift-aid"      element={<GiftAidDashboard me={me} />} />
        <Route path="/engagement"    element={<EngagementDashboard me={me} />} />
        <Route path="/fundraising"   element={<FundraisingDashboard me={me} />} />
        <Route path="/foundation"    element={(me.isEngagement || me.isAdmin) ? <FoundationScreen me={me} /> : <Navigate to="/" replace />} />
        <Route path="/membership/*"  element={(me.isEngagement || me.isAdmin) ? <MembershipScreen me={me} /> : <Navigate to="/" replace />} />
        <Route path="/events-revenue" element={<EventsMeracDashboard me={me} />} />
        <Route path="/claude-help"   element={<ClaudeHelp me={me} />} />
        <Route path="/admin/*"       element={<AdminPanel me={me} />} />
        <Route path="*"              element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
